import { landingCopy } from '../data/landingCopy';

/**
 * EaseVerse Reader – Internationalization (i18n)
 *
 * Supported languages (active): en, zh, zh-TW, fr, ja, de
 * Design: easy to add new languages — just add a new object to `translations`.
 */

const translations = {
  en: {
    'reader.remotePosition.title': 'Continue from your other device?',
    'reader.remotePosition.body': 'Another device reached {percent}% of this book.',
    'reader.remotePosition.jump': 'Jump there',
    'reader.remotePosition.stay': 'Stay here',
    'reader.furthestRead': 'Read up to here',
    // Library
    'library.addBook': 'Add Book',
    'library.uploading': 'Uploading...',
    'library.recentBooks': 'Recent Books',
    'library.emptyTitle': 'Your library is empty',
    'library.emptyHint': 'Click "Add Book" or drag & drop files to get started',
    'library.loading': 'Loading your library...',
    'library.dropHere': 'Drop books here',
    'library.dropFormats': 'epub, pdf, txt, mobi',
    'library.signOut': 'Sign Out',
    'library.account': 'Account Center',
    'stuckPdf.title': '{count} PDF(s) need to be re-uploaded',
    'stuckPdf.explanation': 'These PDFs were uploaded before our PDF→EPUB conversion shipped, so they cannot be opened. Delete and re-upload to enable AI chapter cleanup and dual-view reading.',
    'stuckPdf.deleteBook': 'Delete',
    'sidebar.myBookshelf': 'My Bookshelf',
    'sidebar.allBooks': 'All Books',
    'sidebar.reading': 'Reading',
    'sidebar.finished': 'Finished',
    'sidebar.curatedLibrary': 'Curated Library',
    'sidebar.readingAnalytics': 'Reading Analytics',
    'sidebar.userCenter': 'User Center',
    'sidebar.collapse': 'Collapse sidebar',
    'sidebar.expand': 'Expand sidebar',
    'sidebar.darkMode': 'Dark Mode',
    'sidebar.lightMode': 'Light Mode',
    'sidebar.search': 'Search',
    'sidebar.searchPlaceholder': 'Search books...',
    'sidebar.home': 'Home',
    'sidebar.settings': 'Settings',
    'landing.continueReading': 'Continue Reading',
    'landing.quote': 'Reading Quote',
    'landing.like': 'Like',
    'landing.liked': 'Liked',
    'landing.nextQuote': 'Next quote',
    'landing.todayRead': 'Read today',
    'landing.todayNotStarted': 'Not started today',
    'landing.weekRead': 'This week',
    'landing.monthRead': 'This month',
    'landing.weekReading': 'This Week',
    'landing.lastRead': 'Last read',
    'library.deleteBook': 'Delete book',
    'library.uploadFailed': 'Failed to upload file',
    'library.drmProtected': 'This file is DRM-protected and cannot be opened.',
    'library.huffNotSupported': 'This MOBI file uses HUFF/CDIC compression which is not supported. Please re-save with standard compression.',
    'library.parseFailed': 'Could not read this file — it may be corrupted or in an unsupported variant of its format.',
    'library.loadFailed': 'Failed to load books',

    'library.deleteFailed': 'Failed to delete book',
    'library.invalidFormat': 'Please upload epub, mobi, or pdf files',
    'library.pdfTemporarilyDisabled': 'PDF uploads are temporarily disabled while we rebuild the reader. EPUB and MOBI work well today.',
    'library.pdf.confirm.title': 'Convert this PDF?',
    'library.pdf.confirm.summary': 'This conversion will use {pages} pages of your quota.',
    'library.pdf.confirm.colCurrent': 'Now',
    'library.pdf.confirm.colAfter': 'After',
    'library.pdf.confirm.rowMonthly': 'Monthly',
    'library.pdf.confirm.rowDaily': 'Daily',
    'library.pdf.confirm.confirm': 'Convert',
    'library.pdf.confirm.cancel': 'Cancel',
    'library.pdf.unit.pages': '{n} pages',
    'library.pdf.error.perBookCapTitle': 'PDF too large for your plan',
    'library.pdf.error.perBookCapBody': 'This PDF exceeds the per-book limit of your plan.',
    'library.pdf.error.perBookCapLegend': '{pages} pages — over the {cap}-page cap',
    'library.pdf.error.quotaExceededTitle': 'Not enough quota',
    'library.pdf.error.quotaExceededBody': 'This PDF needs {pages} pages of quota.',
    'library.pdf.error.needs': 'needs {pages}',
    'library.pdf.error.unreadableTitle': 'Cannot read this PDF',
    'library.pdf.error.unreadableBody': 'This file appears to be unreadable. Common causes:',
    'library.pdf.error.unreadableCause1': 'The file is corrupt or incomplete',
    'library.pdf.error.unreadableCause2': 'The PDF is password-protected or encrypted',
    'library.pdf.error.unreadableCause3': 'The file is not actually a PDF (wrong extension)',
    'library.pdf.error.dismiss': 'OK',
    'library.pdf.upgrade.title': 'PDF is a Pro feature',
    'library.pdf.upgrade.body': 'PDF conversion is available on Pro and Max. Upgrade for:',
    'library.pdf.upgrade.benefit1': 'Up to 400 pages per book',
    'library.pdf.upgrade.benefit2': '600 pages per month',
    'library.pdf.upgrade.benefit3': 'Cloud sync across devices',
    'library.pdf.upgrade.cta': 'See plans',
    'library.freeLimitReached': 'Free plan allows up to {limit} books. Upgrade for unlimited books and cloud sync.',
    'library.deleteBook': 'Delete book',
    'library.returnBook': 'Return book',
    'library.bookLimitExceeded': "You've reached your book limit. Return a book from your shelf to make room, or upgrade for unlimited.",
    'library.bookExists': '"{title}"{author} already exists in your library.\n\nUpload again as a new copy?',
    'library.bookExistsByAuthor': ' by {author}',
    'library.cloudProOnly': 'Cloud sync is available for paid users. Upgrade to unlock cloud backup.',
    'library.cloudBackupTitle': 'Your books are backed up to the cloud',
    'library.cloudBackupDesc': 'Your plan privately backs up your books to the cloud so you can read them on any device. You can turn it off anytime with the toggle at the top-right of All Books.',
    'library.cloudBackupTurnOff': 'Turn off',
    'library.cloudBackupGotIt': 'Got it',
    'library.cloudBackupToggle': 'Auto-backup to cloud',
    'library.notAvailable': 'This book was uploaded on another device and is not available here. Please re-upload it.',
    'library.phantomDialogDesc': "This book's file doesn't exist on this device and has no cloud copy. You can upload a file as a new book, or remove this entry from your shelf.",
    'library.phantomUploadNew': 'Upload as a new book',
    'library.phantomRemove': 'Remove from shelf',
    'library.cloudOnly': 'Cloud',
    'library.downloading': 'Downloading…',
    'library.downloadFailed': 'Download failed. Please check your connection and try again.',
    'library.offlineCloudBook': 'You are offline. This book is stored in the cloud and requires an internet connection.',
    'library.confirmDelete': 'Are you sure you want to delete this book?',
    'library.confirmRemoveCurated': 'Remove this book from your shelf? You can add it back anytime from the curated library.',
    'library.deleteDialogDesc': 'This book is backed up in the cloud. What would you like to do?',
    'library.deleteLocalOnly': 'Free Local Space',
    'library.deleteCompletely': 'Delete Completely',
    'library.confirmFreeLocalSpace': 'Remove the local file? The book is backed up in the cloud and can be re-downloaded anytime.',
    'library.confirmBulkFreeLocalSpace': 'Free local space for {count} books? Cloud copies are not affected.',
    'library.freeLocalSpace': 'Free local space',
    'library.freeLocalSpaceShort': 'Free Space',
    'library.allBooks': 'All Books',
    'library.converting': 'Converting',
    'chapterSelection.batchTitle': 'Slow lane — half the quota',
    'chapterSelection.batchHint': 'Usually done within a few hours, next day at the latest. The book opens from your shelf once ready.',
    'chapterSelection.chargeSummary': 'This will use {pages} pages of quota',
    'chapterSelection.cachedDiscount': '({pages} pages hit the shared cache — half price)',
    'library.batchPendingBadge': 'Queued · slow lane',
    'reader.pdfBatch.title': 'Queued for conversion',
    'reader.pdfBatch.hint': 'This book uses the slow lane (half quota). It usually finishes within a few hours, next day at the latest — once ready it opens right from your shelf.',
    'library.badgeExtracting': 'Extracting',
    'library.badgeConverting': 'Cleaning',
    'library.badgeFinishing': 'Finishing',
    'library.pendingPreview': 'Choose chapters',
    'library.pendingPreviewBadge': 'Pick chapters',
    'library.uploadingBadge': 'Uploading…',
    'library.scanOnlyBadge': 'Needs OCR',
    'library.scanOnlyBadgeTitle': 'Scanned PDF — run OCR before uploading',
    'chapterSelection.title': 'Choose chapters to convert',
    'chapterSelection.unreadablePdf': "We couldn't read this PDF — the file may be corrupted or use a non-standard format. Try re-saving it (Preview → Export as PDF, or Print → Save as PDF) and uploading again.",
    'chapterSelection.selectAll': 'Select all',
    'chapterSelection.invertSelection': 'Invert',
    'chapterSelection.selectedCount': '{n} ch · {pages} pages',
    'chapterSelection.includesFreePages': '({pages} pages free)',
    'chapterSelection.alreadyPaid': 'Paid · free',
    'chapterSelection.alreadyConverted': '✓ Converted',
    'library.addChapters': 'Add chapters',
    'chapterSelection.quotaUsage': 'Uses {used} / {cap} pages of your monthly cap',
    'chapterSelection.exceedsCap': 'Exceeds per-book cap of {cap} pages',
    'chapterSelection.exceedsQuota': 'Selection exceeds remaining quota — reduce to proceed',
    'chapterSelection.outlineSparse': 'Chapter info incomplete — this is the best detected list',
    'chapterSelection.outlineHeuristic': 'Outline missing — chapters detected by layout',
    'chapterSelection.tocHarvested': "Chapter titles matched to the book's own table of contents",
    'chapterSelection.tocHarvestedPartial': "Some chapter titles matched to the book's table of contents — the rest are auto-detected",
    'chapterSelection.fallbackSingle': 'No chapter info — will convert the whole book as one section',
    'chapterSelection.loadingOutline': 'Parsing chapters…',
    'chapterSelection.fallbackChunks': 'Couldn\'t detect chapter titles — split evenly by page count',
    'chapterSelection.pageRangeTitle': 'Pages {start}–{end}',
    'chapterSelection.scanOnlyTitle': '⚠️ Scanned PDF — conversion not supported',
    'chapterSelection.scanOnlyBody': 'This PDF averages only {chars} characters per page, so almost no extractable text. Run OCR (Adobe Acrobat, ABBYY FineReader, Tesseract) before uploading.',
    'chapterSelection.startConversion': 'Start conversion',
    'chapterSelection.submitting': 'Submitting…',
    'reader.newChaptersAvailable': '+{n} more section(s) converted',
    'reader.conversionDoneWithNew': 'Conversion complete · +{n} section(s)',
    'reader.conversionDone': 'Conversion complete',
    'reader.refreshToView': 'Refresh',
    'common.dismiss': 'Dismiss',
    'library.searchResults': 'results',
    'library.continueReading': 'Continue Reading',
    'library.continue': 'Continue',
    'library.showAll': 'Show All',
    'library.back': 'Back',
    'library.bookCount': '{count} books',
    'library.select': 'Select',
    'library.cancel': 'Cancel',
    'library.backToHome': 'Back to home',
    'library.scrollToTop': 'Scroll to top',
    'library.selectedCount': '{count} selected',
    'library.selectAll': 'Select All',
    'library.pinToTop': 'Pin to Top',
    'library.unpinFromTop': 'Unpin',
    'library.downloadToLocal': 'Download to Local',
    'library.addToCloudShort': 'Add to Cloud',
    'library.removeFromLocalShort': 'Remove from Local',
    'library.deleteShort': 'Delete',
    'library.confirmRemoveFromLocal': 'Remove {count} books from local library? They will be deleted from this device.',
    'library.finishedReading': 'Finished Reading',
    'library.currentlyReading': 'Currently Reading',
    'library.finishedShort': 'Finished',
    'library.wpmUnit': 'WPM',
    'library.markFinished': 'Mark as finished',
    'library.markFinishedShort': 'Done',
    'library.markFinishedTip': 'Main content has ended. Tap here to mark as finished.',
    'library.confirmMarkFinished': 'Mark this book as finished?',
    'reader.bookFinishedEyebrow': 'You finished reading',
    'reader.bookJourneyRevisitEyebrow': 'Revisiting your journey',
    'reader.bookJourneyBackToReading': 'Back to reading',
    'reader.bookJourneySentinel': '✓ Finished · view your journey',
    'reader.bookJourneyButton': 'Reading journey',
    'reader.bookJourneySelectAll': 'Select all',
    'reader.bookJourneyDeselectAll': 'Deselect all',
    'reader.bookJourneyWordsLabel': 'Words you looked up',
    'reader.bookJourneyNotesLabel': 'Notes',
    'reader.bookJourneyShowAll': 'Show all',
    'reader.bookJourneyAddToLoom': 'Add to Loom',
    'reader.bookJourneyLoomAdded': 'Added {added}',
    'reader.bookJourneyLoomLimited': '{n} over the free limit',
    'reader.bookFinishedOrdinal': 'Book #{n} finished on EaseVerse',
    'reader.bookFinishedShareGroup': 'Share to',
    'reader.bookFinishedShareWechat': 'WeChat',
    'reader.bookFinishedShareWeibo': 'Weibo',
    'reader.bookFinishedShareXhs': 'Xiaohongshu',
    'reader.bookFinishedShareX': 'X',
    'reader.bookFinishedShareIg': 'Instagram',
    'reader.bookFinishedShareFb': 'Facebook',
    'reader.bookFinishedShareLine': 'LINE',
    'reader.bookFinishedShareTg': 'Telegram',
    'reader.bookFinishedShareCopy': 'Copy link',
    'reader.bookFinishedShareText': 'I just finished "{title}" on EaseVerse ✨ My book #{n} 📚 {url}',
    'reader.bookFinishedShareWechatReady': 'Copied — paste into WeChat to share',
    'reader.bookFinishedShareLinkCopied': 'Link copied',
    'reader.bookFinishedShareXhsReady': 'Image saved & caption copied — ready for Xiaohongshu',
    'reader.bookFinishedShareIgReady': 'Image saved & caption copied — ready for Instagram',
    'reader.bookFinishedShareFailed': 'Share failed, please try again',
    'reader.bookFinishedLabelTotalTime': 'Total time',
    'reader.bookFinishedLabelBookLength': 'Book length',
    'reader.bookFinishedLabelHighlights': 'Highlights',
    'reader.bookFinishedLabelNotes': 'Notes',
    'reader.bookFinishedLabelBookmarks': 'Bookmarks',
    'reader.bookFinishedJourneyLabel': 'Your reading journey',
    'reader.bookFinishedJourneyPrev': 'Earlier days',
    'reader.bookFinishedJourneyNext': 'Later days',
    'reader.bookFinishedClose': 'Back to library',
    'library.editFinishedDate': 'Edit date',
    'library.localLibrary': 'Local',
    'library.filter': 'Filter',
    'library.filterStorageSection': 'Storage',
    'library.filterLanguageSection': 'Language',
    'library.filterCloud': 'Cloud',
    'library.filterLocal': 'Local',
    'library.filterCurated': 'Curated',
    'library.curatedLibrary': 'Curated Library',
    'library.curatedLibraryDesc': 'Pre-processed public domain classics with instant chunk annotations',
    'library.curatedComingSoon': 'Coming soon — curated books with pre-processed chunk annotations.',
    'library.freeBadge': 'Free',
    'library.freeThisMonth': 'Free this month',
    'library.curatedEnglish': 'English Library',
    'library.curatedFrench': 'French Library',
    'library.addToLibrary': 'Add to Library',
    'library.addToMyLibrary': 'Add to My Library',
    'library.adding': 'Adding...',
    'library.chapterUnit': 'ch.',
    'library.cloudLibrary': 'Cloud',
    'library.cloudUsage': '{percent}% used',
    'library.cloudUpgrade': 'Cloud library is available for paid users. Upgrade to unlock cloud storage.',
    'library.upgradeLink': 'Upgrade',
    'library.last7Days': 'Last 7 days',
    'library.thisYear': 'This year',
    'library.yearUploaded': 'Uploaded in {year}',
    'library.yearFinishedCount': '{year} ({count})',
    'library.otherFinishedCount': 'Other ({count})',
    'library.completedBooksLine1': 'Your completed books will appear here',
    'library.completedBooksLine2': 'Reading builds systems',
    'library.completedBooksLine3': 'One book at a time',
    'library.readingEmptyLine1': 'Start reading',
    'library.readingEmptyLine2': 'Pick a book and ease in',
    'library.readingEmptyLine3': 'One page at a time',

    // Site metadata (document.title, meta tags)
    'meta.siteTitle': 'EaseVerse Reader ｜ Multilingual Deep Reading',

    // Auth
    'auth.title': 'EaseVerse Reader',
    'auth.subtitle': 'Your personal reading sanctuary',
    'auth.checking': 'Checking authentication...',
    'auth.emailLabel': 'Email',
    'auth.emailPlaceholder': 'your@email.com',
    'auth.sendCode': 'Send Verification Code',
    'auth.sendingCode': 'Sending code...',
    'auth.codeSent': 'Verification code sent to your email',
    'auth.enterCode': 'Enter Verification Code',
    'auth.codeSentTo': 'We sent a 6-digit code to',
    'auth.verify': 'Verify & Sign In',
    'auth.verifying': 'Verifying...',
    'auth.resend': 'Resend code',
    'auth.resendIn': 'Resend code in {seconds}s',
    'auth.diffEmail': 'Use a different email',
    'auth.codeIncomplete': 'Please enter the complete 6-digit code',
    'auth.newCodeSent': 'New verification code sent',
    'auth.helpLink': 'Having trouble? Contact us',
    'auth.extLogin': 'Log in with EaseVerse Lens',
    'auth.extLoggingIn': 'Logging in...',
    'auth.useEmail': 'Sign in with email instead',
    'auth.backToExt': 'Back to EaseVerse Lens login',
    'auth.or': 'or',
    'auth.googleError': 'Google login failed',
    'auth.agreeTerms': 'I agree to the',
    'auth.termsLink': 'Terms of Use',
    'auth.privacyLink': 'Privacy Policy',
    'auth.subscribeNewsletter': 'Send me product updates and reading tips (1–2 emails/month, unsubscribe anytime)',
    'auth.darkMode': 'Dark mode',
    'auth.lightMode': 'Light mode',
    // Auth products
    'auth.products': 'EaseVerse Products',
    'auth.lensDesc': 'Context Dictionary',
    'auth.loomDesc': 'Knowledge Base',
    'auth.readerDesc': 'Reading Platform',
    'auth.switchTitle': 'Switch Account?',
    'auth.switchDesc': 'Your EaseVerse Lens extension is signed in as {email}, which differs from your current Reader account. Switch to this account?',
    'auth.switchConfirm': 'Switch',
    'auth.switchDismiss': 'Stay',

    // Sidebar
    'sidebar.contents': 'Table of Contents',
    'sidebar.coverage.read': 'Read',
    'sidebar.coverage.partial': 'Partly read',
    'sidebar.coverage.unread': 'Not read yet',
    'sidebar.noToc': 'No table of contents available',
    'sidebar.highlightsNotes': 'Highlights & Notes',
    'sidebar.bookmarks': 'Bookmarks',
    'sidebar.all': 'All',
    'sidebar.highlights': 'Highlights',
    'sidebar.notes': 'Notes',
    'sidebar.emptyAll': 'No highlights or notes yet. Select text while reading to highlight or add a note.',
    'sidebar.emptyHighlights': 'No highlights yet. Select text while reading to highlight.',
    'sidebar.emptyNotes': 'No notes yet. Select text and add a note while reading.',
    'sidebar.emptyBookmarks': 'No bookmarks yet. Tap "Add" to bookmark your current position.',
    'sidebar.add': 'Add',
    'sidebar.save': 'Save',
    'sidebar.cancel': 'Cancel',
    'sidebar.removeHighlight': 'Remove highlight (keep note)',
    'sidebar.unknownChapter': 'Unknown Chapter',
    'sidebar.bookmark': 'Bookmark',
    'sidebar.page': 'Page {page}',

    'reader.settingsCurrentBookHint': 'for current book only',
    'reader.settingsAllBooksHint': 'for all books',
    'reader.viewMode': 'View Mode',
    'reader.scroll': 'Scroll',
    'reader.chapter': 'Chapter',
    'reader.fontSize': 'Font Size',
    'reader.pageWidth': 'Page Width',
    'reader.pageMargin': 'Page Margins',
    'reader.pageMarginNarrow': 'Narrow',
    'reader.pageMarginStandard': 'Standard',
    'reader.pageMarginWide': 'Wide',
    'reader.lineHeight': 'Line Height',
    'reader.paragraph': 'Paragraph',
    'reader.letter': 'Letter',
    'reader.setLanguage': 'Please select the language of this book',
    'reader.languageConfirm': 'OK',
    'reader.changeLanguage': 'Change language',
    'reader.languageMismatch': 'This book\'s language may be:',
    'notification.dismiss': 'Dismiss',
    'reader.bookLanguageLabel': 'Book Language',
    'reader.font': 'Font',
    'reader.theme': 'Theme',
    'reader.progressBar': 'Progress Bar',
    'reader.always': 'Always',
    'reader.hover': 'Hover',
    'reader.hidden': 'Hidden',
    'reader.einkMode': 'E-ink mode',
    'reader.einkAuto': 'Auto',
    'reader.einkOn': 'On',
    'reader.einkOff': 'Off',
    'reader.pagedMode': 'Page turn mode',
    'reader.tapToSelect': 'Tap to select word',
    'reader.autoPlayPronunciation': 'Auto-play pronunciation',
    'reader.autoPlayPronunciationHint': 'current book',
    'settings.languageAndLearning': 'Language & Learning',
    'settings.interaction': 'Interaction',
    'settings.pronunciation': 'Pronunciation Preferences',
    'settings.accent': 'Accent',
    'settings.voice': 'Voice',
    'settings.accentUS': 'American',
    'settings.accentGB': 'British',
    'settings.voiceMale': 'Male',
    'settings.voiceFemale': 'Female',
    'settings.proOnly': 'Pro only',
    'language.name.zh': 'Chinese',
    'language.name.en': 'English',
    'language.name.ja': 'Japanese',
    'language.name.ko': 'Korean',
    'language.name.fr': 'French',
    'language.name.de': 'German',
    'language.name.es': 'Spanish',
    'language.name.pt': 'Portuguese',
    'language.name.ru': 'Russian',
    'language.name.it': 'Italian',
    'language.name.vi': 'Vietnamese',
    'language.name.other': 'Other',
    'reader.chapterLabel': 'Chapter',
    'reader.bookLabel': 'Book',
    'reader.prevChapter': 'Previous chapter',
    'reader.nextChapter': 'Next chapter',
    'reader.prevPage': 'Previous page',
    'reader.nextPage': 'Next page',
    'reader.goBack': 'Back',
    'reader.backToPosition': 'Return to previous position',
    'reader.sessionSummary': 'Read {time}',
    'einkSel.highlight': 'Highlight',
    'einkSel.highlighted': 'Highlighted',
    'einkSel.note': 'Note',
    'einkSel.discard': 'Remove highlight',
    'einkSel.gloss': 'Meaning',
    'einkSel.explain': 'Deep dive',
    'einkSel.notePlaceholder': 'Add a note…',
    'einkSel.noteSave': 'Save',
    'einkSel.vocab': 'Vocabulary',
    'einkSel.vocabSaved': 'Saved',
    'einkSel.sentence': 'Whole sentence',
    'reader.sessionSummaryWithWords': 'Read {time} · {words} words',
    'reader.sessionSummaryCoverage': 'Read {time} · {coverage} new words',
    'reader.sessionSummaryReread': 'Read {time} · {reread} re-read',
    'reader.sessionSummaryThreeBucket': 'Read {time} · {coverage} new · {reread} re-read',
    'reader.formatTemporarilyUnavailable': '{format} support is temporarily unavailable while we rebuild it. Your file and reading progress are safely kept and will be accessible when {format} is re-enabled.',
    'reader.pdfConverting': 'Converting your PDF to a readable format — this usually takes 1–2 minutes. The page will refresh automatically when ready.',
    'reader.pdfConvertingElapsed': 'Elapsed: {elapsed}',
    'reader.pdfPreparing.title': 'Preparing your PDF',
    'reader.pdfPreparing.stage1': 'Extracting text from PDF',
    'reader.pdfPreparing.stage2': 'Converting to readable format',
    'reader.pdfPreparing.stage3': 'Assembling ebook',
    'reader.pdfPreparing.stage4': 'Saving to cloud',
    'reader.pdfPreparing.elapsed': 'Elapsed {elapsed}',
    'reader.pdfPreparing.eta': 'About {eta} remaining',
    'reader.pdfPreparing.activeChapter': 'Now processing:',
    'reader.pdfPreparing.modelSlow': 'slow model',
    'reader.pdfPreparing.modelPassthrough': 'passthrough',
    'reader.dualViewModeLabel': 'Reading mode',
    'reader.dualViewText': 'Text',
    'reader.dualViewDual': 'Side-by-side',
    'reader.dualViewOriginal': 'Original',
    'reader.dualViewNeedsUpgrade': 'This book needs to be upgraded to use side-by-side mode.',
    'reader.dualViewNeedsWiderScreen': 'Side-by-side mode needs a wider screen.',
    'reader.reanchor': 'Realign panes to current paragraph',
    'reader.dualViewUpgradeTooltip': 'Upgrade this book to enable side-by-side mode',
    'reader.dualViewUpgradeConfirm': 'Upgrade will reconvert this book (~1–2 minutes). The book will not be readable during conversion. Continue?',
    'reader.dualViewUpgradeFailed': 'Could not start upgrade. Please try again later.',
    'reader.qualityMediumToast': 'Some text in this book may be imperfectly extracted. We’ve enabled side-by-side view so you can verify against the original PDF.',
    'reader.qualityLowToast': 'This PDF appears to be a scan or low quality. We’ve defaulted to the original PDF view.',
    'reader.qualityLowTooltip': 'Text extraction was unreliable for this book. Click for details.',
    'reader.qualityLowAria': 'Text quality warning',
    'reader.qualityLowPopoverTitle': 'Text layer incomplete',
    'reader.qualityLowPopoverBody': 'This book’s text could not be reliably extracted from the source PDF. We’ve defaulted to the original PDF view to preserve fidelity.',
    'reader.qualityDontShowAgain': 'Don’t show this again for this book',
    'reader.openAsOriginal': 'Open as original PDF',
    'reader.openAsOriginalHint': 'View the source PDF directly. Highlights, notes, and Lens are unavailable in this fallback mode.',
    'reader.figurePeekFigureOne': '1 figure',
    'reader.figurePeekFigureMany': '{n} figures',
    'reader.figurePeekTableOne': '1 table',
    'reader.figurePeekTableMany': '{n} tables',
    'reader.figurePeekEquationOne': '1 equation',
    'reader.figurePeekEquationMany': '{n} equations',
    'reader.peekDialogLabel': 'Source PDF page preview',
    'reader.peekLoading': 'Loading PDF page…',
    'reader.peekClose': 'Close preview',
    'reader.pdfConversionFailed': 'We could not convert this PDF. The original file is still saved; try deleting and re-uploading, or contact support.',
    'reader.minutesShort': '{n} min',
    'reader.hoursShort': '{n} hr',
    'reader.hoursMinutesShort': '{h} hr {m} min',
    'reader.theme.white': 'White',
    'reader.theme.slate': 'Slate',
    'reader.theme.sage': 'Eye Care',
    'reader.theme.parchment': 'Parchment',
    'reader.theme.midnight': 'Midnight',
    'reader.loginRequired': 'Please log in to read this book',
    'reader.loadFailed': 'Failed to load book',
    'reader.bookNotFound': 'Book not found',
    'reader.deepReading.title': 'Deep Reading',
    'reader.deepReading.close': 'Close Deep Reading',
    'reader.deepReading.toggle': 'Deep Reading panel',
    'reader.deepReading.bubbles': 'Lookup map',
    'lens.pronounceLocked': 'Upgrade to Pro to pronounce this language',
    'reader.deepReading.viewHistory': 'Recent',
    'reader.deepReading.viewFrequency': 'Frequency',
    'reader.deepReading.playNormal': 'Play',
    'reader.deepReading.playSlow': 'Play slowly (0.7×)',
    'reader.deepReading.notes': 'Notes',
    'reader.deepReading.chat': 'Chat',
    'reader.deepReading.tabLookups': 'Lookups',
    'reader.deepReading.tabNotes': 'Notes',
    'reader.deepReading.done': 'Done',
    'reader.deepReading.comingSoon': 'Coming soon',
    'reader.deepReading.signInToSee': 'Sign in to see your lookup map',
    'reader.deepReading.loading': 'Loading…',
    'reader.deepReading.loadError': "Couldn't load lookups",
    'reader.deepReading.noLookups': 'No lookups in this book yet',
    'reader.deepReading.contexts': 'Contexts',
    'reader.deepReading.back': '← Back to map',
    'reader.deepReading.noNotes': 'No notes or highlights yet',
    'reader.deepReading.notePlaceholder': 'Write your note…',
    'reader.deepReading.addNote': '+ Add a note',
    'reader.deepReading.jumpToPassage': 'Jump to passage',
    'reader.deepReading.toLoom': '→ Add to Loom',
    'reader.deepReading.inLoom': '✓ In Loom',
    'reader.deepReading.loomError': "Couldn't add to Loom",
    'reader.deepReading.collectToLoom': 'Save to Loom',
    'reader.deepReading.deleteEntry': 'Remove from list',
    'reader.deepReading.reply': '↩ Reply',
    'reader.deepReading.replyOpen': 'Reply to this note',
    'reader.deepReading.replyPlaceholder': 'Write a reply…',
    'reader.deepReading.repliesShow': 'Show {n} replies',
    'reader.deepReading.repliesHide': 'Hide replies',
    'reader.deepReading.editCount': 'edited {n}×',
    'reader.deepReading.editHistoryOpen': 'View edit history',
    'reader.deepReading.deleteNote': 'Delete',
    'reader.deepReading.timeJustNow': 'just now',
    'reader.deepReading.timeMinAgo': '{n} min ago',
    'reader.deepReading.timeHourAgo': '{n}h ago',
    'reader.deepReading.timeDayAgo': '{n}d ago',
    'reader.deepReading.editHistoryTitle': 'Edit history',
    'reader.deepReading.editHistoryCurrent': 'Current',
    'reader.deepReading.editHistoryNone': 'No earlier versions',
    'reader.deepReading.editHistoryError': "Couldn't load history",
    'reader.followLink': 'Follow link',

    'note.placeholder': 'Write your note...',
    'note.addNotePlaceholder': 'Add a note...',
    'note.editNote': 'Edit note',
    'note.addNote': 'Add note',
    'note.deleteNote': 'Delete note',
    'note.deleteHighlight': 'Delete highlight',
    'note.save': 'Save',
    'note.copy': 'Copy',
    'note.cancel': 'Cancel',
    'note.close': 'Close',
    'note.unpin': 'Unpin',
    'note.highlightedText': 'Highlighted text',
    'note.highlightColor': 'Highlight {color}',
    'note.underlineColor': 'Underline {color}',
    'note.saving': 'Saving...',
    'note.saved': 'Saved',
    'note.error': 'Error',


    // Loading
    'loading.default': 'Loading...',
    'loading.backToLibrary': 'Back to Library',

    // Stats Card
    'stats.lessThan1min': '<1 min',
    'stats.hour': 'h ',
    'stats.minute': 'min',
    'stats.goalLabel': 'Monthly goal: {days} days/week, {time}/day',
    'stats.goalLabelDaily': 'Monthly goal: {time}/day',
    'stats.noGoal': 'No reading goal set',
    'stats.streakDays': '{n} day reading streak',
    'stats.streakLabel': 'Streak',
    'stats.thisMonth': 'This Month',
    'stats.todayLabel': 'Today',
    'stats.thisWeek': 'This Week',
    'stats.reading': ' Reading',
    'stats.yearGoalLabel': ' Goal',
    'stats.yearlyReadingGoal': '{year} Reading Goal',
    'stats.wan': 'k',
    'stats.wordsUnit': 'words',
    'stats.mon': 'M', 'stats.tue': 'T', 'stats.wed': 'W', 'stats.thu': 'T',
    'stats.fri': 'F', 'stats.sat': 'S', 'stats.sun': 'S',
    'stats.month1': 'Jan', 'stats.month2': 'Feb', 'stats.month3': 'Mar',
    'stats.month4': 'Apr', 'stats.month5': 'May', 'stats.month6': 'Jun',
    'stats.month7': 'Jul', 'stats.month8': 'Aug', 'stats.month9': 'Sep',
    'stats.month10': 'Oct', 'stats.month11': 'Nov', 'stats.month12': 'Dec',
    'stats.setGoalTitle': 'Set Monthly Reading Goal',
    'stats.weeklyDays': 'Days per week',
    'stats.dailyMinutes': 'Daily reading time',
    'stats.yearlyWords': 'Annual word goal',
    'stats.unitWan': 'x10k words',
    'stats.save': 'Save',
    'stats.confirmSave': 'Confirm',
    'stats.goalConfirmMsg': 'After saving, you can change your goal {n} more time(s) this month. Continue?',
    'stats.goalEditExhausted': 'You have used all goal changes for this month.',
    'stats.goalRemaining': '{n} change(s) remaining this month',
    'stats.cancel': 'Cancel',
    'stats.minuteShort': 'min',
    'stats.custom': 'Custom',
    'stats.wordsCount': '≈{n} words',
    'stats.wordsEstimateHint': 'Estimated from reading position changes',
    'stats.noReading': 'No reading',
    'stats.streakHint': 'Reading \u22655min counts toward streak',
    'stats.dailyThreshold': '{time}/day',
    'stats.remaining': '{time} remaining',
    // Dashboard
    'dashboard.title': 'Reading Analytics',
    'dashboard.titleLang': '{lang} Reading Analytics',
    'dashboard.backToLibrary': 'Library',
    'dashboard.langDistribution': 'Language Distribution',
    'dashboard.langMonth': 'Month',
    'dashboard.langYear': 'Year',
    'dashboard.langAll': 'All',
    'dashboard.langSingleHint': 'When you read books in other languages, you can filter by language here',
    'dashboard.langFold': 'Less',
    'dashboard.langName.zh': 'Chinese',
    'dashboard.langName.en': 'English',
    'dashboard.langName.ja': 'Japanese',
    'dashboard.langName.ko': 'Korean',
    'dashboard.langName.fr': 'French',
    'dashboard.langName.de': 'German',
    'dashboard.langName.es': 'Spanish',
    'dashboard.langName.pt': 'Portuguese',
    'dashboard.langName.ru': 'Russian',
    'dashboard.langName.it': 'Italian',
    'dashboard.langName.nl': 'Dutch',
    'dashboard.langName.ar': 'Arabic',
    'dashboard.langName.th': 'Thai',
    'dashboard.langName.vi': 'Vietnamese',
    'dashboard.todaySummary': 'Today',
    'dashboard.weekSummary': 'This Week',
    'dashboard.readingTime': 'Reading Time',
    'dashboard.wordsRead': 'Words Read',
    'dashboard.totalReadingDays': 'Total Reading',
    'dashboard.dayUnit': ' days',
    'dashboard.achievements': 'Achievements',
    'dashboard.currentStreak': '{count}-day streak',
    'dashboard.showAllBadges': 'Show all badges...',
    'dashboard.cat_member': 'Pro Member',
    'dashboard.memberBadgeLabel': 'Pro',
    'dashboard.cat_days': 'Total Days',
    'dashboard.cat_streak': 'Reading Streak',
    'dashboard.cat_words': 'Words Read',
    'dashboard.cat_time': 'Reading Time',
    'dashboard.cat_books': 'Books Completed',
    'dashboard.nextMilestone': 'Next',
    'dashboard.memberRewardLine1': 'Maintain any paid plan (Basic / Pro / Max) for 365 days',
    'dashboard.memberRewardLine2': 'to unlock 1 month free Pro membership',
    'dashboard.memberGiftUnlocked': "Congrats! 365 days as an EaseVerse member — your gift has been delivered~🎁",
    'dashboard.memberActiveTooltip': "Day {days} as an EaseVerse member — on day 365, a gift awaits you~",
    'dashboard.memberPausedTooltip': "You were an EaseVerse member for {days} days. Renew to keep counting~",
    'dashboard.memberNotPaidTooltip': "You're not an EaseVerse member yet — head to your profile to unlock membership~",
    'dashboard.badgeUnlocked': 'Congrats on earning "{0}"!\nKeep going to unlock "{1}"',
    'dashboard.badgeMaxLevel': 'Congrats on earning "{0}"!\nYou\'ve reached the highest level!',
    'dashboard.badgeLocked': 'Start reading now\nto unlock "{0}"',
    'dashboard.badgeCountSuffix': ' earned',
    'dashboard.badgeCongratsTooltip': 'Congrats 🎉 Unlocked!',
    'dashboard.badgeCountText': '{n} readers unlocked this',
    'dashboard.badgeExpandMore': 'Show all',
    'dashboard.daysSuffix': 'd',
    'dashboard.booksSuffix': '',
    'dashboard.viewAllBadges': 'View all badges',
    'dashboard.statToday': 'Today Reading',
    'dashboard.statWords': 'Words Read',
    'dashboard.statTotalWords': 'Total Words Read',
    'dashboard.statRereadWords': '+{n} re-read',
    'dashboard.wordsPopoverTitle': 'Display Languages',
    'dashboard.wordsPopoverSaved': 'Saved',
    'dashboard.statWeek': 'This Week Reading',
    'dashboard.statTotalDays': 'Total Days',
    'dashboard.totalDaysTooltip': 'On {date}, you began your reading journey on EaseVerse. {days} days and counting!',
    'dashboard.dailyTrend': 'Daily Trend',
    'dashboard.monthlyTotal': 'Read {time} this month',
    'dashboard.dailyAvg': 'Daily avg {time}',
    'dashboard.yearMonth': '{month}/{year}',
    'dashboard.hourlyInsight': 'Prefer reading in the {period}',
    'dashboard.hourlyInsightRange': 'Prefer reading {from} to {to}',
    'dashboard.hourlyWords': 'Words Per Minute',
    'dashboard.wpmHint': 'Words Per Minute',
    'dashboard.wpmSubtitle': 'This month {wpm} words/min',
    'dashboard.wpmSubtitleUp': 'This month {wpm} words/min, up {pct}% from last month',
    'dashboard.wpmSubtitleDown': 'This month {wpm} words/min, down {pct}% from last month',
    'dashboard.wpmInsight': '{period} WPM highest',
    'dashboard.wpmInsightRange': '{from} to {to} WPM highest',
    'dashboard.hourlyTime': 'Hourly Reading Time',
    'dashboard.wordsUnit': 'words',
    'dashboard.period.lateNight': 'late night',
    'dashboard.period.dawn': 'dawn',
    'dashboard.period.earlyMorning': 'early morning',
    'dashboard.period.lateMorning': 'late morning',
    'dashboard.period.earlyAfternoon': 'early afternoon',
    'dashboard.period.lateAfternoon': 'late afternoon',
    'dashboard.period.evening': 'evening',
    'dashboard.period.nightfall': 'nightfall',
    'dashboard.bookBreakdown': 'Books',
    'dashboard.noData': 'No reading data yet',
    'dashboard.editGoal': 'Set Goal',
    'dashboard.viewDetails': 'View details',
    'dashboard.yearlyTime': 'Yearly Reading Time',
    'dashboard.yearlyWpm': 'Yearly WPM',
    'dashboard.monthUnit': '',
    'dashboard.wpmExplainPlaceholder': 'WPM (Words Per Minute) measures your reading speed — a detailed explanation is coming soon.',
    // Milestones
    'milestone.streak_3d': '3-day reading streak!',
    'milestone.streak_7d': '7-day reading streak!',
    'milestone.streak_14d': '14-day reading streak!',
    'milestone.streak_21d': '21-day reading streak!',
    'milestone.streak_30d': '30-day reading streak!',
    'milestone.streak_60d': '60-day reading streak!',
    'milestone.streak_100d': '100-day reading streak!',
    'milestone.streak_180d': '180-day reading streak!',
    'milestone.streak_365d': '365-day reading streak!',
    'milestone.words_10k': 'Read 10,000 words!',
    'milestone.words_50k': 'Read 50,000 words!',
    'milestone.words_100k': 'Read 100,000 words!',
    'milestone.words_500k': 'Read 500,000 words!',
    'milestone.words_1m': 'Read 1,000,000 words!',
    'milestone.words_2m': 'Read 2,000,000 words!',
    'milestone.words_3m': 'Read 3,000,000 words!',
    'milestone.words_4m': 'Read 4,000,000 words!',
    'milestone.words_5m': 'Read 5,000,000 words!',
    'milestone.time_10h': '10 hours of reading!',
    'milestone.time_50h': '50 hours of reading!',
    'milestone.time_100h': '100 hours of reading!',
    'milestone.time_300h': '300 hours of reading!',
    'milestone.time_500h': '500 hours of reading!',
    'milestone.time_1000h': '1,000 hours of reading!',
    'milestone.time_2000h': '2,000 hours of reading!',
    'milestone.weekly_goal_first': 'First weekly goal achieved!',
    'milestone.weekly_goal_4': '4 consecutive weeks on goal!',
    'milestone.yearly_25': '25% of yearly goal!',
    'milestone.yearly_50': 'Halfway through yearly goal!',
    'milestone.yearly_75': '75% of yearly goal!',
    'milestone.yearly_100': 'Yearly goal achieved!',
    'milestone.books_1': 'Finished your first book!',
    'milestone.books_3': 'Finished 3 books!',
    'milestone.books_5': 'Finished 5 books!',
    'milestone.books_10': 'Finished 10 books!',
    'milestone.books_25': 'Finished 25 books!',
    'milestone.books_50': 'Finished 50 books!',
    'milestone.books_75': 'Finished 75 books!',
    'milestone.books_100': 'Finished 100 books!',
    'milestone.days_7': 'Read for 7 days!',
    'milestone.days_30': 'Read for 30 days!',
    'milestone.days_100': 'Read for 100 days!',
    'milestone.days_200': 'Read for 200 days!',
    'milestone.days_365': 'Read for 365 days!',
    'milestone.member_365d': '365 days as Pro member!',
    'milestone.lang_words': 'Read {amount} {lang} words!',
    'milestone.lang_time': '{amount} reading {lang}!',

    // Chunks — Settings
    'chunks.title': 'Vocabulary Chunks',
    'chunks.showAnnotations': 'Show chunk annotations',
    'chunks.filterRange': 'Display range',
    'chunks.levelAdaptive': 'Adaptive',
    'chunks.modeManual': 'Manual',
    'chunks.levelShowAll': 'Show All',
    'chunks.density': 'Density',
    'chunks.densityFocused': 'Focused',
    'chunks.densityDetailed': 'Detailed',
    'chunks.adaptiveExplore': 'Learning your preferences ({count}/{total})',
    'chunks.adaptiveActive': 'Adapted from {count} interactions',
    'chunks.manualLevel': 'Level',
    'chunks.levelAll': 'All',
    'chunks.manualCategory': 'Category',
    'chunks.cat_vocabulary': 'Vocabulary',
    'chunks.cat_phrasal_verb': 'Phrasal Verb',
    'chunks.cat_collocation': 'Collocation',
    'chunks.cat_idiom': 'Idiom',
    'chunks.cat_grammar': 'Grammar',
    'chunks.cat_knowledge': 'Knowledge',
    'chunks.cat_tone': 'Tone',
    'chunks.resetProfile': 'Reset profile',
    // Chunks — Lens TranslationBar & FloatingModal
    'chunks.modeTranslate': 'Translate',
    'chunks.modeSynonym': 'Synonym',
    'chunks.loading': 'Loading...',
    'chunks.rehinting': 'Looking at this from a different angle…',
    'chunks.reportBadCache': 'Report bad translation',
    'chunks.generating': 'Generating explanation...',
    'chunks.askPlaceholder': 'Ask anything',
    'lens.sentenceAnalysis': 'Sentence Analysis',
    'lens.originalSentence': 'Original',
    'lens.step1': 'Meaning',
    'lens.step2': 'Structure',
    'lens.step3': 'Deep Dive',
    'lens.analyzing': 'Analyzing...',
    'lens.showGrammar': 'Show Grammar Details',
    'lens.hideGrammar': 'Hide Grammar Details',
    'lens.openChat': 'Ask anything',
    'lens.saveNote': 'Save note',
    'lens.noteSaved': 'Saved',
    'lens.saveNoteFailed': 'Save failed',
    'lens.removeNote': 'Remove note',
    'lens.removeNoteFailed': 'Remove failed',
    'lens.checkingNote': 'Checking…',
    'lens.collapse': 'Collapse',
    'chunks.resetConfirm': 'Reset all chunk learning data? This cannot be undone.',
    'chunks.onboardingHint': 'Tap the dots to see explanations',
    'chunks.statusLoading': 'Loading annotations…',
    'chunks.statusProcessing': 'Analyzing vocabulary…',
    'chunks.cacheRefreshed': 'Annotations upgraded — click to refresh',
    'chunks.proRequired': 'Upgrade to a paid plan to unlock vocabulary chunk annotations.',
    'chunks.bookUsage': "Chunk splitting: {used}/{limit} books this month",
    'chunks.bookLimitReached': "{limit}/{limit} books used this month — upgrade to Pro for unlimited",

    // Device limit modal
    'deviceLimit.titleConfirm': 'Device limit reached',
    'deviceLimit.titleSelect': 'Device limit reached',
    'deviceLimit.confirmDesc': 'Continuing will log out the following device:',
    'deviceLimit.selectDesc': 'Select a device to replace:',
    'deviceLimit.loggedIn': 'logged in',
    'deviceLimit.cancel': 'Cancel',
    'deviceLimit.confirming': 'Logging in...',
    'deviceLimit.confirmLogin': 'Confirm & Log In',
    'deviceLimit.confirmReplace': 'Replace & Log In',
    'deviceLimit.unknownDevice': 'Unknown device',

    // Session invalid modal
    'session.invalidTitle': 'Session expired',
    'session.invalidHint': 'This device has been logged out.',
    'session.reLogin': 'Log in again',

    // Signing-out overlay
    'signOut.title': 'Signing out…',
    'signOut.message': 'Saving your unsynced changes',
    'signOut.hint': 'Please wait a moment',
  },

  zh: {
    'reader.remotePosition.title': '从另一台设备继续？',
    'reader.remotePosition.body': '另一台设备已读到本书的 {percent}%。',
    'reader.remotePosition.jump': '跳转到那里',
    'reader.remotePosition.stay': '留在当前',
    'reader.furthestRead': '读到这里',
    // Library
    'library.addBook': '添加书籍',
    'library.uploading': '上传中...',
    'library.recentBooks': '最近阅读',
    'library.emptyTitle': '书库为空',
    'library.emptyHint': '点击「添加书籍」或拖放文件开始',
    'library.loading': '正在加载书库...',
    'library.dropHere': '将书籍拖放到这里',
    'library.dropFormats': 'epub, pdf, txt, mobi',
    'library.signOut': '退出登录',
    'library.account': '账号中心',
    'stuckPdf.title': '{count} 本旧 PDF 需要重新上传',
    'stuckPdf.explanation': '这些 PDF 是在 PDF 转换功能上线之前上传的，无法打开阅读。删除后重新上传即可享受 AI 章节清理和双视图阅读。',
    'stuckPdf.deleteBook': '删除',
    'sidebar.myBookshelf': '我的书架',
    'sidebar.allBooks': '全部图书',
    'sidebar.reading': '正在阅读',
    'sidebar.finished': '已读完',
    'sidebar.curatedLibrary': '精选书库',
    'sidebar.readingAnalytics': '阅读分析',
    'sidebar.userCenter': '用户中心',
    'sidebar.collapse': '收起侧边栏',
    'sidebar.expand': '展开侧边栏',
    'sidebar.darkMode': '深色模式',
    'sidebar.lightMode': '浅色模式',
    'sidebar.search': '搜索',
    'sidebar.searchPlaceholder': '搜索书籍...',
    'sidebar.home': '首页',
    'sidebar.settings': '设置',
    'landing.continueReading': '继续阅读',
    'landing.quote': '阅读金句',
    'landing.like': '点赞',
    'landing.liked': '已赞',
    'landing.nextQuote': '换一句',
    'landing.todayRead': '今日已阅读',
    'landing.todayNotStarted': '今日尚未开始',
    'landing.weekRead': '本周已阅读',
    'landing.monthRead': '本月已阅读',
    'landing.weekReading': '本周阅读',
    'landing.lastRead': '最近阅读',
    'library.deleteBook': '删除书籍',
    'library.uploadFailed': '上传失败',
    'library.drmProtected': '此文件受 DRM 保护，无法打开。',
    'library.huffNotSupported': '此 MOBI 使用 HUFF/CDIC 压缩，暂不支持。请以标准压缩格式重新保存后再试。',
    'library.parseFailed': '无法读取此文件 — 文件可能已损坏或使用了不支持的格式变体。',
    'library.loadFailed': '加载书籍失败',

    'library.deleteFailed': '删除书籍失败',
    'library.invalidFormat': '请上传 epub、mobi 或 pdf 格式的文件',
    'library.pdfTemporarilyDisabled': 'PDF 上传暂时关闭，我们正在重做 PDF 阅读器。目前 EPUB 和 MOBI 体验最佳。',
    'library.pdf.confirm.title': '确认转换 PDF？',
    'library.pdf.confirm.summary': '本次转换将消耗 {pages} 页配额',
    'library.pdf.confirm.colCurrent': '当前',
    'library.pdf.confirm.colAfter': '转换后',
    'library.pdf.confirm.rowMonthly': '本月',
    'library.pdf.confirm.rowDaily': '今日',
    'library.pdf.confirm.confirm': '确认转换',
    'library.pdf.confirm.cancel': '取消',
    'library.pdf.unit.pages': '{n} 页',
    'library.pdf.error.perBookCapTitle': '单本超出限额',
    'library.pdf.error.perBookCapBody': '此 PDF 超出当前套餐的单本上限',
    'library.pdf.error.perBookCapLegend': '{pages} 页 — 超过 {cap} 页上限',
    'library.pdf.error.quotaExceededTitle': '配额不足',
    'library.pdf.error.quotaExceededBody': '本次转换需要 {pages} 页配额',
    'library.pdf.error.needs': '需要 {pages}',
    'library.pdf.error.unreadableTitle': '无法读取此 PDF',
    'library.pdf.error.unreadableBody': '此文件似乎无法读取，常见原因：',
    'library.pdf.error.unreadableCause1': '文件损坏或不完整',
    'library.pdf.error.unreadableCause2': 'PDF 加密或受密码保护',
    'library.pdf.error.unreadableCause3': '文件其实不是 PDF（仅后缀是 .pdf）',
    'library.pdf.error.dismiss': '我知道了',
    'library.pdf.upgrade.title': 'PDF 转换是 Pro 专属功能',
    'library.pdf.upgrade.body': 'PDF 转换为 Pro 和 Max 专属功能。升级解锁：',
    'library.pdf.upgrade.benefit1': '单本最多 400 页',
    'library.pdf.upgrade.benefit2': '每月最多 600 页',
    'library.pdf.upgrade.benefit3': '跨设备云同步',
    'library.pdf.upgrade.cta': '查看套餐',
    'library.freeLimitReached': '免费版最多 {limit} 本书。升级付费版解锁无限书籍和云同步。',
    'library.deleteBook': '删除',
    'library.returnBook': '还书',
    'library.bookLimitExceeded': '已达书架上限。还一本书腾出名额，或升级解锁无限书籍。',
    'library.bookExists': '「{title}」{author}已存在。\n\n是否上传为新副本？',
    'library.bookExistsByAuthor': '（{author}）',
    'library.cloudProOnly': '云同步仅限付费用户。升级以解锁云备份。',
    'library.cloudBackupTitle': '你的书会自动备份到云端',
    'library.cloudBackupDesc': '付费会员的书会私密备份到云端，换设备也能继续读。可随时用「全部图书」右上角的开关关闭。',
    'library.cloudBackupTurnOff': '关闭',
    'library.cloudBackupGotIt': '知道了',
    'library.cloudBackupToggle': '自动备份到云端',
    'library.notAvailable': '这本书在其他设备上传，此处不可用。请重新上传。',
    'library.phantomDialogDesc': '这本书的文件在此设备上不存在，云端也没有副本。你可以重新上传文件（将作为新书添加），或将它从书架移除。',
    'library.phantomUploadNew': '作为新书上传',
    'library.phantomRemove': '从书架移除',
    'library.cloudOnly': '云端',
    'library.downloading': '正在下载…',
    'library.downloadFailed': '下载失败，请检查网络连接后重试。',
    'library.offlineCloudBook': '当前离线，此书存储在云端，需要联网才能阅读。',
    'library.confirmDelete': '确定要删除这本书吗？',
    'library.confirmRemoveCurated': '从书架移除这本书？你可以随时从精选书库重新添加。',
    'library.deleteDialogDesc': '此书已备份到云端，你想要？',
    'library.deleteLocalOnly': '释放本地空间',
    'library.deleteCompletely': '彻底删除',
    'library.confirmFreeLocalSpace': '删除本地文件？此书已备份到云端，可以随时重新下载。',
    'library.confirmBulkFreeLocalSpace': '释放 {count} 本书的本地空间？云端副本不受影响。',
    'library.freeLocalSpace': '释放本地空间',
    'library.freeLocalSpaceShort': '释放空间',
    'library.allBooks': '全部图书',
    'library.converting': '转换中',
    'chapterSelection.batchTitle': '慢速转换，配额减半',
    'chapterSelection.batchHint': '通常数小时内完成，最迟次日；完成后书架上即可阅读',
    'chapterSelection.chargeSummary': '本次将扣 {pages} 页配额',
    'chapterSelection.cachedDiscount': '（{pages} 页命中共享缓存，半价）',
    'library.batchPendingBadge': '排队中·慢速',
    'reader.pdfBatch.title': '排队转换中',
    'reader.pdfBatch.hint': '这本书选择了慢速转换（配额减半）。通常数小时内完成，最迟次日——转换好后书架上就能直接打开。',
    'library.badgeExtracting': '解析中',
    'library.badgeConverting': '整理',
    'library.badgeFinishing': '即将完成',
    'library.pendingPreview': '请选择章节',
    'library.pendingPreviewBadge': '待选章节',
    'library.uploadingBadge': '上传中…',
    'library.scanOnlyBadge': '需 OCR',
    'library.scanOnlyBadgeTitle': '扫描版 PDF — 请先用 OCR 工具识别文字后再上传',
    'chapterSelection.title': '选择要转换的章节',
    'chapterSelection.unreadablePdf': '无法读取这个 PDF——文件可能已损坏或格式异常。试试重新导出（预览 → 导出为 PDF，或 打印 → 存为 PDF）后再上传。',
    'chapterSelection.selectAll': '全选',
    'chapterSelection.invertSelection': '反选',
    'chapterSelection.selectedCount': '已选 {n} 章 / {pages} 页',
    'chapterSelection.includesFreePages': '（其中 {pages} 页免费）',
    'chapterSelection.alreadyPaid': '已付 · 免费',
    'chapterSelection.alreadyConverted': '✓ 已转换',
    'library.addChapters': '增加章节',
    'chapterSelection.quotaUsage': '占用配额 {used} / {cap} 页',
    'chapterSelection.exceedsCap': '超出单本上限 {cap} 页',
    'chapterSelection.exceedsQuota': '选中页数超出剩余配额，请减少选择',
    'chapterSelection.outlineSparse': '章节信息不完整，已尽量识别',
    'chapterSelection.outlineHeuristic': '原书无目录，已按版式自动切分',
    'chapterSelection.tocHarvested': '已按原书目录校正章节标题',
    'chapterSelection.tocHarvestedPartial': '部分章节标题已按原书目录校正，其余为自动检测',
    'chapterSelection.fallbackSingle': '无法识别章节，将作为单本转换',
    'chapterSelection.loadingOutline': '正在解析章节…',
    'chapterSelection.fallbackChunks': '未能识别章节名，已按页数均分',
    'chapterSelection.pageRangeTitle': '第 {start}–{end} 页',
    'chapterSelection.scanOnlyTitle': '⚠️ 扫描版 PDF，无法转换',
    'chapterSelection.scanOnlyBody': '此 PDF 平均每页仅 {chars} 个字符，几乎没有可提取的文本。请先用 OCR 工具（如 Adobe Acrobat、ABBYY FineReader、Tesseract）将扫描内容识别为文本后再上传。',
    'chapterSelection.startConversion': '开始转换',
    'chapterSelection.submitting': '提交中…',
    'reader.newChaptersAvailable': '新增 {n} 段内容',
    'reader.conversionDoneWithNew': '转换完毕 · 新增 {n} 段',
    'reader.conversionDone': '转换完毕',
    'reader.refreshToView': '刷新查看',
    'common.dismiss': '关闭',
    'library.searchResults': '个结果',
    'library.continueReading': '继续阅读',
    'library.continue': '继续',
    'library.showAll': '显示全部',
    'library.back': '返回',
    'library.bookCount': '{count} 本书',
    'library.select': '选择',
    'library.cancel': '取消',
    'library.backToHome': '返回首页',
    'library.scrollToTop': '回到顶部',
    'library.selectedCount': '已选择 {count} 本',
    'library.selectAll': '选择全部',
    'library.pinToTop': '置顶',
    'library.unpinFromTop': '取消置顶',
    'library.downloadToLocal': '下载到本地',
    'library.addToCloudShort': '添加到云端书库',
    'library.removeFromLocalShort': '移出本地书库',
    'library.deleteShort': '删除',
    'library.confirmRemoveFromLocal': '确定将这 {count} 本书移出本地书库？书籍将从本设备删除。',
    'library.finishedReading': '已读完',
    'library.currentlyReading': '正在阅读',
    'library.finishedShort': '读完',
    'library.wpmUnit': '字/分钟',
    'library.markFinished': '标记读完',
    'library.markFinishedShort': '读完',
    'library.markFinishedTip': '正文内容已结束。点此标记已读完。',
    'library.confirmMarkFinished': '确定将这本书标记为已读完吗？',
    'reader.bookFinishedEyebrow': '读完了',
    'reader.bookJourneyRevisitEyebrow': '重温这段旅途',
    'reader.bookJourneyBackToReading': '返回阅读',
    'reader.bookJourneySentinel': '✓ 已读完 · 翻看旅途',
    'reader.bookJourneyButton': '阅读旅程',
    'reader.bookJourneySelectAll': '全选',
    'reader.bookJourneyDeselectAll': '取消全选',
    'reader.bookJourneyWordsLabel': '查过的词',
    'reader.bookJourneyNotesLabel': '笔记',
    'reader.bookJourneyShowAll': '查看全部',
    'reader.bookJourneyAddToLoom': '加入 Loom',
    'reader.bookJourneyLoomAdded': '已加入 {added}',
    'reader.bookJourneyLoomLimited': '{n} 个超出免费上限',
    'reader.bookFinishedOrdinal': '这是你在 EaseVerse 读完的第 {n} 本书',
    'reader.bookFinishedShareGroup': '分享到',
    'reader.bookFinishedShareWechat': '微信',
    'reader.bookFinishedShareWeibo': '微博',
    'reader.bookFinishedShareXhs': '小红书',
    'reader.bookFinishedShareX': 'X',
    'reader.bookFinishedShareIg': 'Instagram',
    'reader.bookFinishedShareFb': 'Facebook',
    'reader.bookFinishedShareLine': 'LINE',
    'reader.bookFinishedShareTg': 'Telegram',
    'reader.bookFinishedShareCopy': '复制链接',
    'reader.bookFinishedShareText': '我在 EaseVerse 读完了《{title}》✨ 这是我的第 {n} 本 📚 {url}',
    'reader.bookFinishedShareWechatReady': '文案已复制，粘贴到微信分享',
    'reader.bookFinishedShareLinkCopied': '链接已复制',
    'reader.bookFinishedShareXhsReady': '图片已保存，文案已复制，去小红书发布吧',
    'reader.bookFinishedShareIgReady': '图片已保存，文案已复制，去 Instagram 发布吧',
    'reader.bookFinishedShareFailed': '分享失败，请再试一次',
    'reader.bookFinishedLabelTotalTime': '累计阅读',
    'reader.bookFinishedLabelBookLength': '全书字数',
    'reader.bookFinishedLabelHighlights': '高亮',
    'reader.bookFinishedLabelNotes': '笔记',
    'reader.bookFinishedLabelBookmarks': '书签',
    'reader.bookFinishedJourneyLabel': '你的阅读旅程',
    'reader.bookFinishedJourneyPrev': '更早的日子',
    'reader.bookFinishedJourneyNext': '后面的日子',
    'reader.bookFinishedClose': '返回书库',
    'library.editFinishedDate': '修改日期',
    'library.localLibrary': '本地书库',
    'library.filter': '筛选',
    'library.filterStorageSection': '存储',
    'library.filterLanguageSection': '语言',
    'library.filterCloud': '云端',
    'library.filterLocal': '本地',
    'library.filterCurated': '精选书',
    'library.curatedLibrary': '精选书库',
    'library.curatedLibraryDesc': '经典好书，智能注释已就绪',
    'library.curatedComingSoon': '即将上线——精选好书，语块注释即开即用。',
    'library.freeBadge': '免费',
    'library.freeThisMonth': '本月免费',
    'library.curatedEnglish': '英语书库',
    'library.curatedFrench': '法语书库',
    'library.addToLibrary': '加入书库',
    'library.addToMyLibrary': '加入我的书库',
    'library.adding': '添加中...',
    'library.chapterUnit': '章',
    'library.cloudLibrary': '云端书库',
    'library.cloudUsage': '已用 {percent}%',
    'library.cloudUpgrade': '云端书库为付费用户专属功能。升级即可解锁云端存储空间。',
    'library.upgradeLink': '查看方案',
    'library.last7Days': '最近7天',
    'library.thisYear': '今年上传',
    'library.yearUploaded': '{year}年上传',
    'library.yearFinishedCount': '{year}年 （{count}）',
    'library.otherFinishedCount': '其他 （{count}）',
    'library.completedBooksLine1': '阅读里程碑',
    'library.completedBooksLine2': '尚未解锁',
    'library.completedBooksLine3': '从第一本开始',
    'library.readingEmptyLine1': '开始阅读',
    'library.readingEmptyLine2': '选一本书，从这里开始',
    'library.readingEmptyLine3': '随时翻开，随时放下',

    // Site metadata
    'meta.siteTitle': 'EaseVerse Reader ｜ 多语深度阅读平台',

    // Auth
    'auth.title': 'EaseVerse Reader',
    'auth.subtitle': '你的私人阅读空间',
    'auth.checking': '正在检查登录状态...',
    'auth.emailLabel': '邮箱',
    'auth.emailPlaceholder': 'your@email.com',
    'auth.sendCode': '发送验证码',
    'auth.sendingCode': '发送中...',
    'auth.codeSent': '验证码已发送到你的邮箱',
    'auth.enterCode': '输入验证码',
    'auth.codeSentTo': '我们已向以下邮箱发送了 6 位验证码：',
    'auth.verify': '验证并登录',
    'auth.verifying': '验证中...',
    'auth.resend': '重新发送',
    'auth.resendIn': '{seconds}秒后重新发送',
    'auth.diffEmail': '使用其他邮箱',
    'auth.codeIncomplete': '请输入完整的 6 位验证码',
    'auth.newCodeSent': '新验证码已发送',
    'auth.helpLink': '遇到问题？联系我们',
    'auth.extLogin': '使用 EaseVerse Lens 账号登录',
    'auth.extLoggingIn': '登录中...',
    'auth.useEmail': '使用邮箱登录',
    'auth.backToExt': '返回 EaseVerse Lens 登录',
    'auth.or': '或',
    'auth.googleError': 'Google 登录失败',
    'auth.agreeTerms': '我已阅读并同意',
    'auth.termsLink': '服务条款',
    'auth.privacyLink': '隐私政策',
    'auth.subscribeNewsletter': '订阅产品更新和阅读技巧邮件（每月 1–2 封，随时可退订）',
    'auth.darkMode': '深色模式',
    'auth.lightMode': '浅色模式',
    'auth.products': 'EaseVerse 产品',
    'auth.lensDesc': '语境词典',
    'auth.loomDesc': '知识库',
    'auth.readerDesc': '阅读平台',
    'auth.switchTitle': '切换帐号？',
    'auth.switchDesc': '你的 EaseVerse Lens 扩展当前登录的是 {email}，与 Reader 的帐号不同。是否切换到该帐号？',
    'auth.switchConfirm': '切换',
    'auth.switchDismiss': '保持当前',

    // Sidebar
    'sidebar.contents': '目录',
    'sidebar.coverage.read': '已读',
    'sidebar.coverage.partial': '读了一部分',
    'sidebar.coverage.unread': '未读',
    'sidebar.noToc': '没有目录',
    'sidebar.highlightsNotes': '高亮与笔记',
    'sidebar.bookmarks': '书签',
    'sidebar.all': '全部',
    'sidebar.highlights': '高亮',
    'sidebar.notes': '笔记',
    'sidebar.emptyAll': '还没有高亮或笔记。阅读时选择文本即可高亮或添加笔记。',
    'sidebar.emptyHighlights': '还没有高亮。阅读时选择文本即可高亮。',
    'sidebar.emptyNotes': '还没有笔记。阅读时选择文本并添加笔记。',
    'sidebar.emptyBookmarks': '还没有书签。点击「添加」收藏当前位置。',
    'sidebar.add': '添加',
    'sidebar.save': '保存',
    'sidebar.cancel': '取消',
    'sidebar.removeHighlight': '移除高亮（保留笔记）',
    'sidebar.unknownChapter': '未知章节',
    'sidebar.bookmark': '书签',
    'sidebar.page': '第 {page} 页',

    'reader.settingsCurrentBookHint': '仅当前书',
    'reader.settingsAllBooksHint': '所有书',
    'reader.viewMode': '阅读模式',
    'reader.scroll': '滚动',
    'reader.chapter': '章节',
    'reader.fontSize': '字号',
    'reader.pageWidth': '页宽',
    'reader.pageMargin': '页边距',
    'reader.pageMarginNarrow': '窄',
    'reader.pageMarginStandard': '标准',
    'reader.pageMarginWide': '宽',
    'reader.lineHeight': '行高',
    'reader.paragraph': '段距',
    'reader.letter': '字距',
    'reader.setLanguage': '请选择本书的语言',
    'reader.languageConfirm': '确定',
    'reader.changeLanguage': '更改语言',
    'reader.languageMismatch': '此书语言可能为：',
    'notification.dismiss': '关闭',
    'reader.bookLanguageLabel': '书籍语言',
    'reader.font': '字体',
    'reader.theme': '主题',
    'reader.progressBar': '进度条',
    'reader.always': '始终',
    'reader.hover': '悬停',
    'reader.hidden': '隐藏',
    'reader.einkMode': '墨水屏模式',
    'reader.einkAuto': '自动',
    'reader.einkOn': '开启',
    'reader.einkOff': '关闭',
    'reader.pagedMode': '翻页模式',
    'reader.tapToSelect': '单击查词',
    'reader.autoPlayPronunciation': '自动播放发音',
    'reader.autoPlayPronunciationHint': '仅当前书',
    'settings.languageAndLearning': '语言与学习',
    'settings.interaction': '互动',
    'settings.pronunciation': '读音偏好',
    'settings.accent': '口音',
    'settings.voice': '声音',
    'settings.accentUS': '美音',
    'settings.accentGB': '英音',
    'settings.voiceMale': '男声',
    'settings.voiceFemale': '女声',
    'settings.proOnly': '仅 Pro 可用',
    'language.name.zh': '中文',
    'language.name.en': '英语',
    'language.name.ja': '日语',
    'language.name.ko': '韩语',
    'language.name.fr': '法语',
    'language.name.de': '德语',
    'language.name.es': '西班牙语',
    'language.name.pt': '葡萄牙语',
    'language.name.ru': '俄语',
    'language.name.it': '意大利语',
    'language.name.vi': '越南语',
    'language.name.other': '其他',
    'reader.chapterLabel': '本章',
    'reader.bookLabel': '全书',
    'reader.prevChapter': '上一章',
    'reader.nextChapter': '下一章',
    'reader.prevPage': '上一页',
    'reader.nextPage': '下一页',
    'reader.goBack': '返回',
    'reader.backToPosition': '返回原位置',
    'reader.sessionSummary': '本次阅读 {time}',
    'einkSel.highlight': '高亮',
    'einkSel.highlighted': '已高亮',
    'einkSel.note': '笔记',
    'einkSel.discard': '删除高亮',
    'einkSel.gloss': '释义',
    'einkSel.explain': '深入解释',
    'einkSel.notePlaceholder': '写点笔记…',
    'einkSel.noteSave': '保存',
    'einkSel.vocab': '生词本',
    'einkSel.vocabSaved': '已加入',
    'einkSel.sentence': '整句',
    'reader.sessionSummaryWithWords': '本次阅读 {time} · {words} 字',
    'reader.sessionSummaryCoverage': '本次阅读 {time} · 新读 {coverage} 字',
    'reader.sessionSummaryReread': '本次阅读 {time} · 重温 {reread} 字',
    'reader.sessionSummaryThreeBucket': '本次阅读 {time} · 新读 {coverage} 字 · 重温 {reread} 字',
    'reader.formatTemporarilyUnavailable': '{format} 格式正在重新打磨，暂不可用。你的原始文件和阅读进度已经安全保存，恢复后可直接打开。',
    'reader.pdfConverting': 'PDF 正在转换为可阅读格式，通常需要 1-2 分钟。完成后页面会自动刷新。',
    'reader.pdfConvertingElapsed': '已等待 {elapsed}',
    'reader.pdfPreparing.title': '正在准备你的 PDF',
    'reader.pdfPreparing.stage1': '解析 PDF 文本',
    'reader.pdfPreparing.stage2': '正在整理为可读格式',
    'reader.pdfPreparing.stage3': '生成电子书',
    'reader.pdfPreparing.stage4': '保存到云端',
    'reader.pdfPreparing.elapsed': '已用时 {elapsed}',
    'reader.pdfPreparing.eta': '约还需 {eta}',
    'reader.pdfPreparing.activeChapter': '正在处理:',
    'reader.pdfPreparing.modelSlow': '慢速模型',
    'reader.pdfPreparing.modelPassthrough': '直通',
    'reader.dualViewModeLabel': '阅读模式',
    'reader.dualViewText': '文字',
    'reader.dualViewDual': '对照',
    'reader.dualViewOriginal': '原版',
    'reader.dualViewNeedsUpgrade': '此书需要升级才能使用对照模式',
    'reader.dualViewNeedsWiderScreen': '对照模式需要较宽的屏幕',
    'reader.reanchor': '将两侧对齐到当前段落',
    'reader.dualViewUpgradeTooltip': '升级此书以启用对照模式',
    'reader.dualViewUpgradeConfirm': '升级会重新处理这本书，约 1-2 分钟。期间无法阅读。继续？',
    'reader.dualViewUpgradeFailed': '升级失败，请稍后再试。',
    'reader.qualityMediumToast': '本书部分文字层提取可能不完整，已为你默认对照模式以便核对原版。',
    'reader.qualityLowToast': '这本书像是扫描版或低质量 PDF，已为你默认原版模式。',
    'reader.qualityLowTooltip': '本书的文字提取不可靠，点击查看详情。',
    'reader.qualityLowAria': '文字质量提示',
    'reader.qualityLowPopoverTitle': '文字层不完整',
    'reader.qualityLowPopoverBody': '本书无法从源 PDF 中可靠地提取文字。已为你默认原版模式以保留原貌。',
    'reader.qualityDontShowAgain': '本书不再提示',
    'reader.openAsOriginal': '以原版 PDF 打开',
    'reader.openAsOriginalHint': '直接查看源 PDF。在此兜底模式下，划线、笔记和 Lens 不可用。',
    'reader.figurePeekFigureOne': '1 张图',
    'reader.figurePeekFigureMany': '{n} 张图',
    'reader.figurePeekTableOne': '1 张表',
    'reader.figurePeekTableMany': '{n} 张表',
    'reader.figurePeekEquationOne': '1 个公式',
    'reader.figurePeekEquationMany': '{n} 个公式',
    'reader.peekDialogLabel': '原版 PDF 页面预览',
    'reader.peekLoading': '正在加载 PDF 页…',
    'reader.peekClose': '关闭预览',
    'reader.pdfConversionFailed': '这份 PDF 转换失败。原文件已保留；你可以删除后重新上传，或联系我们。',
    'reader.minutesShort': '{n} 分钟',
    'reader.hoursShort': '{n} 小时',
    'reader.hoursMinutesShort': '{h} 小时 {m} 分钟',
    'reader.theme.white': '白色',
    'reader.theme.slate': '石板灰',
    'reader.theme.sage': '护眼绿',
    'reader.theme.parchment': '羊皮纸',
    'reader.theme.midnight': '午夜',
    'reader.loginRequired': '请先登录再阅读本书',
    'reader.loadFailed': '加载书籍失败',
    'reader.bookNotFound': '未找到该书籍',
    'reader.deepReading.title': '深读',
    'reader.deepReading.close': '关闭深读面板',
    'reader.deepReading.toggle': '深读面板',
    'reader.deepReading.bubbles': '查词图谱',
    'lens.pronounceLocked': '升级 Pro 即可朗读该语言',
    'reader.deepReading.viewHistory': '历史',
    'reader.deepReading.viewFrequency': '词频',
    'reader.deepReading.playNormal': '朗读',
    'reader.deepReading.playSlow': '慢速朗读 (0.7×)',
    'reader.deepReading.notes': '笔记',
    'reader.deepReading.chat': '对话',
    'reader.deepReading.tabLookups': '查词',
    'reader.deepReading.tabNotes': '笔记',
    'reader.deepReading.done': '完成',
    'reader.deepReading.comingSoon': '即将上线',
    'reader.deepReading.signInToSee': '登录后查看你的查词图谱',
    'reader.deepReading.loading': '加载中…',
    'reader.deepReading.loadError': '加载失败',
    'reader.deepReading.noLookups': '本书还没有查词记录',
    'reader.deepReading.contexts': '上下文',
    'reader.deepReading.back': '← 返回图谱',
    'reader.deepReading.noNotes': '还没有笔记或高亮',
    'reader.deepReading.notePlaceholder': '写下你的笔记…',
    'reader.deepReading.addNote': '+ 添加笔记',
    'reader.deepReading.jumpToPassage': '跳转到原文',
    'reader.deepReading.toLoom': '→ 加入 Loom',
    'reader.deepReading.inLoom': '✓ 已在 Loom',
    'reader.deepReading.loomError': '加入 Loom 失败',
    'reader.deepReading.collectToLoom': '收藏到 Loom',
    'reader.deepReading.deleteEntry': '从列表移除',
    'reader.deepReading.reply': '↩ 回复',
    'reader.deepReading.replyOpen': '回复这条笔记',
    'reader.deepReading.replyPlaceholder': '写下你的回复…',
    'reader.deepReading.repliesShow': '展开 {n} 条回复',
    'reader.deepReading.repliesHide': '收起回复',
    'reader.deepReading.editCount': '编辑过 {n} 次',
    'reader.deepReading.editHistoryOpen': '查看编辑历史',
    'reader.deepReading.deleteNote': '删除',
    'reader.deepReading.timeJustNow': '刚刚',
    'reader.deepReading.timeMinAgo': '{n} 分钟前',
    'reader.deepReading.timeHourAgo': '{n} 小时前',
    'reader.deepReading.timeDayAgo': '{n} 天前',
    'reader.deepReading.editHistoryTitle': '编辑历史',
    'reader.deepReading.editHistoryCurrent': '当前版本',
    'reader.deepReading.editHistoryNone': '暂无历史版本',
    'reader.deepReading.editHistoryError': '加载历史失败',
    'reader.followLink': '前往链接',

    'note.placeholder': '写笔记...',
    'note.addNotePlaceholder': '添加笔记...',
    'note.editNote': '编辑笔记',
    'note.addNote': '添加笔记',
    'note.deleteNote': '删除笔记',
    'note.deleteHighlight': '删除高亮',
    'note.save': '保存',
    'note.copy': '复制',
    'note.cancel': '取消',
    'note.close': '关闭',
    'note.unpin': '取消固定',
    'note.highlightedText': '高亮文本',
    'note.highlightColor': '高亮 {color}',
    'note.underlineColor': '下划线 {color}',
    'note.saving': '保存中...',
    'note.saved': '已保存',
    'note.error': '错误',

    // Loading
    'loading.default': '加载中...',
    'loading.backToLibrary': '返回书库',

    // Stats Card
    'stats.lessThan1min': '不到1分钟',
    'stats.hour': '小时',
    'stats.minute': '分钟',
    'stats.goalLabel': '本月阅读目标：每周{days}天，每天{time}',
    'stats.goalLabelDaily': '本月阅读目标：每天{time}',
    'stats.noGoal': '未设置阅读目标',
    'stats.streakDays': '连续阅读{n}天',
    'stats.streakLabel': '连续',
    'stats.thisMonth': '本月',
    'stats.todayLabel': '今日',
    'stats.thisWeek': '本周阅读',
    'stats.reading': '月阅读',
    'stats.yearGoalLabel': '年目标',
    'stats.yearlyReadingGoal': '{year}年阅读目标',
    'stats.wan': '万',
    'stats.wordsUnit': '字',
    'stats.mon': '一', 'stats.tue': '二', 'stats.wed': '三', 'stats.thu': '四',
    'stats.fri': '五', 'stats.sat': '六', 'stats.sun': '日',
    'stats.month1': '1', 'stats.month2': '2', 'stats.month3': '3',
    'stats.month4': '4', 'stats.month5': '5', 'stats.month6': '6',
    'stats.month7': '7', 'stats.month8': '8', 'stats.month9': '9',
    'stats.month10': '10', 'stats.month11': '11', 'stats.month12': '12',
    'stats.setGoalTitle': '设置本月阅读目标',
    'stats.weeklyDays': '每周阅读天数',
    'stats.dailyMinutes': '每天阅读时长',
    'stats.yearlyWords': '年度字数目标',
    'stats.unitWan': '万字',
    'stats.save': '保存',
    'stats.confirmSave': '确认修改',
    'stats.goalConfirmMsg': '保存后本月还可修改{n}次，确定要修改吗？',
    'stats.goalEditExhausted': '本月修改次数已用完，下个月才能再次修改。',
    'stats.goalRemaining': '本月剩余修改次数：{n}次',
    'stats.cancel': '取消',
    'stats.minuteShort': '分钟',
    'stats.custom': '自定义',
    'stats.wordsCount': '≈{n}字',
    'stats.wordsEstimateHint': '基于阅读位置变化的估算值',
    'stats.noReading': '未阅读',
    'stats.streakHint': '阅读≥5分钟即计入连续天数',
    'stats.dailyThreshold': '每天{time}',
    'stats.remaining': '还需{time}',
    // Dashboard
    'dashboard.title': '阅读分析',
    'dashboard.titleLang': '{lang}文本阅读分析',
    'dashboard.backToLibrary': '书库',
    'dashboard.langDistribution': '语言分布',
    'dashboard.langMonth': '月',
    'dashboard.langYear': '年',
    'dashboard.langAll': '全部',
    'dashboard.langSingleHint': '当你阅读其他语言的书籍时，可在此按语言筛选',
    'dashboard.langFold': '收起',
    'dashboard.langName.zh': '中文',
    'dashboard.langName.en': '英语',
    'dashboard.langName.ja': '日语',
    'dashboard.langName.ko': '韩语',
    'dashboard.langName.fr': '法语',
    'dashboard.langName.de': '德语',
    'dashboard.langName.es': '西班牙语',
    'dashboard.langName.pt': '葡萄牙语',
    'dashboard.langName.ru': '俄语',
    'dashboard.langName.it': '意大利语',
    'dashboard.langName.nl': '荷兰语',
    'dashboard.langName.ar': '阿拉伯语',
    'dashboard.langName.th': '泰语',
    'dashboard.langName.vi': '越南语',
    'dashboard.todaySummary': '今日',
    'dashboard.weekSummary': '本周',
    'dashboard.readingTime': '阅读时长',
    'dashboard.wordsRead': '阅读字数',
    'dashboard.totalReadingDays': '累计阅读',
    'dashboard.dayUnit': '天',
    'dashboard.achievements': '阅读成就',
    'dashboard.currentStreak': '连续阅读{count}天',
    'dashboard.showAllBadges': '查看全部徽章...',
    'dashboard.cat_member': '付费会员',
    'dashboard.memberBadgeLabel': 'Pro会员',
    'dashboard.cat_days': '累计阅读',
    'dashboard.cat_streak': '连续阅读',
    'dashboard.cat_words': '阅读字数',
    'dashboard.cat_time': '阅读时长',
    'dashboard.cat_books': '读完书籍',
    'dashboard.nextMilestone': '下一个',
    'dashboard.memberRewardLine1': '累计维持任一付费档（Basic / Pro / Max）365 天',
    'dashboard.memberRewardLine2': '即可解锁一个月 Pro 会员奖励',
    'dashboard.memberGiftUnlocked': '恭喜！你已经陪伴EaseVerse满365天啦，你的礼物已送达~🎁',
    'dashboard.memberActiveTooltip': '这是你成为EaseVerse会员的第{days}天，等到第365天，我有一份礼物要送给你哦~😊',
    'dashboard.memberPausedTooltip': '你已累计成为EaseVerse会员{days}天，续费后继续累计哦～',
    'dashboard.memberNotPaidTooltip': '哎呀，你还没有成为EaseVerse会员呢，现在就去个人中心解锁会员身份吧～',
    'dashboard.badgeUnlocked': '恭喜你获得「{0}」徽章\n继续努力，解锁「{1}」',
    'dashboard.badgeMaxLevel': '恭喜你获得「{0}」徽章\n你已达到最高等级！',
    'dashboard.badgeLocked': '现在就开始阅读\n解锁「{0}」成就',
    'dashboard.badgeCountSuffix': '人已获得',
    'dashboard.badgeCongratsTooltip': '恭喜 🎉 你已解锁此勋章',
    'dashboard.badgeCountText': '已有 {n} 位同学解锁',
    'dashboard.badgeExpandMore': '展开更多',
    'dashboard.daysSuffix': '天',
    'dashboard.booksSuffix': '本',
    'dashboard.viewAllBadges': '查看全部徽章',
    'dashboard.statToday': '今日阅读',
    'dashboard.statWords': '阅读字数',
    'dashboard.statTotalWords': '累计阅读字数',
    'dashboard.statRereadWords': '另含重温 {n} 字',
    'dashboard.wordsPopoverTitle': '显示语言',
    'dashboard.wordsPopoverSaved': '已保存',
    'dashboard.statWeek': '本周阅读',
    'dashboard.statTotalDays': '累计阅读',
    'dashboard.totalDaysTooltip': '{date}，你正式开启了在EaseVerse的原著阅读之旅，至今已经{days}天啦✈️',
    'dashboard.dailyTrend': '每日趋势',
    'dashboard.monthlyTotal': '本月阅读{time}',
    'dashboard.dailyAvg': '日均阅读{time}',
    'dashboard.yearMonth': '{year}年{month}月',
    'dashboard.hourlyInsight': '偏好{period}阅读',
    'dashboard.hourlyInsightRange': '偏好{from}至{to}阅读',
    'dashboard.hourlyWords': '每分钟阅读字数',
    'dashboard.wpmHint': '每分钟阅读字数',
    'dashboard.wpmSubtitle': '本月WPM {wpm}字/分钟',
    'dashboard.wpmSubtitleUp': '本月WPM {wpm}字/分钟，相较上个月提升{pct}%',
    'dashboard.wpmSubtitleDown': '本月WPM {wpm}字/分钟，相较上个月下降{pct}%',
    'dashboard.wpmInsight': '{period}每分钟阅读字数最多',
    'dashboard.wpmInsightRange': '{from}至{to}每分钟阅读字数最多',
    'dashboard.hourlyTime': '每时阅读时长',
    'dashboard.wordsUnit': '字',
    'dashboard.period.lateNight': '深夜',
    'dashboard.period.dawn': '黎明',
    'dashboard.period.earlyMorning': '清晨',
    'dashboard.period.lateMorning': '上午',
    'dashboard.period.earlyAfternoon': '午后',
    'dashboard.period.lateAfternoon': '傍晚',
    'dashboard.period.evening': '晚间',
    'dashboard.period.nightfall': '入夜',
    'dashboard.bookBreakdown': '书籍',
    'dashboard.noData': '暂无阅读数据',
    'dashboard.editGoal': '制定目标',
    'dashboard.viewDetails': '查看详情',
    'dashboard.yearlyTime': '年阅读时长',
    'dashboard.yearlyWpm': '年阅读效率',
    'dashboard.monthUnit': '月',
    'dashboard.wpmExplainPlaceholder': 'WPM（每分钟阅读字数）衡量你的阅读速度——详细说明即将上线。',
    // Milestones
    'milestone.streak_3d': '连续阅读3天！',
    'milestone.streak_7d': '连续阅读7天！',
    'milestone.streak_14d': '连续阅读14天！',
    'milestone.streak_21d': '连续阅读21天！',
    'milestone.streak_30d': '连续阅读30天！',
    'milestone.streak_60d': '连续阅读60天！',
    'milestone.streak_100d': '连续阅读100天！',
    'milestone.streak_180d': '连续阅读180天！',
    'milestone.streak_365d': '连续阅读365天！',
    'milestone.words_10k': '累计阅读1万字！',
    'milestone.words_50k': '累计阅读5万字！',
    'milestone.words_100k': '累计阅读10万字！',
    'milestone.words_500k': '累计阅读50万字！',
    'milestone.words_1m': '累计阅读100万字！',
    'milestone.words_2m': '累计阅读200万字！',
    'milestone.words_3m': '累计阅读300万字！',
    'milestone.words_4m': '累计阅读400万字！',
    'milestone.words_5m': '累计阅读500万字！',
    'milestone.time_10h': '累计阅读10小时！',
    'milestone.time_50h': '累计阅读50小时！',
    'milestone.time_100h': '累计阅读100小时！',
    'milestone.time_300h': '累计阅读300小时！',
    'milestone.time_500h': '累计阅读500小时！',
    'milestone.time_1000h': '累计阅读1000小时！',
    'milestone.time_2000h': '累计阅读2000小时！',
    'milestone.weekly_goal_first': '首次达成周目标！',
    'milestone.weekly_goal_4': '连续4周达成目标！',
    'milestone.yearly_25': '年度目标完成25%！',
    'milestone.yearly_50': '年度目标已过半！',
    'milestone.yearly_75': '年度目标完成75%！',
    'milestone.yearly_100': '年度目标达成！',
    'milestone.books_1': '读完第一本书！',
    'milestone.books_3': '读完3本书！',
    'milestone.books_5': '读完5本书！',
    'milestone.books_10': '读完10本书！',
    'milestone.books_25': '读完25本书！',
    'milestone.books_50': '读完50本书！',
    'milestone.books_75': '读完75本书！',
    'milestone.books_100': '读完100本书！',
    'milestone.days_7': '累计阅读7天！',
    'milestone.days_30': '累计阅读30天！',
    'milestone.days_100': '累计阅读100天！',
    'milestone.days_200': '累计阅读200天！',
    'milestone.days_365': '累计阅读365天！',
    'milestone.member_365d': '付费会员满365天！',
    'milestone.lang_words': '{lang}阅读达到{amount}字！',
    'milestone.lang_time': '{lang}阅读达到{amount}！',

    // Chunks — 设置
    'chunks.title': '语块标注',
    'chunks.showAnnotations': '显示语块标注',
    'chunks.filterRange': '显示范围',
    'chunks.levelAdaptive': '自动适配',
    'chunks.modeManual': '手动选择',
    'chunks.levelShowAll': '显示全部',
    'chunks.density': '密度',
    'chunks.densityFocused': '精选',
    'chunks.densityDetailed': '详细',
    'chunks.adaptiveExplore': '正在学习你的偏好 ({count}/{total})',
    'chunks.adaptiveActive': '已根据 {count} 次互动自动调整',
    'chunks.manualLevel': '等级',
    'chunks.levelAll': '全部',
    'chunks.manualCategory': '类别',
    'chunks.cat_vocabulary': '词汇',
    'chunks.cat_phrasal_verb': '动词短语',
    'chunks.cat_collocation': '搭配',
    'chunks.cat_idiom': '习语',
    'chunks.cat_grammar': '语法',
    'chunks.cat_knowledge': '知识',
    'chunks.cat_tone': '语气',
    'chunks.resetProfile': '重置偏好',
    'chunks.modeTranslate': '翻译',
    'chunks.modeSynonym': '同义',
    'chunks.loading': '加载中...',
    'chunks.rehinting': '正在换个角度解读…',
    'chunks.reportBadCache': '报告翻译错误',
    'chunks.generating': '正在生成解释...',
    'chunks.askPlaceholder': '追问',
    'lens.sentenceAnalysis': '句子分析',
    'lens.originalSentence': '原句',
    'lens.step1': '含义',
    'lens.step2': '结构',
    'lens.step3': '深入',
    'lens.analyzing': '分析中...',
    'lens.showGrammar': '显示语法详情',
    'lens.hideGrammar': '隐藏语法详情',
    'lens.openChat': '追问',
    'lens.saveNote': '保存笔记',
    'lens.noteSaved': '已保存',
    'lens.saveNoteFailed': '保存失败',
    'lens.removeNote': '移除笔记',
    'lens.removeNoteFailed': '移除失败',
    'lens.checkingNote': '检查中…',
    'lens.collapse': '收起',
    'chunks.resetConfirm': '重置所有语块学习数据？此操作无法撤销。',
    'chunks.onboardingHint': '点击圆点查看释义',
    'chunks.statusLoading': '正在加载语块标注…',
    'chunks.statusProcessing': '正在分析词汇…',
    'chunks.cacheRefreshed': '本书语块已升级，点此刷新查看',
    'chunks.proRequired': '升级到付费方案解锁语块标注功能。',
    'chunks.bookUsage': "本月语块：{used}/{limit} 本",
    'chunks.bookLimitReached': "本月 {limit} 本已用完，升级 Pro 解锁无限",

    // 设备限制弹窗
    'deviceLimit.titleConfirm': '设备数量已达上限',
    'deviceLimit.titleSelect': '已达设备上限',
    'deviceLimit.confirmDesc': '继续登录将登出以下设备：',
    'deviceLimit.selectDesc': '选择要替换的设备：',
    'deviceLimit.loggedIn': '登录',
    'deviceLimit.cancel': '取消',
    'deviceLimit.confirming': '登录中...',
    'deviceLimit.confirmLogin': '确认登录',
    'deviceLimit.confirmReplace': '确认替换并登录',
    'deviceLimit.unknownDevice': '未知设备',

    // 会话失效弹窗
    'session.invalidTitle': '登录已失效',
    'session.invalidHint': '当前设备已登出。',
    'session.reLogin': '重新登录',

    // 登出进度遮罩
    'signOut.title': '正在登出…',
    'signOut.message': '正在保存未同步的改动',
    'signOut.hint': '请稍候',
  },

  'zh-TW': {
    'reader.remotePosition.title': '從另一台裝置繼續？',
    'reader.remotePosition.body': '另一台裝置已讀到本書的 {percent}%。',
    'reader.remotePosition.jump': '跳轉到那裡',
    'reader.remotePosition.stay': '留在目前位置',
    'reader.furthestRead': '讀到這裡',
    // Library
    'library.addBook': '新增書籍',
    'library.uploading': '上傳中...',
    'library.recentBooks': '最近閱讀',
    'library.emptyTitle': '書庫為空',
    'library.emptyHint': '點擊「新增書籍」或拖放檔案開始',
    'library.loading': '正在載入書庫...',
    'library.dropHere': '將書籍拖放到這裡',
    'library.dropFormats': 'epub, pdf, txt, mobi',
    'library.signOut': '登出',
    'library.account': '帳號中心',
    'stuckPdf.title': '{count} 本舊 PDF 需要重新上傳',
    'stuckPdf.explanation': '這些 PDF 是在 PDF 轉換功能上線之前上傳的，無法開啟閱讀。刪除後重新上傳即可享受 AI 章節清理和雙視圖閱讀。',
    'stuckPdf.deleteBook': '刪除',
    'sidebar.myBookshelf': '我的書架',
    'sidebar.allBooks': '全部圖書',
    'sidebar.reading': '正在閱讀',
    'sidebar.finished': '已讀完',
    'sidebar.curatedLibrary': '精選書庫',
    'sidebar.readingAnalytics': '閱讀分析',
    'sidebar.userCenter': '用戶中心',
    'sidebar.collapse': '收起側邊欄',
    'sidebar.expand': '展開側邊欄',
    'sidebar.darkMode': '深色模式',
    'sidebar.lightMode': '淺色模式',
    'sidebar.search': '搜尋',
    'sidebar.searchPlaceholder': '搜尋書籍...',
    'sidebar.home': '首頁',
    'sidebar.settings': '設定',
    'landing.continueReading': '繼續閱讀',
    'landing.quote': '閱讀金句',
    'landing.like': '點讚',
    'landing.liked': '已讚',
    'landing.nextQuote': '換一句',
    'landing.todayRead': '今日已閱讀',
    'landing.todayNotStarted': '今日尚未開始',
    'landing.weekRead': '本週已閱讀',
    'landing.monthRead': '本月已閱讀',
    'landing.weekReading': '本週閱讀',
    'landing.lastRead': '最近閱讀',
    'library.deleteBook': '刪除書籍',
    'library.uploadFailed': '上傳失敗',
    'library.drmProtected': '此檔案受 DRM 保護，無法開啟。',
    'library.huffNotSupported': '此 MOBI 使用 HUFF/CDIC 壓縮，暫不支援。請以標準壓縮格式重新儲存後再試。',
    'library.parseFailed': '無法讀取此檔案 — 檔案可能已損壞或使用了不支援的格式變體。',
    'library.loadFailed': '載入書籍失敗',
    'library.deleteFailed': '刪除書籍失敗',
    'library.invalidFormat': '請上傳 epub、mobi 或 pdf 格式的檔案',
    'library.pdfTemporarilyDisabled': 'PDF 上傳暫時關閉，我們正在重做 PDF 閱讀器。目前 EPUB 和 MOBI 體驗最佳。',
    'library.pdf.confirm.title': '確認轉換 PDF？',
    'library.pdf.confirm.summary': '本次轉換將消耗 {pages} 頁配額',
    'library.pdf.confirm.colCurrent': '當前',
    'library.pdf.confirm.colAfter': '轉換後',
    'library.pdf.confirm.rowMonthly': '本月',
    'library.pdf.confirm.rowDaily': '今日',
    'library.pdf.confirm.confirm': '確認轉換',
    'library.pdf.confirm.cancel': '取消',
    'library.pdf.unit.pages': '{n} 頁',
    'library.pdf.error.perBookCapTitle': '單本超出限額',
    'library.pdf.error.perBookCapBody': '此 PDF 超出當前套餐的單本上限',
    'library.pdf.error.perBookCapLegend': '{pages} 頁 — 超過 {cap} 頁上限',
    'library.pdf.error.quotaExceededTitle': '配額不足',
    'library.pdf.error.quotaExceededBody': '本次轉換需要 {pages} 頁配額',
    'library.pdf.error.needs': '需要 {pages}',
    'library.pdf.error.unreadableTitle': '無法讀取此 PDF',
    'library.pdf.error.unreadableBody': '此檔案似乎無法讀取，常見原因：',
    'library.pdf.error.unreadableCause1': '檔案損毀或不完整',
    'library.pdf.error.unreadableCause2': 'PDF 加密或受密碼保護',
    'library.pdf.error.unreadableCause3': '檔案其實不是 PDF（僅副檔名是 .pdf）',
    'library.pdf.error.dismiss': '知道了',
    'library.pdf.upgrade.title': 'PDF 轉換是 Pro 專屬功能',
    'library.pdf.upgrade.body': 'PDF 轉換為 Pro 和 Max 專屬功能。升級解鎖：',
    'library.pdf.upgrade.benefit1': '單本最多 400 頁',
    'library.pdf.upgrade.benefit2': '每月最多 600 頁',
    'library.pdf.upgrade.benefit3': '跨裝置雲同步',
    'library.pdf.upgrade.cta': '查看套餐',
    'library.freeLimitReached': '免費版最多 {limit} 本書。升級付費版解鎖無限書籍和雲端同步。',
    'library.deleteBook': '刪除',
    'library.returnBook': '還書',
    'library.bookLimitExceeded': '已達書架上限。還一本書騰出名額，或升級解鎖無限書籍。',
    'library.bookExists': '「{title}」{author}已存在。\n\n是否上傳為新副本？',
    'library.bookExistsByAuthor': '（{author}）',
    'library.cloudProOnly': '雲端同步僅限付費使用者。升級以解鎖雲端備份。',
    'library.cloudBackupTitle': '你的書會自動備份到雲端',
    'library.cloudBackupDesc': '付費會員的書會私密備份到雲端，換裝置也能繼續讀。可隨時用「全部圖書」右上角的開關關閉。',
    'library.cloudBackupTurnOff': '關閉',
    'library.cloudBackupGotIt': '知道了',
    'library.cloudBackupToggle': '自動備份到雲端',
    'library.notAvailable': '這本書在其他裝置上傳，此處不可用。請重新上傳。',
    'library.phantomDialogDesc': '這本書的檔案在此裝置上不存在，雲端也沒有副本。你可以重新上傳檔案（將作為新書加入），或將它從書架移除。',
    'library.phantomUploadNew': '作為新書上傳',
    'library.phantomRemove': '從書架移除',
    'library.cloudOnly': '雲端',
    'library.downloading': '正在下載…',
    'library.downloadFailed': '下載失敗，請檢查網路連線後重試。',
    'library.offlineCloudBook': '目前離線，此書儲存在雲端，需要連線才能閱讀。',
    'library.confirmDelete': '確定要刪除這本書嗎？',
    'library.confirmRemoveCurated': '從書架移除這本書？你可以隨時從精選書庫重新添加。',
    'library.deleteDialogDesc': '此書已備份到雲端，你想要？',
    'library.deleteLocalOnly': '釋放本地空間',
    'library.deleteCompletely': '徹底刪除',
    'library.confirmFreeLocalSpace': '刪除本地檔案？此書已備份到雲端，可以隨時重新下載。',
    'library.confirmBulkFreeLocalSpace': '釋放 {count} 本書的本地空間？雲端副本不受影響。',
    'library.freeLocalSpace': '釋放本地空間',
    'library.freeLocalSpaceShort': '釋放空間',
    'library.allBooks': '全部圖書',
    'library.converting': '轉換中',
    'chapterSelection.batchTitle': '慢速轉換，配額減半',
    'chapterSelection.batchHint': '通常數小時內完成，最遲次日；完成後書架上即可閱讀',
    'chapterSelection.chargeSummary': '本次將扣 {pages} 頁配額',
    'chapterSelection.cachedDiscount': '（{pages} 頁命中共享快取，半價）',
    'library.batchPendingBadge': '排隊中·慢速',
    'reader.pdfBatch.title': '排隊轉換中',
    'reader.pdfBatch.hint': '這本書選擇了慢速轉換（配額減半）。通常數小時內完成，最遲次日——轉換好後書架上就能直接打開。',
    'library.badgeExtracting': '解析中',
    'library.badgeConverting': '整理',
    'library.badgeFinishing': '即將完成',
    'library.pendingPreview': '請選擇章節',
    'library.pendingPreviewBadge': '待選章節',
    'library.uploadingBadge': '上傳中…',
    'library.scanOnlyBadge': '需 OCR',
    'library.scanOnlyBadgeTitle': '掃描版 PDF — 請先用 OCR 工具辨識文字後再上傳',
    'chapterSelection.title': '選擇要轉換的章節',
    'chapterSelection.unreadablePdf': '無法讀取這個 PDF——檔案可能已損毀或格式異常。試試重新匯出（預覽 → 輸出為 PDF，或 列印 → 儲存為 PDF）後再上傳。',
    'chapterSelection.selectAll': '全選',
    'chapterSelection.invertSelection': '反選',
    'chapterSelection.selectedCount': '已選 {n} 章 / {pages} 頁',
    'chapterSelection.includesFreePages': '（其中 {pages} 頁免費）',
    'chapterSelection.alreadyPaid': '已付 · 免費',
    'chapterSelection.alreadyConverted': '✓ 已轉換',
    'library.addChapters': '增加章節',
    'chapterSelection.quotaUsage': '占用配額 {used} / {cap} 頁',
    'chapterSelection.exceedsCap': '超出單本上限 {cap} 頁',
    'chapterSelection.exceedsQuota': '選中頁數超出剩餘配額，請減少選擇',
    'chapterSelection.outlineSparse': '章節資訊不完整，已盡量辨識',
    'chapterSelection.outlineHeuristic': '原書無目錄，已按版式自動切分',
    'chapterSelection.tocHarvested': '已按原書目錄校正章節標題',
    'chapterSelection.tocHarvestedPartial': '部分章節標題已按原書目錄校正，其餘為自動檢測',
    'chapterSelection.fallbackSingle': '無法辨識章節，將作為單本轉換',
    'chapterSelection.loadingOutline': '正在解析章節…',
    'chapterSelection.fallbackChunks': '未能辨識章節名，已按頁數均分',
    'chapterSelection.pageRangeTitle': '第 {start}–{end} 頁',
    'chapterSelection.scanOnlyTitle': '⚠️ 掃描版 PDF，無法轉換',
    'chapterSelection.scanOnlyBody': '此 PDF 平均每頁僅 {chars} 個字元，幾乎沒有可提取的文字。請先用 OCR 工具（如 Adobe Acrobat、ABBYY FineReader、Tesseract）將掃描內容辨識為文字後再上傳。',
    'chapterSelection.startConversion': '開始轉換',
    'chapterSelection.submitting': '提交中…',
    'reader.newChaptersAvailable': '新增 {n} 段內容',
    'reader.conversionDoneWithNew': '轉換完畢 · 新增 {n} 段',
    'reader.conversionDone': '轉換完畢',
    'reader.refreshToView': '重新整理檢視',
    'common.dismiss': '關閉',
    'library.searchResults': '個結果',
    'library.continueReading': '繼續閱讀',
    'library.continue': '繼續',
    'library.showAll': '顯示全部',
    'library.back': '返回',
    'library.bookCount': '{count} 本書',
    'library.select': '選擇',
    'library.cancel': '取消',
    'library.backToHome': '返回首頁',
    'library.scrollToTop': '回到頂部',
    'library.selectedCount': '已選擇 {count} 本',
    'library.selectAll': '選擇全部',
    'library.pinToTop': '置頂',
    'library.unpinFromTop': '取消置頂',
    'library.downloadToLocal': '下載到本地',
    'library.addToCloudShort': '新增到雲端書庫',
    'library.removeFromLocalShort': '移出本地書庫',
    'library.deleteShort': '刪除',
    'library.confirmRemoveFromLocal': '確定將這 {count} 本書移出本地書庫？書籍將從本裝置刪除。',
    'library.finishedReading': '已讀完',
    'library.currentlyReading': '正在閱讀',
    'library.finishedShort': '讀完',
    'library.wpmUnit': '字/分鐘',
    'library.localLibrary': '本地書庫',
    'library.filter': '篩選',
    'library.filterStorageSection': '儲存',
    'library.filterLanguageSection': '語言',
    'library.filterCloud': '雲端',
    'library.filterLocal': '本地',
    'library.filterCurated': '精選書',
    'library.curatedLibrary': '精選書庫',
    'library.curatedLibraryDesc': '經典好書，智能注釋已就緒',
    'library.curatedComingSoon': '即將上線——精選好書，語塊注釋即開即用。',
    'library.freeBadge': '免費',
    'library.freeThisMonth': '本月免費',
    'library.curatedEnglish': '英語書庫',
    'library.curatedFrench': '法語書庫',
    'library.addToLibrary': '加入書庫',
    'library.addToMyLibrary': '加入我的書庫',
    'library.adding': '添加中...',
    'library.chapterUnit': '章',
    'library.cloudLibrary': '雲端書庫',
    'library.cloudUsage': '已用 {percent}%',
    'library.cloudUpgrade': '雲端書庫為付費使用者專屬功能。升級即可解鎖雲端儲存空間。',
    'library.upgradeLink': '查看方案',
    'library.last7Days': '最近7天',
    'library.thisYear': '今年上傳',
    'library.yearUploaded': '{year}年上傳',
    'library.yearFinishedCount': '{year}年 （{count}）',
    'library.otherFinishedCount': '其他 （{count}）',
    'library.markFinished': '標記讀完',
    'library.markFinishedShort': '讀完',
    'library.markFinishedTip': '正文內容已結束。點此標記為已讀完。',
    'library.confirmMarkFinished': '確定將這本書標記為已讀完嗎？',
    'reader.bookFinishedEyebrow': '讀完了',
    'reader.bookJourneyRevisitEyebrow': '重溫這段旅途',
    'reader.bookJourneyBackToReading': '返回閱讀',
    'reader.bookJourneySentinel': '✓ 已讀完 · 翻看旅途',
    'reader.bookJourneyButton': '閱讀旅程',
    'reader.bookJourneySelectAll': '全選',
    'reader.bookJourneyDeselectAll': '取消全選',
    'reader.bookJourneyWordsLabel': '查過的詞',
    'reader.bookJourneyNotesLabel': '筆記',
    'reader.bookJourneyShowAll': '查看全部',
    'reader.bookJourneyAddToLoom': '加入 Loom',
    'reader.bookJourneyLoomAdded': '已加入 {added}',
    'reader.bookJourneyLoomLimited': '{n} 個超出免費上限',
    'reader.bookFinishedOrdinal': '這是你在 EaseVerse 讀完的第 {n} 本書',
    'reader.bookFinishedShareGroup': '分享到',
    'reader.bookFinishedShareWechat': '微信',
    'reader.bookFinishedShareWeibo': '微博',
    'reader.bookFinishedShareXhs': '小紅書',
    'reader.bookFinishedShareX': 'X',
    'reader.bookFinishedShareIg': 'Instagram',
    'reader.bookFinishedShareFb': 'Facebook',
    'reader.bookFinishedShareLine': 'LINE',
    'reader.bookFinishedShareTg': 'Telegram',
    'reader.bookFinishedShareCopy': '複製連結',
    'reader.bookFinishedShareText': '我在 EaseVerse 讀完了《{title}》✨ 這是我的第 {n} 本 📚 {url}',
    'reader.bookFinishedShareWechatReady': '文案已複製，貼到微信分享',
    'reader.bookFinishedShareLinkCopied': '連結已複製',
    'reader.bookFinishedShareXhsReady': '圖片已儲存，文案已複製，去小紅書發布吧',
    'reader.bookFinishedShareIgReady': '圖片已儲存，文案已複製，去 Instagram 發布吧',
    'reader.bookFinishedShareFailed': '分享失敗，請再試一次',
    'reader.bookFinishedLabelTotalTime': '累計閱讀',
    'reader.bookFinishedLabelBookLength': '全書字數',
    'reader.bookFinishedLabelHighlights': '重點',
    'reader.bookFinishedLabelNotes': '筆記',
    'reader.bookFinishedLabelBookmarks': '書籤',
    'reader.bookFinishedJourneyLabel': '你的閱讀旅程',
    'reader.bookFinishedJourneyPrev': '更早的日子',
    'reader.bookFinishedJourneyNext': '後面的日子',
    'reader.bookFinishedClose': '返回書庫',
    'library.editFinishedDate': '修改日期',
    'library.completedBooksLine1': '閱讀里程碑',
    'library.completedBooksLine2': '尚未解鎖',
    'library.completedBooksLine3': '從第一本開始',
    'library.readingEmptyLine1': '開始閱讀',
    'library.readingEmptyLine2': '選一本書，從這裡開始',
    'library.readingEmptyLine3': '隨時翻開，隨時放下',

    // Site metadata
    'meta.siteTitle': 'EaseVerse Reader ｜ 多語深度閱讀平台',

    // Auth
    'auth.title': 'EaseVerse Reader',
    'auth.subtitle': '你的私人閱讀空間',
    'auth.checking': '正在檢查登入狀態...',
    'auth.emailLabel': '信箱',
    'auth.emailPlaceholder': 'your@email.com',
    'auth.sendCode': '發送驗證碼',
    'auth.sendingCode': '發送中...',
    'auth.codeSent': '驗證碼已發送到你的信箱',
    'auth.enterCode': '輸入驗證碼',
    'auth.codeSentTo': '我們已向以下信箱發送了 6 位驗證碼：',
    'auth.verify': '驗證並登入',
    'auth.verifying': '驗證中...',
    'auth.resend': '重新發送',
    'auth.resendIn': '{seconds}秒後重新發送',
    'auth.diffEmail': '使用其他信箱',
    'auth.codeIncomplete': '請輸入完整的 6 位驗證碼',
    'auth.newCodeSent': '新驗證碼已發送',
    'auth.helpLink': '遇到問題？聯繫我們',
    'auth.extLogin': '使用 EaseVerse Lens 帳號登入',
    'auth.extLoggingIn': '登入中...',
    'auth.useEmail': '使用信箱登入',
    'auth.backToExt': '返回 EaseVerse Lens 登入',
    'auth.or': '或',
    'auth.googleError': 'Google 登入失敗',
    'auth.agreeTerms': '我已閱讀並同意',
    'auth.termsLink': '服務條款',
    'auth.privacyLink': '隱私政策',
    'auth.subscribeNewsletter': '訂閱產品更新和閱讀技巧郵件（每月 1–2 封，隨時可退訂）',
    'auth.darkMode': '深色模式',
    'auth.lightMode': '淺色模式',
    'auth.products': 'EaseVerse 產品',
    'auth.lensDesc': '語境詞典',
    'auth.loomDesc': '知識庫',
    'auth.readerDesc': '閱讀平台',
    'auth.switchTitle': '切換帳號？',
    'auth.switchDesc': '你的 EaseVerse Lens 擴充功能目前登入的是 {email}，與 Reader 的帳號不同。是否切換到該帳號？',
    'auth.switchConfirm': '切換',
    'auth.switchDismiss': '保持當前',

    // Sidebar
    'sidebar.contents': '目錄',
    'sidebar.coverage.read': '已讀',
    'sidebar.coverage.partial': '讀了一部分',
    'sidebar.coverage.unread': '未讀',
    'sidebar.noToc': '沒有目錄',
    'sidebar.highlightsNotes': '標註與筆記',
    'sidebar.bookmarks': '書籤',
    'sidebar.all': '全部',
    'sidebar.highlights': '標註',
    'sidebar.notes': '筆記',
    'sidebar.emptyAll': '還沒有標註或筆記。閱讀時選擇文字即可標註或新增筆記。',
    'sidebar.emptyHighlights': '還沒有標註。閱讀時選擇文字即可標註。',
    'sidebar.emptyNotes': '還沒有筆記。閱讀時選擇文字並新增筆記。',
    'sidebar.emptyBookmarks': '還沒有書籤。點擊「新增」收藏目前位置。',
    'sidebar.add': '新增',
    'sidebar.save': '儲存',
    'sidebar.cancel': '取消',
    'sidebar.removeHighlight': '移除標註（保留筆記）',
    'sidebar.unknownChapter': '未知章節',
    'sidebar.bookmark': '書籤',
    'sidebar.page': '第 {page} 頁',

    'reader.settingsCurrentBookHint': '僅當前書',
    'reader.settingsAllBooksHint': '所有書',
    'reader.viewMode': '閱讀模式',
    'reader.scroll': '捲動',
    'reader.chapter': '章節',
    'reader.fontSize': '字型大小',
    'reader.pageWidth': '頁寬',
    'reader.pageMargin': '頁邊距',
    'reader.pageMarginNarrow': '窄',
    'reader.pageMarginStandard': '標準',
    'reader.pageMarginWide': '寬',
    'reader.lineHeight': '行高',
    'reader.paragraph': '段距',
    'reader.letter': '字距',
    'reader.setLanguage': '請選擇本書的語言',
    'reader.languageConfirm': '確定',
    'reader.changeLanguage': '更改語言',
    'notification.dismiss': '關閉',
    'reader.languageMismatch': '此書語言可能為：',
    'reader.bookLanguageLabel': '書籍語言',
    'reader.font': '字型',
    'reader.theme': '主題',
    'reader.progressBar': '進度條',
    'reader.always': '始終',
    'reader.hover': '懸停',
    'reader.hidden': '隱藏',
    'reader.einkMode': '墨水屏模式',
    'reader.einkAuto': '自動',
    'reader.einkOn': '開啟',
    'reader.einkOff': '關閉',
    'reader.pagedMode': '翻頁模式',
    'reader.tapToSelect': '點按選詞',
    'reader.autoPlayPronunciation': '自動播放發音',
    'reader.autoPlayPronunciationHint': '僅當前書',
    'settings.languageAndLearning': '語言與學習',
    'settings.interaction': '互動',
    'settings.pronunciation': '讀音偏好',
    'settings.accent': '口音',
    'settings.voice': '聲音',
    'settings.accentUS': '美音',
    'settings.accentGB': '英音',
    'settings.voiceMale': '男聲',
    'settings.voiceFemale': '女聲',
    'settings.proOnly': '僅 Pro 可用',
    'language.name.zh': '中文',
    'language.name.en': '英語',
    'language.name.ja': '日語',
    'language.name.ko': '韓語',
    'language.name.fr': '法語',
    'language.name.de': '德語',
    'language.name.es': '西班牙語',
    'language.name.pt': '葡萄牙語',
    'language.name.ru': '俄語',
    'language.name.it': '義大利語',
    'language.name.vi': '越南語',
    'language.name.other': '其他',
    'reader.chapterLabel': '本章',
    'reader.bookLabel': '全書',
    'reader.prevChapter': '上一章',
    'reader.nextChapter': '下一章',
    'reader.prevPage': '上一頁',
    'reader.nextPage': '下一頁',
    'reader.goBack': '返回',
    'reader.backToPosition': '返回原位置',
    'reader.sessionSummary': '本次閱讀 {time}',
    'einkSel.highlight': '螢光標記',
    'einkSel.highlighted': '已標記',
    'einkSel.note': '筆記',
    'einkSel.discard': '刪除標記',
    'einkSel.gloss': '釋義',
    'einkSel.explain': '深入解釋',
    'einkSel.notePlaceholder': '寫點筆記…',
    'einkSel.noteSave': '儲存',
    'einkSel.vocab': '生詞本',
    'einkSel.vocabSaved': '已加入',
    'einkSel.sentence': '整句',
    'reader.sessionSummaryWithWords': '本次閱讀 {time} · {words} 字',
    'reader.sessionSummaryCoverage': '本次閱讀 {time} · 新讀 {coverage} 字',
    'reader.sessionSummaryReread': '本次閱讀 {time} · 重溫 {reread} 字',
    'reader.sessionSummaryThreeBucket': '本次閱讀 {time} · 新讀 {coverage} 字 · 重溫 {reread} 字',
    'reader.formatTemporarilyUnavailable': '{format} 格式正在重新打磨，暫不可用。你的原始檔案和閱讀進度已安全保存，恢復後可直接開啟。',
    'reader.pdfConverting': 'PDF 正在轉換為可閱讀格式，通常需要 1-2 分鐘。完成後頁面會自動刷新。',
    'reader.pdfConvertingElapsed': '已等待 {elapsed}',
    'reader.pdfPreparing.title': '正在準備你的 PDF',
    'reader.pdfPreparing.stage1': '解析 PDF 文本',
    'reader.pdfPreparing.stage2': '正在整理為可讀格式',
    'reader.pdfPreparing.stage3': '生成電子書',
    'reader.pdfPreparing.stage4': '儲存到雲端',
    'reader.pdfPreparing.elapsed': '已用時 {elapsed}',
    'reader.pdfPreparing.eta': '約還需 {eta}',
    'reader.pdfPreparing.activeChapter': '正在處理:',
    'reader.pdfPreparing.modelSlow': '慢速模型',
    'reader.pdfPreparing.modelPassthrough': '直通',
    'reader.dualViewModeLabel': '閱讀模式',
    'reader.dualViewText': '文字',
    'reader.dualViewDual': '對照',
    'reader.dualViewOriginal': '原版',
    'reader.dualViewNeedsUpgrade': '此書需要升級才能使用對照模式',
    'reader.dualViewNeedsWiderScreen': '對照模式需要較寬的螢幕',
    'reader.reanchor': '將兩側對齊到當前段落',
    'reader.dualViewUpgradeTooltip': '升級此書以啟用對照模式',
    'reader.dualViewUpgradeConfirm': '升級會重新處理這本書，約 1-2 分鐘。期間無法閱讀。繼續？',
    'reader.dualViewUpgradeFailed': '升級失敗，請稍後再試。',
    'reader.qualityMediumToast': '本書部分文字層提取可能不完整，已為你預設對照模式以便核對原版。',
    'reader.qualityLowToast': '這本書像是掃描版或低品質 PDF，已為你預設原版模式。',
    'reader.qualityLowTooltip': '本書的文字提取不可靠，點擊查看詳情。',
    'reader.qualityLowAria': '文字品質提示',
    'reader.qualityLowPopoverTitle': '文字層不完整',
    'reader.qualityLowPopoverBody': '本書無法從來源 PDF 中可靠地提取文字。已為你預設原版模式以保留原貌。',
    'reader.qualityDontShowAgain': '本書不再提示',
    'reader.openAsOriginal': '以原版 PDF 開啟',
    'reader.openAsOriginalHint': '直接查看來源 PDF。此兜底模式下，劃線、筆記與 Lens 不可用。',
    'reader.figurePeekFigureOne': '1 張圖',
    'reader.figurePeekFigureMany': '{n} 張圖',
    'reader.figurePeekTableOne': '1 張表',
    'reader.figurePeekTableMany': '{n} 張表',
    'reader.figurePeekEquationOne': '1 個公式',
    'reader.figurePeekEquationMany': '{n} 個公式',
    'reader.peekDialogLabel': '原版 PDF 頁面預覽',
    'reader.peekLoading': '正在載入 PDF 頁…',
    'reader.peekClose': '關閉預覽',
    'reader.pdfConversionFailed': '這份 PDF 轉換失敗。原檔案已保留；你可以刪除後重新上傳，或聯繫我們。',
    'reader.minutesShort': '{n} 分鐘',
    'reader.hoursShort': '{n} 小時',
    'reader.hoursMinutesShort': '{h} 小時 {m} 分鐘',
    'reader.theme.white': '白色',
    'reader.theme.slate': '石板灰',
    'reader.theme.sage': '護眼綠',
    'reader.theme.parchment': '羊皮紙',
    'reader.theme.midnight': '午夜',
    'reader.loginRequired': '請先登入再閱讀本書',
    'reader.loadFailed': '載入書籍失敗',
    'reader.bookNotFound': '未找到該書籍',
    'reader.deepReading.title': '深讀',
    'reader.deepReading.close': '關閉深讀面板',
    'reader.deepReading.toggle': '深讀面板',
    'reader.deepReading.bubbles': '查詞圖譜',
    'lens.pronounceLocked': '升級 Pro 即可朗讀該語言',
    'reader.deepReading.viewHistory': '歷史',
    'reader.deepReading.viewFrequency': '詞頻',
    'reader.deepReading.playNormal': '朗讀',
    'reader.deepReading.playSlow': '慢速朗讀 (0.7×)',
    'reader.deepReading.notes': '筆記',
    'reader.deepReading.chat': '對話',
    'reader.deepReading.tabLookups': '查詞',
    'reader.deepReading.tabNotes': '筆記',
    'reader.deepReading.done': '完成',
    'reader.deepReading.comingSoon': '即將上線',
    'reader.deepReading.signInToSee': '登入後檢視你的查詞圖譜',
    'reader.deepReading.loading': '載入中…',
    'reader.deepReading.loadError': '載入失敗',
    'reader.deepReading.noLookups': '本書還沒有查詞紀錄',
    'reader.deepReading.contexts': '上下文',
    'reader.deepReading.back': '← 返回圖譜',
    'reader.deepReading.noNotes': '還沒有筆記或標註',
    'reader.deepReading.notePlaceholder': '寫下你的筆記…',
    'reader.deepReading.addNote': '+ 新增筆記',
    'reader.deepReading.jumpToPassage': '跳到原文',
    'reader.deepReading.toLoom': '→ 加入 Loom',
    'reader.deepReading.inLoom': '✓ 已在 Loom',
    'reader.deepReading.loomError': '加入 Loom 失敗',
    'reader.deepReading.collectToLoom': '收藏到 Loom',
    'reader.deepReading.deleteEntry': '從列表移除',
    'reader.deepReading.reply': '↩ 回覆',
    'reader.deepReading.replyOpen': '回覆這則筆記',
    'reader.deepReading.replyPlaceholder': '寫下你的回覆…',
    'reader.deepReading.repliesShow': '展開 {n} 則回覆',
    'reader.deepReading.repliesHide': '收起回覆',
    'reader.deepReading.editCount': '已編輯 {n} 次',
    'reader.deepReading.editHistoryOpen': '查看編輯紀錄',
    'reader.deepReading.deleteNote': '刪除',
    'reader.deepReading.timeJustNow': '剛剛',
    'reader.deepReading.timeMinAgo': '{n} 分鐘前',
    'reader.deepReading.timeHourAgo': '{n} 小時前',
    'reader.deepReading.timeDayAgo': '{n} 天前',
    'reader.deepReading.editHistoryTitle': '編輯紀錄',
    'reader.deepReading.editHistoryCurrent': '目前版本',
    'reader.deepReading.editHistoryNone': '尚無歷史版本',
    'reader.deepReading.editHistoryError': '載入紀錄失敗',
    'reader.followLink': '前往連結',

    'note.placeholder': '寫筆記...',
    'note.addNotePlaceholder': '新增筆記...',
    'note.editNote': '編輯筆記',
    'note.addNote': '新增筆記',
    'note.deleteNote': '刪除筆記',
    'note.deleteHighlight': '刪除標註',
    'note.save': '儲存',
    'note.copy': '複製',
    'note.cancel': '取消',
    'note.close': '關閉',
    'note.unpin': '取消固定',
    'note.highlightedText': '標註文字',
    'note.highlightColor': '標註 {color}',
    'note.underlineColor': '底線 {color}',
    'note.saving': '儲存中...',
    'note.saved': '已儲存',
    'note.error': '錯誤',

    // Loading
    'loading.default': '載入中...',
    'loading.backToLibrary': '返回書庫',

    // Stats Card
    'stats.lessThan1min': '不到1分鐘',
    'stats.hour': '小時',
    'stats.minute': '分鐘',
    'stats.goalLabel': '本月閱讀目標：每週{days}天，每天{time}',
    'stats.goalLabelDaily': '本月閱讀目標：每天{time}',
    'stats.noGoal': '未設置閱讀目標',
    'stats.streakDays': '連續閱讀{n}天',
    'stats.streakLabel': '連續',
    'stats.thisMonth': '本月',
    'stats.todayLabel': '今日',
    'stats.thisWeek': '本週閱讀',
    'stats.reading': '月閱讀',
    'stats.yearGoalLabel': '年目標',
    'stats.yearlyReadingGoal': '{year}年閱讀目標',
    'stats.wan': '萬',
    'stats.wordsUnit': '字',
    'stats.mon': '一', 'stats.tue': '二', 'stats.wed': '三', 'stats.thu': '四',
    'stats.fri': '五', 'stats.sat': '六', 'stats.sun': '日',
    'stats.month1': '1', 'stats.month2': '2', 'stats.month3': '3',
    'stats.month4': '4', 'stats.month5': '5', 'stats.month6': '6',
    'stats.month7': '7', 'stats.month8': '8', 'stats.month9': '9',
    'stats.month10': '10', 'stats.month11': '11', 'stats.month12': '12',
    'stats.setGoalTitle': '設置本月閱讀目標',
    'stats.weeklyDays': '每週閱讀天數',
    'stats.dailyMinutes': '每天閱讀時長',
    'stats.yearlyWords': '年度字數目標',
    'stats.unitWan': '萬字',
    'stats.save': '儲存',
    'stats.confirmSave': '確認修改',
    'stats.goalConfirmMsg': '儲存後本月還可修改{n}次，確定要修改嗎？',
    'stats.goalEditExhausted': '本月修改次數已用完，下個月才能再次修改。',
    'stats.goalRemaining': '本月剩餘修改次數：{n}次',
    'stats.cancel': '取消',
    'stats.minuteShort': '分鐘',
    'stats.custom': '自定義',
    'stats.wordsCount': '≈{n}字',
    'stats.wordsEstimateHint': '基於閱讀位置變化的估算值',
    'stats.noReading': '未閱讀',
    'stats.streakHint': '閱讀≥5分鐘即計入連續天數',
    'stats.dailyThreshold': '每天{time}',
    'stats.remaining': '還需{time}',
    // Dashboard
    'dashboard.title': '閱讀分析',
    'dashboard.titleLang': '{lang}文本閱讀分析',
    'dashboard.backToLibrary': '書庫',
    'dashboard.langDistribution': '語言分佈',
    'dashboard.langMonth': '月',
    'dashboard.langYear': '年',
    'dashboard.langAll': '全部',
    'dashboard.langSingleHint': '當你閱讀其他語言的書籍時，可在此按語言篩選',
    'dashboard.langFold': '收起',
    'dashboard.langName.zh': '中文',
    'dashboard.langName.en': '英語',
    'dashboard.langName.ja': '日語',
    'dashboard.langName.ko': '韓語',
    'dashboard.langName.fr': '法語',
    'dashboard.langName.de': '德語',
    'dashboard.langName.es': '西班牙語',
    'dashboard.langName.pt': '葡萄牙語',
    'dashboard.langName.ru': '俄語',
    'dashboard.langName.it': '義大利語',
    'dashboard.langName.nl': '荷蘭語',
    'dashboard.langName.ar': '阿拉伯語',
    'dashboard.langName.th': '泰語',
    'dashboard.langName.vi': '越南語',
    'dashboard.todaySummary': '今日',
    'dashboard.weekSummary': '本週',
    'dashboard.readingTime': '閱讀時長',
    'dashboard.wordsRead': '閱讀字數',
    'dashboard.totalReadingDays': '累計閱讀',
    'dashboard.dayUnit': '天',
    'dashboard.achievements': '閱讀成就',
    'dashboard.currentStreak': '連續閱讀{count}天',
    'dashboard.showAllBadges': '查看全部徽章...',
    'dashboard.cat_member': '付費會員',
    'dashboard.memberBadgeLabel': 'Pro會員',
    'dashboard.cat_days': '累計閱讀',
    'dashboard.cat_streak': '連續閱讀',
    'dashboard.cat_words': '閱讀字數',
    'dashboard.cat_time': '閱讀時長',
    'dashboard.cat_books': '讀完書籍',
    'dashboard.nextMilestone': '下一個',
    'dashboard.memberRewardLine1': '累計維持任一付費檔（Basic / Pro / Max）365 天',
    'dashboard.memberRewardLine2': '即可解鎖一個月 Pro 會員獎勵',
    'dashboard.memberGiftUnlocked': '恭喜！你已經陪伴EaseVerse滿365天啦，你的禮物已送達~🎁',
    'dashboard.memberActiveTooltip': '這是你成為EaseVerse會員的第{days}天，等到第365天，我有一份禮物要送給你哦~😊',
    'dashboard.memberPausedTooltip': '你已累計成為EaseVerse會員{days}天，續費後繼續累計哦～',
    'dashboard.memberNotPaidTooltip': '哎呀，你還沒有成為EaseVerse會員呢，現在就去個人中心解鎖會員身份吧～',
    'dashboard.badgeUnlocked': '恭喜你獲得「{0}」徽章\n繼續努力，解鎖「{1}」',
    'dashboard.badgeMaxLevel': '恭喜你獲得「{0}」徽章\n你已達到最高等級！',
    'dashboard.badgeLocked': '現在就開始閱讀\n解鎖「{0}」成就',
    'dashboard.badgeCountSuffix': '人已獲得',
    'dashboard.badgeCongratsTooltip': '恭喜 🎉 你已解鎖此勳章',
    'dashboard.badgeCountText': '已有 {n} 位同學解鎖',
    'dashboard.badgeExpandMore': '展開更多',
    'dashboard.daysSuffix': '天',
    'dashboard.booksSuffix': '本',
    'dashboard.viewAllBadges': '查看全部徽章',
    'dashboard.statToday': '今日閱讀',
    'dashboard.statWords': '閱讀字數',
    'dashboard.statTotalWords': '累計閱讀字數',
    'dashboard.statRereadWords': '另含重溫 {n} 字',
    'dashboard.wordsPopoverTitle': '顯示語言',
    'dashboard.wordsPopoverSaved': '已儲存',
    'dashboard.statWeek': '本週閱讀',
    'dashboard.statTotalDays': '累計閱讀',
    'dashboard.totalDaysTooltip': '{date}，你正式開啟了在EaseVerse的原著閱讀之旅，至今已經{days}天啦✈️',
    'dashboard.dailyTrend': '每日趨勢',
    'dashboard.monthlyTotal': '本月閱讀{time}',
    'dashboard.dailyAvg': '日均閱讀{time}',
    'dashboard.yearMonth': '{year}年{month}月',
    'dashboard.hourlyInsight': '偏好{period}閱讀',
    'dashboard.hourlyInsightRange': '偏好{from}至{to}閱讀',
    'dashboard.hourlyWords': '每分鐘閱讀字數',
    'dashboard.wpmHint': '每分鐘閱讀字數',
    'dashboard.wpmSubtitle': '本月WPM {wpm}字/分鐘',
    'dashboard.wpmSubtitleUp': '本月WPM {wpm}字/分鐘，相較上個月提升{pct}%',
    'dashboard.wpmSubtitleDown': '本月WPM {wpm}字/分鐘，相較上個月下降{pct}%',
    'dashboard.wpmInsight': '{period}每分鐘閱讀字數最多',
    'dashboard.wpmInsightRange': '{from}至{to}每分鐘閱讀字數最多',
    'dashboard.hourlyTime': '每時閱讀時長',
    'dashboard.wordsUnit': '字',
    'dashboard.period.lateNight': '深夜',
    'dashboard.period.dawn': '黎明',
    'dashboard.period.earlyMorning': '清晨',
    'dashboard.period.lateMorning': '上午',
    'dashboard.period.earlyAfternoon': '午後',
    'dashboard.period.lateAfternoon': '傍晚',
    'dashboard.period.evening': '晚間',
    'dashboard.period.nightfall': '入夜',
    'dashboard.bookBreakdown': '書籍',
    'dashboard.noData': '暫無閱讀數據',
    'dashboard.editGoal': '制定目標',
    'dashboard.viewDetails': '查看詳情',
    'dashboard.yearlyTime': '年閱讀時長',
    'dashboard.yearlyWpm': '年閱讀效率',
    'dashboard.monthUnit': '月',
    'dashboard.wpmExplainPlaceholder': 'WPM（每分鐘閱讀字數）衡量你的閱讀速度——詳細說明即將上線。',
    // Milestones
    'milestone.streak_3d': '連續閱讀3天！',
    'milestone.streak_7d': '連續閱讀7天！',
    'milestone.streak_14d': '連續閱讀14天！',
    'milestone.streak_21d': '連續閱讀21天！',
    'milestone.streak_30d': '連續閱讀30天！',
    'milestone.streak_60d': '連續閱讀60天！',
    'milestone.streak_100d': '連續閱讀100天！',
    'milestone.streak_180d': '連續閱讀180天！',
    'milestone.streak_365d': '連續閱讀365天！',
    'milestone.words_10k': '累計閱讀1萬字！',
    'milestone.words_50k': '累計閱讀5萬字！',
    'milestone.words_100k': '累計閱讀10萬字！',
    'milestone.words_500k': '累計閱讀50萬字！',
    'milestone.words_1m': '累計閱讀100萬字！',
    'milestone.words_2m': '累計閱讀200萬字！',
    'milestone.words_3m': '累計閱讀300萬字！',
    'milestone.words_4m': '累計閱讀400萬字！',
    'milestone.words_5m': '累計閱讀500萬字！',
    'milestone.time_10h': '累計閱讀10小時！',
    'milestone.time_50h': '累計閱讀50小時！',
    'milestone.time_100h': '累計閱讀100小時！',
    'milestone.time_300h': '累計閱讀300小時！',
    'milestone.time_500h': '累計閱讀500小時！',
    'milestone.time_1000h': '累計閱讀1000小時！',
    'milestone.time_2000h': '累計閱讀2000小時！',
    'milestone.weekly_goal_first': '首次達成週目標！',
    'milestone.weekly_goal_4': '連續4週達成目標！',
    'milestone.yearly_25': '年度目標完成25%！',
    'milestone.yearly_50': '年度目標已過半！',
    'milestone.yearly_75': '年度目標完成75%！',
    'milestone.yearly_100': '年度目標達成！',
    'milestone.books_1': '讀完第一本書！',
    'milestone.books_3': '讀完3本書！',
    'milestone.books_5': '讀完5本書！',
    'milestone.books_10': '讀完10本書！',
    'milestone.books_25': '讀完25本書！',
    'milestone.books_50': '讀完50本書！',
    'milestone.books_75': '讀完75本書！',
    'milestone.books_100': '讀完100本書！',
    'milestone.days_7': '累計閱讀7天！',
    'milestone.days_30': '累計閱讀30天！',
    'milestone.days_100': '累計閱讀100天！',
    'milestone.days_200': '累計閱讀200天！',
    'milestone.days_365': '累計閱讀365天！',
    'milestone.member_365d': '付費會員滿365天！',
    'milestone.lang_words': '{lang}閱讀達到{amount}字！',
    'milestone.lang_time': '{lang}閱讀達到{amount}！',

    // Chunks — 設定
    'chunks.title': '語塊標註',
    'chunks.showAnnotations': '顯示語塊標註',
    'chunks.filterRange': '顯示範圍',
    'chunks.levelAdaptive': '自動適配',
    'chunks.modeManual': '手動選擇',
    'chunks.levelShowAll': '顯示全部',
    'chunks.density': '密度',
    'chunks.densityFocused': '精選',
    'chunks.densityDetailed': '詳細',
    'chunks.adaptiveExplore': '正在學習你的偏好 ({count}/{total})',
    'chunks.adaptiveActive': '已根據 {count} 次互動自動調整',
    'chunks.manualLevel': '等級',
    'chunks.levelAll': '全部',
    'chunks.manualCategory': '類別',
    'chunks.cat_vocabulary': '詞彙',
    'chunks.cat_phrasal_verb': '動詞短語',
    'chunks.cat_collocation': '搭配',
    'chunks.cat_idiom': '習語',
    'chunks.cat_grammar': '語法',
    'chunks.cat_knowledge': '知識',
    'chunks.cat_tone': '語氣',
    'chunks.resetProfile': '重設偏好',
    'chunks.modeTranslate': '翻譯',
    'chunks.modeSynonym': '同義',
    'chunks.loading': '載入中...',
    'chunks.rehinting': '正在換個角度解讀…',
    'chunks.reportBadCache': '回報翻譯錯誤',
    'chunks.generating': '正在生成解釋...',
    'chunks.askPlaceholder': '追問',
    'lens.sentenceAnalysis': '句子分析',
    'lens.originalSentence': '原句',
    'lens.step1': '含義',
    'lens.step2': '結構',
    'lens.step3': '深入',
    'lens.analyzing': '分析中...',
    'lens.showGrammar': '顯示語法詳情',
    'lens.hideGrammar': '隱藏語法詳情',
    'lens.openChat': '追問',
    'lens.saveNote': '儲存筆記',
    'lens.noteSaved': '已儲存',
    'lens.saveNoteFailed': '儲存失敗',
    'lens.removeNote': '移除筆記',
    'lens.removeNoteFailed': '移除失敗',
    'lens.checkingNote': '檢查中…',
    'lens.collapse': '收起',
    'chunks.resetConfirm': '重置所有語塊學習數據？此操作無法撤銷。',
    'chunks.onboardingHint': '點擊圓點查看釋義',
    'chunks.statusLoading': '正在載入語塊標註…',
    'chunks.cacheRefreshed': '本書語塊已升級，點此重新整理查看',
    'chunks.statusProcessing': '正在分析詞彙…',
    'chunks.proRequired': '升級到付費方案解鎖語塊標註功能。',
    'chunks.bookUsage': "本月語塊：{used}/{limit} 本",
    'chunks.bookLimitReached': "本月 {limit} 本已用完，升級 Pro 解鎖無限",

    // 裝置限制彈窗
    'deviceLimit.titleConfirm': '裝置數量已達上限',
    'deviceLimit.titleSelect': '已達裝置上限',
    'deviceLimit.confirmDesc': '繼續登入將登出以下裝置：',
    'deviceLimit.selectDesc': '選擇要替換的裝置：',
    'deviceLimit.loggedIn': '登入',
    'deviceLimit.cancel': '取消',
    'deviceLimit.confirming': '登入中...',
    'deviceLimit.confirmLogin': '確認登入',
    'deviceLimit.confirmReplace': '確認替換並登入',
    'deviceLimit.unknownDevice': '未知裝置',

    // 登入失效彈窗
    'session.invalidTitle': '登入已失效',
    'session.invalidHint': '目前裝置已登出。',
    'session.reLogin': '重新登入',

    // 登出進度遮罩
    'signOut.title': '正在登出…',
    'signOut.message': '正在保存未同步的變更',
    'signOut.hint': '請稍候',
  },

  fr: {
    'reader.remotePosition.title': 'Reprendre sur votre autre appareil ?',
    'reader.remotePosition.body': 'Un autre appareil a atteint {percent} % de ce livre.',
    'reader.remotePosition.jump': 'Y aller',
    'reader.remotePosition.stay': 'Rester ici',
    'reader.furthestRead': 'Lu jusqu’ici',
    // Library
    'library.addBook': 'Ajouter un livre',
    'library.uploading': 'Téléversement...',
    'library.recentBooks': 'Livres récents',
    'library.emptyTitle': 'Votre bibliothèque est vide',
    'library.emptyHint': 'Cliquez sur « Ajouter un livre » ou glissez-déposez des fichiers',
    'library.loading': 'Chargement de votre bibliothèque...',
    'library.dropHere': 'Déposez les livres ici',
    'library.dropFormats': 'epub, pdf, txt, mobi',
    'library.signOut': 'Se déconnecter',
    'library.account': 'Centre du compte',
    'stuckPdf.title': '{count} PDF à re-téléverser',
    'stuckPdf.explanation': 'Ces PDF ont été téléversés avant la mise en service de la conversion PDF→EPUB, ils ne peuvent pas être ouverts. Supprimez-les et re-téléversez pour activer le nettoyage par IA et la lecture en double vue.',
    'stuckPdf.deleteBook': 'Supprimer',
    'sidebar.myBookshelf': 'Ma Bibliothèque',
    'sidebar.allBooks': 'Tous les livres',
    'sidebar.reading': 'En lecture',
    'sidebar.finished': 'Terminés',
    'sidebar.curatedLibrary': 'Bibliothèque Sélectionnée',
    'sidebar.readingAnalytics': 'Analyses de Lecture',
    'sidebar.userCenter': 'Centre Utilisateur',
    'sidebar.collapse': 'Réduire la barre latérale',
    'sidebar.expand': 'Développer la barre latérale',
    'sidebar.darkMode': 'Mode Sombre',
    'sidebar.lightMode': 'Mode Clair',
    'sidebar.search': 'Rechercher',
    'sidebar.searchPlaceholder': 'Rechercher des livres...',
    'sidebar.home': 'Accueil',
    'sidebar.settings': 'Paramètres',
    'landing.continueReading': 'Continuer la lecture',
    'landing.quote': 'Citation du jour',
    'landing.like': 'Aimer',
    'landing.liked': 'Aimé',
    'landing.nextQuote': 'Suivante',
    'landing.todayRead': "Lu aujourd'hui",
    'landing.todayNotStarted': "Pas encore aujourd'hui",
    'landing.weekRead': 'Cette semaine',
    'landing.monthRead': 'Ce mois-ci',
    'landing.weekReading': 'Cette semaine',
    'landing.lastRead': 'Dernière lecture',
    'library.deleteBook': 'Supprimer le livre',
    'library.uploadFailed': 'Échec du téléversement',
    'library.drmProtected': 'Ce fichier est protégé par DRM et ne peut pas être ouvert.',
    'library.huffNotSupported': 'Ce fichier MOBI utilise la compression HUFF/CDIC non prise en charge. Veuillez le ré-enregistrer avec une compression standard.',
    'library.parseFailed': 'Impossible de lire ce fichier — il est peut-être corrompu ou dans une variante non prise en charge.',
    'library.loadFailed': 'Échec du chargement des livres',
    'library.deleteFailed': 'Échec de la suppression',
    'library.invalidFormat': 'Veuillez téléverser des fichiers epub, mobi ou pdf',
    'library.pdfTemporarilyDisabled': "Le téléversement de PDF est temporairement désactivé pendant que nous refaisons le lecteur. EPUB et MOBI fonctionnent bien aujourd'hui.",
    'library.pdf.confirm.title': 'Convertir ce PDF ?',
    'library.pdf.confirm.summary': 'Cette conversion utilisera {pages} pages de quota.',
    'library.pdf.confirm.colCurrent': 'Actuel',
    'library.pdf.confirm.colAfter': 'Après',
    'library.pdf.confirm.rowMonthly': 'Mensuel',
    'library.pdf.confirm.rowDaily': 'Aujourd’hui',
    'library.pdf.confirm.confirm': 'Convertir',
    'library.pdf.confirm.cancel': 'Annuler',
    'library.pdf.unit.pages': '{n} pages',
    'library.pdf.error.perBookCapTitle': 'PDF trop volumineux pour votre offre',
    'library.pdf.error.perBookCapBody': 'Ce PDF dépasse la limite par livre de votre offre.',
    'library.pdf.error.perBookCapLegend': '{pages} pages — au-delà de la limite de {cap}',
    'library.pdf.error.quotaExceededTitle': 'Quota insuffisant',
    'library.pdf.error.quotaExceededBody': 'Cette conversion nécessite {pages} pages de quota.',
    'library.pdf.error.needs': 'requiert {pages}',
    'library.pdf.error.unreadableTitle': 'Impossible de lire ce PDF',
    'library.pdf.error.unreadableBody': 'Ce fichier semble illisible. Causes courantes :',
    'library.pdf.error.unreadableCause1': 'Le fichier est corrompu ou incomplet',
    'library.pdf.error.unreadableCause2': 'PDF protégé par mot de passe ou chiffré',
    'library.pdf.error.unreadableCause3': 'Le fichier n’est pas réellement un PDF (mauvaise extension)',
    'library.pdf.error.dismiss': 'OK',
    'library.pdf.upgrade.title': 'Le PDF est une fonctionnalité Pro',
    'library.pdf.upgrade.body': 'La conversion PDF est disponible sur Pro et Max. Passez à une offre supérieure pour :',
    'library.pdf.upgrade.benefit1': 'Jusqu’à 400 pages par livre',
    'library.pdf.upgrade.benefit2': '600 pages par mois',
    'library.pdf.upgrade.benefit3': 'Synchronisation multi-appareils',
    'library.pdf.upgrade.cta': 'Voir les offres',
    'library.freeLimitReached': 'Le forfait gratuit permet {limit} livres. Passez à un forfait payant pour des livres illimités.',
    'library.deleteBook': 'Supprimer le livre',
    'library.returnBook': 'Rendre le livre',
    'library.bookLimitExceeded': 'Vous avez atteint votre limite de livres. Rendez un livre de votre étagère pour faire de la place, ou passez à un forfait illimité.',
    'library.bookExists': '« {title} »{author} existe déjà.\n\nTéléverser comme nouvelle copie ?',
    'library.bookExistsByAuthor': ' de {author}',
    'library.cloudProOnly': 'La synchronisation cloud est réservée aux abonnés payants.',
    'library.cloudBackupTitle': 'Vos livres sont sauvegardés dans le cloud',
    'library.cloudBackupDesc': 'Votre abonnement sauvegarde vos livres de façon privée dans le cloud pour les lire sur n’importe quel appareil. Vous pouvez le désactiver à tout moment avec le bouton en haut à droite de « Tous les livres ».',
    'library.cloudBackupTurnOff': 'Désactiver',
    'library.cloudBackupGotIt': 'Compris',
    'library.cloudBackupToggle': 'Sauvegarde auto vers le cloud',
    'library.notAvailable': 'Ce livre a été importé sur un autre appareil. Veuillez le téléverser à nouveau.',
    'library.phantomDialogDesc': "Le fichier de ce livre n'existe pas sur cet appareil et aucune copie cloud n'est disponible. Vous pouvez importer un fichier comme nouveau livre, ou retirer cette entrée de votre étagère.",
    'library.phantomUploadNew': 'Importer comme nouveau livre',
    'library.phantomRemove': "Retirer de l'étagère",
    'library.cloudOnly': 'Cloud',
    'library.downloading': 'Téléchargement…',
    'library.downloadFailed': 'Échec du téléchargement. Vérifiez votre connexion et réessayez.',
    'library.offlineCloudBook': 'Vous êtes hors ligne. Ce livre est dans le cloud et nécessite une connexion internet.',
    'library.confirmDelete': 'Êtes-vous sûr de vouloir supprimer ce livre ?',
    'library.confirmRemoveCurated': 'Retirer ce livre de votre bibliothèque ? Vous pourrez le réajouter depuis la bibliothèque sélectionnée.',
    'library.deleteDialogDesc': 'Ce livre est sauvegardé dans le cloud. Que souhaitez-vous faire ?',
    'library.deleteLocalOnly': 'Libérer l\'espace local',
    'library.deleteCompletely': 'Supprimer définitivement',
    'library.confirmFreeLocalSpace': 'Supprimer le fichier local ? Ce livre est sauvegardé dans le cloud et peut être retéléchargé.',
    'library.confirmBulkFreeLocalSpace': 'Libérer l\'espace local pour {count} livres ? Les copies cloud ne sont pas affectées.',
    'library.freeLocalSpace': 'Libérer l\'espace local',
    'library.freeLocalSpaceShort': 'Libérer',
    'library.allBooks': 'Tous les livres',
    'library.converting': 'Conversion',
    'chapterSelection.batchTitle': 'File lente — moitié du quota',
    'chapterSelection.batchHint': 'Généralement terminé en quelques heures, au plus tard le lendemain. Le livre s\'ouvre depuis votre bibliothèque une fois prêt.',
    'chapterSelection.chargeSummary': 'Cette conversion utilisera {pages} pages de quota',
    'chapterSelection.cachedDiscount': '({pages} pages en cache partagé — demi-tarif)',
    'library.batchPendingBadge': 'En file · lente',
    'reader.pdfBatch.title': 'En file de conversion',
    'reader.pdfBatch.hint': 'Ce livre utilise la file lente (quota réduit de moitié). Généralement prêt en quelques heures, au plus tard le lendemain — il s\'ouvrira directement depuis votre bibliothèque.',
    'library.badgeExtracting': 'Extraction',
    'library.badgeConverting': 'Nettoyage',
    'library.badgeFinishing': 'Finalisation',
    'library.pendingPreview': 'Choisir des chapitres',
    'library.pendingPreviewBadge': 'À sélectionner',
    'library.uploadingBadge': 'Téléversement…',
    'library.scanOnlyBadge': 'OCR requis',
    'library.scanOnlyBadgeTitle': 'PDF scanné — appliquez un OCR avant de téléverser',
    'chapterSelection.title': 'Choisir les chapitres à convertir',
    'chapterSelection.unreadablePdf': "Impossible de lire ce PDF — le fichier est peut-être corrompu ou dans un format non standard. Réexportez-le (Aperçu → Exporter au format PDF, ou Imprimer → Enregistrer au format PDF), puis réimportez-le.",
    'chapterSelection.selectAll': 'Tout sélectionner',
    'chapterSelection.invertSelection': 'Inverser',
    'chapterSelection.selectedCount': '{n} ch · {pages} pages',
    'chapterSelection.includesFreePages': '({pages} pages gratuites)',
    'chapterSelection.alreadyPaid': 'Payé · gratuit',
    'chapterSelection.alreadyConverted': '✓ Converti',
    'library.addChapters': 'Ajouter des chapitres',
    'chapterSelection.quotaUsage': 'Utilise {used} / {cap} pages de votre quota mensuel',
    'chapterSelection.exceedsCap': 'Dépasse la limite par livre de {cap} pages',
    'chapterSelection.exceedsQuota': 'La sélection dépasse le quota restant — réduisez pour continuer',
    'chapterSelection.outlineSparse': 'Informations de chapitres incomplètes',
    'chapterSelection.outlineHeuristic': 'Aucun sommaire — chapitres détectés par mise en page',
    'chapterSelection.tocHarvested': 'Titres des chapitres alignés sur le sommaire du livre',
    'chapterSelection.tocHarvestedPartial': 'Certains titres sont alignés sur le sommaire du livre — les autres sont détectés automatiquement',
    'chapterSelection.fallbackSingle': 'Pas d\'info de chapitre — converti en bloc',
    'chapterSelection.loadingOutline': 'Analyse des chapitres…',
    'chapterSelection.fallbackChunks': 'Titres de chapitres indétectables — découpé par nombre de pages',
    'chapterSelection.pageRangeTitle': 'Pages {start}–{end}',
    'chapterSelection.scanOnlyTitle': '⚠️ PDF scanné — conversion impossible',
    'chapterSelection.scanOnlyBody': 'Ce PDF ne contient en moyenne que {chars} caractères par page, donc presque aucun texte extractible. Appliquez un OCR (Adobe Acrobat, ABBYY FineReader, Tesseract) avant de téléverser.',
    'chapterSelection.startConversion': 'Lancer la conversion',
    'chapterSelection.submitting': 'Envoi…',
    'reader.newChaptersAvailable': '+{n} section(s) converti(es)',
    'reader.conversionDoneWithNew': 'Conversion terminée · +{n} section(s)',
    'reader.conversionDone': 'Conversion terminée',
    'reader.refreshToView': 'Actualiser',
    'common.dismiss': 'Fermer',
    'library.searchResults': 'résultats',
    'library.continueReading': 'Continuer la lecture',
    'library.continue': 'Continuer',
    'library.showAll': 'Tout afficher',
    'library.back': 'Retour',
    'library.bookCount': '{count} livres',
    'library.select': 'Sélectionner',
    'library.cancel': 'Annuler',
    'library.backToHome': 'Retour à l\'accueil',
    'library.scrollToTop': 'Retour en haut',
    'library.selectedCount': '{count} sélectionné(s)',
    'library.selectAll': 'Tout sélectionner',
    'library.pinToTop': 'Épingler',
    'library.unpinFromTop': 'Désépingler',
    'library.downloadToLocal': 'Télécharger en local',
    'library.addToCloudShort': 'Ajouter au cloud',
    'library.removeFromLocalShort': 'Retirer du local',
    'library.deleteShort': 'Supprimer',
    'library.confirmRemoveFromLocal': 'Retirer {count} livre(s) de la bibliothèque locale ? Ils seront supprimés de cet appareil.',
    'library.finishedReading': 'Terminé',
    'library.currentlyReading': 'En cours de lecture',
    'library.finishedShort': 'Terminé',
    'library.wpmUnit': 'mots/min',
    'library.localLibrary': 'Local',
    'library.filter': 'Filtrer',
    'library.filterStorageSection': 'Stockage',
    'library.filterLanguageSection': 'Langue',
    'library.filterCloud': 'Cloud',
    'library.filterLocal': 'Local',
    'library.filterCurated': 'Sélection',
    'library.curatedLibrary': 'Bibliothèque curatée',
    'library.curatedLibraryDesc': 'Classiques du domaine public avec annotations pré-traitées',
    'library.curatedComingSoon': 'Bientôt disponible — livres sélectionnés avec annotations pré-traitées.',
    'library.freeBadge': 'Gratuit',
    'library.freeThisMonth': 'Gratuit ce mois-ci',
    'library.curatedEnglish': 'Bibliothèque anglaise',
    'library.curatedFrench': 'Bibliothèque française',
    'library.addToLibrary': 'Ajouter à la bibliothèque',
    'library.addToMyLibrary': 'Ajouter à ma bibliothèque',
    'library.adding': 'Ajout en cours...',
    'library.chapterUnit': 'ch.',
    'library.cloudLibrary': 'Cloud',
    'library.cloudUsage': '{percent}% utilisé',
    'library.cloudUpgrade': "La bibliothèque cloud est réservée aux abonnés payants. Passez à un forfait payant pour le stockage cloud.",
    'library.upgradeLink': 'Mettre à niveau',
    'library.last7Days': '7 derniers jours',
    'library.thisYear': 'Cette année',
    'library.yearUploaded': 'Téléversé en {year}',
    'library.yearFinishedCount': '{year} ({count})',
    'library.otherFinishedCount': 'Autres ({count})',
    'library.markFinished': 'Marquer comme terminé',
    'library.markFinishedShort': 'Terminé',
    'library.markFinishedTip': 'Le contenu principal est terminé. Appuyez ici pour marquer comme lu.',
    'library.confirmMarkFinished': 'Marquer ce livre comme terminé ?',
    'reader.bookFinishedEyebrow': 'Livre terminé',
    'reader.bookJourneyRevisitEyebrow': 'Vous revisitez ce parcours',
    'reader.bookJourneyBackToReading': 'Reprendre la lecture',
    'reader.bookJourneySentinel': '✓ Terminé · voir votre parcours',
    'reader.bookJourneyButton': 'Parcours de lecture',
    'reader.bookJourneySelectAll': 'Tout sélectionner',
    'reader.bookJourneyDeselectAll': 'Tout désélectionner',
    'reader.bookJourneyWordsLabel': 'Mots recherchés',
    'reader.bookJourneyNotesLabel': 'Notes',
    'reader.bookJourneyShowAll': 'Tout afficher',
    'reader.bookJourneyAddToLoom': 'Ajouter à Loom',
    'reader.bookJourneyLoomAdded': '{added} ajouté(s)',
    'reader.bookJourneyLoomLimited': '{n} au-delà de la limite gratuite',
    'reader.bookFinishedOrdinal': 'Livre n°{n} terminé sur EaseVerse',
    'reader.bookFinishedShareGroup': 'Partager sur',
    'reader.bookFinishedShareWechat': 'WeChat',
    'reader.bookFinishedShareWeibo': 'Weibo',
    'reader.bookFinishedShareXhs': 'Xiaohongshu',
    'reader.bookFinishedShareX': 'X',
    'reader.bookFinishedShareIg': 'Instagram',
    'reader.bookFinishedShareFb': 'Facebook',
    'reader.bookFinishedShareLine': 'LINE',
    'reader.bookFinishedShareTg': 'Telegram',
    'reader.bookFinishedShareCopy': 'Copier le lien',
    'reader.bookFinishedShareText': 'Je viens de terminer « {title} » sur EaseVerse ✨ Mon livre n°{n} 📚 {url}',
    'reader.bookFinishedShareWechatReady': 'Copié — collez dans WeChat pour partager',
    'reader.bookFinishedShareLinkCopied': 'Lien copié',
    'reader.bookFinishedShareXhsReady': 'Image enregistrée, texte copié — prêt pour Xiaohongshu',
    'reader.bookFinishedShareIgReady': 'Image enregistrée, texte copié — prêt pour Instagram',
    'reader.bookFinishedShareFailed': 'Échec du partage, réessayez',
    'reader.bookFinishedLabelTotalTime': 'Temps total',
    'reader.bookFinishedLabelBookLength': 'Longueur',
    'reader.bookFinishedLabelHighlights': 'Surlignages',
    'reader.bookFinishedLabelNotes': 'Notes',
    'reader.bookFinishedLabelBookmarks': 'Signets',
    'reader.bookFinishedJourneyLabel': 'Votre parcours de lecture',
    'reader.bookFinishedJourneyPrev': 'Jours précédents',
    'reader.bookFinishedJourneyNext': 'Jours suivants',
    'reader.bookFinishedClose': 'Retour à la bibliothèque',
    'library.editFinishedDate': 'Modifier la date',
    'library.completedBooksLine1': 'Vos livres terminés apparaîtront ici',
    'library.completedBooksLine2': 'La lecture construit des systèmes',
    'library.completedBooksLine3': 'Un livre à la fois',
    'library.readingEmptyLine1': 'Commencer à lire',
    'library.readingEmptyLine2': 'Choisissez un livre et commencez',
    'library.readingEmptyLine3': 'À votre rythme',

    // Site metadata
    'meta.siteTitle': 'EaseVerse Reader ｜ Lecture multilingue approfondie',

    // Auth
    'auth.title': 'EaseVerse Reader',
    'auth.subtitle': 'Votre espace de lecture personnel',
    'auth.checking': 'Vérification de l\'authentification...',
    'auth.emailLabel': 'E-mail',
    'auth.emailPlaceholder': 'votre@email.com',
    'auth.sendCode': 'Envoyer le code',
    'auth.sendingCode': 'Envoi en cours...',
    'auth.codeSent': 'Code de vérification envoyé à votre e-mail',
    'auth.enterCode': 'Entrez le code de vérification',
    'auth.codeSentTo': 'Nous avons envoyé un code à 6 chiffres à',
    'auth.verify': 'Vérifier et se connecter',
    'auth.verifying': 'Vérification...',
    'auth.resend': 'Renvoyer le code',
    'auth.resendIn': 'Renvoyer dans {seconds}s',
    'auth.diffEmail': 'Utiliser un autre e-mail',
    'auth.codeIncomplete': 'Veuillez entrer le code à 6 chiffres complet',
    'auth.newCodeSent': 'Nouveau code envoyé',
    'auth.helpLink': 'Un problème ? Contactez-nous',
    'auth.extLogin': 'Se connecter avec EaseVerse Lens',
    'auth.extLoggingIn': 'Connexion...',
    'auth.useEmail': 'Se connecter par e-mail',
    'auth.backToExt': 'Retour à la connexion EaseVerse Lens',
    'auth.or': 'ou',
    'auth.googleError': 'Echec de la connexion Google',
    'auth.agreeTerms': "J'accepte les",
    'auth.termsLink': "Conditions d'utilisation",
    'auth.privacyLink': 'Politique de confidentialité',
    'auth.subscribeNewsletter': "Recevoir les mises à jour et conseils de lecture (1 à 2 emails/mois, désabonnement à tout moment)",
    'auth.darkMode': 'Mode sombre',
    'auth.lightMode': 'Mode clair',
    'auth.products': 'Produits EaseVerse',
    'auth.lensDesc': 'Dictionnaire contextuel',
    'auth.loomDesc': 'Base de connaissances',
    'auth.readerDesc': 'Plateforme de lecture',
    'auth.switchTitle': 'Changer de compte ?',
    'auth.switchDesc': 'Votre extension EaseVerse Lens est connectée avec {email}, un compte différent de votre compte Reader actuel. Passer à ce compte ?',
    'auth.switchConfirm': 'Changer',
    'auth.switchDismiss': 'Rester',

    // Sidebar
    'sidebar.contents': 'Table des matières',
    'sidebar.coverage.read': 'Lu',
    'sidebar.coverage.partial': 'Partiellement lu',
    'sidebar.coverage.unread': 'Non lu',
    'sidebar.noToc': 'Aucune table des matières disponible',
    'sidebar.highlightsNotes': 'Surlignages et notes',
    'sidebar.bookmarks': 'Signets',
    'sidebar.all': 'Tout',
    'sidebar.highlights': 'Surlignages',
    'sidebar.notes': 'Notes',
    'sidebar.emptyAll': 'Pas encore de surlignages ou notes. Sélectionnez du texte en lisant.',
    'sidebar.emptyHighlights': 'Pas encore de surlignages. Sélectionnez du texte en lisant.',
    'sidebar.emptyNotes': 'Pas encore de notes. Sélectionnez du texte et ajoutez une note.',
    'sidebar.emptyBookmarks': 'Pas encore de signets. Appuyez sur « Ajouter » pour marquer votre position.',
    'sidebar.add': 'Ajouter',
    'sidebar.save': 'Enregistrer',
    'sidebar.cancel': 'Annuler',
    'sidebar.removeHighlight': 'Supprimer le surlignage (garder la note)',
    'sidebar.unknownChapter': 'Chapitre inconnu',
    'sidebar.bookmark': 'Signet',
    'sidebar.page': 'Page {page}',

    'reader.settingsCurrentBookHint': 'pour le livre actuel uniquement',
    'reader.settingsAllBooksHint': 'pour tous les livres',
    'reader.viewMode': 'Mode de lecture',
    'reader.scroll': 'Défilement',
    'reader.chapter': 'Chapitre',
    'reader.fontSize': 'Taille du texte',
    'reader.pageWidth': 'Largeur de page',
    'reader.pageMargin': 'Marges',
    'reader.pageMarginNarrow': 'Étroites',
    'reader.pageMarginStandard': 'Standard',
    'reader.pageMarginWide': 'Larges',
    'reader.lineHeight': 'Interligne',
    'reader.paragraph': 'Paragraphe',
    'reader.letter': 'Espacement',
    'reader.setLanguage': 'Veuillez sélectionner la langue de ce livre',
    'reader.languageConfirm': 'OK',
    'notification.dismiss': 'Fermer',
    'reader.changeLanguage': 'Changer la langue',
    'reader.languageMismatch': 'La langue de ce livre pourrait être :',
    'reader.bookLanguageLabel': 'Langue du livre',
    'reader.font': 'Police',
    'reader.theme': 'Thème',
    'reader.progressBar': 'Barre de progression',
    'reader.always': 'Toujours',
    'reader.hover': 'Survol',
    'reader.hidden': 'Masquée',
    'reader.einkMode': 'Mode e-ink',
    'reader.einkAuto': 'Auto',
    'reader.einkOn': 'Activé',
    'reader.einkOff': 'Désactivé',
    'reader.pagedMode': 'Mode pagination',
    'reader.tapToSelect': 'Toucher pour sélectionner',
    'reader.autoPlayPronunciation': 'Lecture automatique de la prononciation',
    'reader.autoPlayPronunciationHint': 'livre actuel',
    'settings.languageAndLearning': 'Langue et apprentissage',
    'settings.interaction': 'Interaction',
    'settings.pronunciation': 'Preferences de prononciation',
    'settings.accent': 'Accent',
    'settings.voice': 'Voix',
    'settings.accentUS': 'Americain',
    'settings.accentGB': 'Britannique',
    'settings.voiceMale': 'Homme',
    'settings.voiceFemale': 'Femme',
    'settings.proOnly': 'Pro uniquement',
    'language.name.zh': 'Chinois',
    'language.name.en': 'Anglais',
    'language.name.ja': 'Japonais',
    'language.name.ko': 'Coreen',
    'language.name.fr': 'Francais',
    'language.name.de': 'Allemand',
    'language.name.es': 'Espagnol',
    'language.name.pt': 'Portugais',
    'language.name.ru': 'Russe',
    'language.name.it': 'Italien',
    'language.name.vi': 'Vietnamien',
    'language.name.other': 'Autre',
    'reader.chapterLabel': 'Chapitre',
    'reader.bookLabel': 'Livre',
    'reader.prevChapter': 'Chapitre précédent',
    'reader.nextChapter': 'Chapitre suivant',
    'reader.prevPage': 'Page précédente',
    'reader.nextPage': 'Page suivante',
    'reader.goBack': 'Retour',
    'reader.backToPosition': 'Retour à la position précédente',
    'reader.sessionSummary': 'Lu {time}',
    'einkSel.highlight': 'Surligner',
    'einkSel.highlighted': 'Surligné',
    'einkSel.note': 'Note',
    'einkSel.discard': 'Supprimer le surlignage',
    'einkSel.gloss': 'Sens',
    'einkSel.explain': 'Analyse',
    'einkSel.notePlaceholder': 'Ajouter une note…',
    'einkSel.noteSave': 'Enregistrer',
    'einkSel.vocab': 'Vocabulaire',
    'einkSel.vocabSaved': 'Ajouté',
    'einkSel.sentence': 'Phrase entière',
    'reader.sessionSummaryWithWords': 'Lu {time} · {words} mots',
    'reader.sessionSummaryCoverage': 'Lu {time} · {coverage} mots inédits',
    'reader.sessionSummaryReread': 'Lu {time} · {reread} relus',
    'reader.sessionSummaryThreeBucket': 'Lu {time} · {coverage} inédits · {reread} relus',
    'reader.formatTemporarilyUnavailable': 'La prise en charge de {format} est temporairement indisponible pendant sa refonte. Votre fichier et votre progression sont conservés en toute sécurité et seront accessibles dès le retour de {format}.',
    'reader.pdfConverting': 'Conversion de votre PDF au format lisible — généralement 1 à 2 minutes. La page se rafraîchira automatiquement.',
    'reader.pdfConvertingElapsed': 'Écoulé : {elapsed}',
    'reader.dualViewModeLabel': 'Mode de lecture',
    'reader.dualViewText': 'Texte',
    'reader.dualViewDual': 'Côte à côte',
    'reader.dualViewOriginal': 'Original',
    'reader.dualViewNeedsUpgrade': 'Ce livre doit être mis à niveau pour utiliser le mode côte à côte.',
    'reader.dualViewNeedsWiderScreen': 'Le mode côte à côte nécessite un écran plus large.',
    'reader.reanchor': 'Aligner les volets au paragraphe actuel',
    'reader.dualViewUpgradeTooltip': 'Mettre à niveau ce livre pour activer le mode côte à côte',
    'reader.dualViewUpgradeConfirm': 'La mise à niveau retraite ce livre (~1–2 minutes). Le livre sera indisponible pendant la conversion. Continuer ?',
    'reader.dualViewUpgradeFailed': 'Échec de la mise à niveau. Veuillez réessayer plus tard.',
    'reader.qualityMediumToast': 'Une partie du texte de ce livre peut avoir été imparfaitement extraite. Le mode côte à côte est activé pour vérifier avec le PDF d’origine.',
    'reader.qualityLowToast': 'Ce PDF semble être un scan ou de qualité médiocre. Le mode original est activé par défaut.',
    'reader.qualityLowTooltip': 'L’extraction du texte n’est pas fiable pour ce livre. Cliquez pour plus de détails.',
    'reader.qualityLowAria': 'Avertissement de qualité du texte',
    'reader.qualityLowPopoverTitle': 'Couche de texte incomplète',
    'reader.qualityLowPopoverBody': 'Le texte de ce livre n’a pas pu être extrait de manière fiable depuis le PDF source. Le mode original est utilisé pour préserver la fidélité.',
    'reader.qualityDontShowAgain': 'Ne plus afficher pour ce livre',
    'reader.openAsOriginal': 'Ouvrir le PDF original',
    'reader.openAsOriginalHint': 'Affiche directement le PDF source. Surlignages, notes et Lens ne sont pas disponibles en mode de secours.',
    'reader.figurePeekFigureOne': '1 figure',
    'reader.figurePeekFigureMany': '{n} figures',
    'reader.figurePeekTableOne': '1 tableau',
    'reader.figurePeekTableMany': '{n} tableaux',
    'reader.figurePeekEquationOne': '1 équation',
    'reader.figurePeekEquationMany': '{n} équations',
    'reader.peekDialogLabel': 'Aperçu de la page PDF source',
    'reader.peekLoading': 'Chargement de la page PDF…',
    'reader.peekClose': 'Fermer l’aperçu',
    'reader.pdfConversionFailed': "Nous n'avons pas pu convertir ce PDF. Le fichier original est conservé ; vous pouvez le supprimer et le téléverser à nouveau, ou contacter le support.",
    'reader.minutesShort': '{n} min',
    'reader.hoursShort': '{n} h',
    'reader.hoursMinutesShort': '{h} h {m} min',
    'reader.theme.white': 'Blanc',
    'reader.theme.slate': 'Ardoise',
    'reader.theme.sage': 'Confort visuel',
    'reader.theme.parchment': 'Parchemin',
    'reader.theme.midnight': 'Minuit',
    'reader.loginRequired': 'Veuillez vous connecter pour lire ce livre',
    'reader.loadFailed': 'Échec du chargement du livre',
    'reader.bookNotFound': 'Livre introuvable',
    'reader.deepReading.title': 'Lecture approfondie',
    'reader.deepReading.close': 'Fermer la lecture approfondie',
    'reader.deepReading.toggle': 'Panneau de lecture approfondie',
    'reader.deepReading.bubbles': 'Carte des recherches',
    'lens.pronounceLocked': 'Passez à Pro pour prononcer cette langue',
    'reader.deepReading.viewHistory': 'Récents',
    'reader.deepReading.viewFrequency': 'Fréquence',
    'reader.deepReading.playNormal': 'Écouter',
    'reader.deepReading.playSlow': 'Écouter lentement (0,7×)',
    'reader.deepReading.notes': 'Notes',
    'reader.deepReading.chat': 'Discussion',
    'reader.deepReading.tabLookups': 'Mots',
    'reader.deepReading.tabNotes': 'Notes',
    'reader.deepReading.done': 'Terminé',
    'reader.deepReading.comingSoon': 'Bientôt disponible',
    'reader.deepReading.signInToSee': 'Connectez-vous pour voir votre carte',
    'reader.deepReading.loading': 'Chargement…',
    'reader.deepReading.loadError': 'Échec du chargement',
    'reader.deepReading.noLookups': 'Aucune recherche dans ce livre',
    'reader.deepReading.contexts': 'Contextes',
    'reader.deepReading.back': '← Retour à la carte',
    'reader.deepReading.noNotes': 'Aucune note ni surlignage',
    'reader.deepReading.notePlaceholder': 'Écrivez votre note…',
    'reader.deepReading.addNote': '+ Ajouter une note',
    'reader.deepReading.jumpToPassage': 'Aller au passage',
    'reader.deepReading.toLoom': '→ Ajouter à Loom',
    'reader.deepReading.inLoom': '✓ Dans Loom',
    'reader.deepReading.loomError': "Échec de l'ajout à Loom",
    'reader.deepReading.collectToLoom': 'Enregistrer dans Loom',
    'reader.deepReading.deleteEntry': 'Retirer de la liste',
    'reader.deepReading.reply': '↩ Répondre',
    'reader.deepReading.replyOpen': 'Répondre à cette note',
    'reader.deepReading.replyPlaceholder': 'Écrivez une réponse…',
    'reader.deepReading.repliesShow': 'Afficher {n} réponses',
    'reader.deepReading.repliesHide': 'Masquer les réponses',
    'reader.deepReading.editCount': 'modifié {n}×',
    'reader.deepReading.editHistoryOpen': "Voir l'historique",
    'reader.deepReading.deleteNote': 'Supprimer',
    'reader.deepReading.timeJustNow': "à l'instant",
    'reader.deepReading.timeMinAgo': 'il y a {n} min',
    'reader.deepReading.timeHourAgo': 'il y a {n} h',
    'reader.deepReading.timeDayAgo': 'il y a {n} j',
    'reader.deepReading.editHistoryTitle': 'Historique des modifications',
    'reader.deepReading.editHistoryCurrent': 'Actuelle',
    'reader.deepReading.editHistoryNone': 'Aucune version antérieure',
    'reader.deepReading.editHistoryError': "Échec du chargement de l'historique",
    'reader.followLink': 'Suivre le lien',

    'note.placeholder': 'Écrivez votre note...',
    'note.addNotePlaceholder': 'Ajouter une note...',
    'note.editNote': 'Modifier la note',
    'note.addNote': 'Ajouter une note',
    'note.deleteNote': 'Supprimer la note',
    'note.deleteHighlight': 'Supprimer le surlignage',
    'note.save': 'Enregistrer',
    'note.copy': 'Copier',
    'note.cancel': 'Annuler',
    'note.close': 'Fermer',
    'note.unpin': 'Désépingler',
    'note.highlightedText': 'Texte surligné',
    'note.highlightColor': 'Surligner {color}',
    'note.underlineColor': 'Souligner {color}',
    'note.saving': 'Enregistrement...',
    'note.saved': 'Enregistré',
    'note.error': 'Erreur',

    // Loading
    'loading.default': 'Chargement...',
    'loading.backToLibrary': 'Retour à la bibliothèque',

    // Stats Card
    'stats.lessThan1min': '<1 min',
    'stats.hour': 'h ',
    'stats.minute': 'min',
    'stats.goalLabel': 'Objectif mensuel : {days} jours/semaine, {time}/jour',
    'stats.goalLabelDaily': 'Objectif mensuel : {time}/jour',
    'stats.noGoal': 'Aucun objectif défini',
    'stats.streakDays': '{n} jours de lecture consécutifs',
    'stats.streakLabel': 'Série',
    'stats.thisMonth': 'Ce mois',
    'stats.todayLabel': "Aujourd'hui",
    'stats.thisWeek': 'Cette semaine',
    'stats.reading': ' Lecture',
    'stats.yearGoalLabel': ' Objectif',
    'stats.yearlyReadingGoal': 'Objectif de lecture {year}',
    'stats.wan': 'k',
    'stats.wordsUnit': 'mots',
    'stats.mon': 'L', 'stats.tue': 'M', 'stats.wed': 'M', 'stats.thu': 'J',
    'stats.fri': 'V', 'stats.sat': 'S', 'stats.sun': 'D',
    'stats.month1': 'Jan', 'stats.month2': 'Fév', 'stats.month3': 'Mar',
    'stats.month4': 'Avr', 'stats.month5': 'Mai', 'stats.month6': 'Jun',
    'stats.month7': 'Jul', 'stats.month8': 'Aoû', 'stats.month9': 'Sep',
    'stats.month10': 'Oct', 'stats.month11': 'Nov', 'stats.month12': 'Déc',
    'stats.setGoalTitle': 'Définir l\'objectif mensuel',
    'stats.weeklyDays': 'Jours par semaine',
    'stats.dailyMinutes': 'Temps de lecture quotidien',
    'stats.yearlyWords': 'Objectif annuel de mots',
    'stats.unitWan': 'x10k mots',
    'stats.save': 'Enregistrer',
    'stats.confirmSave': 'Confirmer',
    'stats.goalConfirmMsg': 'Après enregistrement, il vous reste {n} modification(s) ce mois-ci. Continuer ?',
    'stats.goalEditExhausted': 'Vous avez utilisé toutes les modifications pour ce mois.',
    'stats.goalRemaining': '{n} modification(s) restante(s) ce mois-ci',
    'stats.cancel': 'Annuler',
    'stats.minuteShort': 'min',
    'stats.custom': 'Personnalisé',
    'stats.wordsCount': '≈{n} mots',
    'stats.wordsEstimateHint': 'Estimation basée sur la position de lecture',
    'stats.noReading': 'Pas de lecture',
    'stats.streakHint': 'Lire \u22655min compte pour la s\u00e9rie',
    'stats.dailyThreshold': '{time}/jour',
    'stats.remaining': 'Encore {time}',
    // Dashboard
    'dashboard.title': 'Analyse de lecture',
    'dashboard.titleLang': 'Analyse de lecture en {lang}',
    'dashboard.backToLibrary': 'Bibliothèque',
    'dashboard.langDistribution': 'Répartition linguistique',
    'dashboard.langMonth': 'Mois',
    'dashboard.langYear': 'Année',
    'dashboard.langAll': 'Tout',
    'dashboard.langSingleHint': 'Quand vous lisez dans d\'autres langues, vous pourrez filtrer ici',
    'dashboard.langFold': 'Moins',
    'dashboard.langName.zh': 'chinois',
    'dashboard.langName.en': 'anglais',
    'dashboard.langName.ja': 'japonais',
    'dashboard.langName.ko': 'coréen',
    'dashboard.langName.fr': 'français',
    'dashboard.langName.de': 'allemand',
    'dashboard.langName.es': 'espagnol',
    'dashboard.langName.pt': 'portugais',
    'dashboard.langName.ru': 'russe',
    'dashboard.langName.it': 'italien',
    'dashboard.langName.nl': 'néerlandais',
    'dashboard.langName.ar': 'arabe',
    'dashboard.langName.th': 'thaï',
    'dashboard.langName.vi': 'vietnamien',
    'dashboard.todaySummary': "Aujourd'hui",
    'dashboard.weekSummary': 'Cette semaine',
    'dashboard.readingTime': 'Temps de lecture',
    'dashboard.wordsRead': 'Mots lus',
    'dashboard.totalReadingDays': 'Lecture totale',
    'dashboard.dayUnit': ' jours',
    'dashboard.achievements': 'Accomplissements',
    'dashboard.currentStreak': 'Série de {count} jours',
    'dashboard.showAllBadges': 'Voir tous les badges...',
    'dashboard.cat_member': 'Membre Pro',
    'dashboard.memberBadgeLabel': 'Pro',
    'dashboard.cat_days': 'Jours cumulés',
    'dashboard.cat_streak': 'Série de lecture',
    'dashboard.cat_words': 'Mots lus',
    'dashboard.cat_time': 'Temps de lecture',
    'dashboard.cat_books': 'Livres terminés',
    'dashboard.nextMilestone': 'Suivant',
    'dashboard.memberRewardLine1': 'Maintenir un abonnement payant (Basic / Pro / Max) pendant 365 jours',
    'dashboard.memberRewardLine2': 'pour débloquer 1 mois Pro gratuit',
    'dashboard.memberGiftUnlocked': "Félicitations ! 365 jours en tant que membre EaseVerse — votre cadeau est arrivé~🎁",
    'dashboard.memberActiveTooltip': "Jour {days} en tant que membre EaseVerse — un cadeau vous attend au jour 365~",
    'dashboard.memberPausedTooltip': "Vous avez été membre EaseVerse pendant {days} jours. Renouvelez pour continuer~",
    'dashboard.memberNotPaidTooltip': "Vous n'êtes pas encore membre EaseVerse — rendez-vous dans votre profil pour débloquer~",
    'dashboard.badgeUnlocked': 'Bravo pour le badge "{0}" !\nContinuez pour débloquer "{1}"',
    'dashboard.badgeMaxLevel': 'Bravo pour le badge "{0}" !\nVous avez atteint le niveau max !',
    'dashboard.badgeLocked': 'Commencez à lire\npour débloquer "{0}"',
    'dashboard.badgeCountSuffix': ' obtenu',
    'dashboard.badgeCongratsTooltip': 'Bravo 🎉 Débloqué !',
    'dashboard.badgeCountText': '{n} lecteurs ont débloqué',
    'dashboard.badgeExpandMore': 'Tout afficher',
    'dashboard.daysSuffix': 'j',
    'dashboard.booksSuffix': '',
    'dashboard.viewAllBadges': 'Voir tous les badges',
    'dashboard.statToday': "Lecture du jour",
    'dashboard.statWords': 'Mots lus',
    'dashboard.statTotalWords': 'Total mots lus',
    'dashboard.statRereadWords': '+{n} relus',
    'dashboard.wordsPopoverTitle': 'Langues affichées',
    'dashboard.wordsPopoverSaved': 'Enregistré',
    'dashboard.statWeek': 'Lecture semaine',
    'dashboard.statTotalDays': 'Jours cumulés',
    'dashboard.totalDaysTooltip': 'Le {date}, vous avez commencé votre voyage de lecture sur EaseVerse. Déjà {days} jours !',
    'dashboard.dailyTrend': 'Tendance quotidienne',
    'dashboard.monthlyTotal': '{time} ce mois',
    'dashboard.dailyAvg': 'Moyenne {time}/jour',
    'dashboard.yearMonth': '{month}/{year}',
    'dashboard.hourlyInsight': 'Préférence de lecture : {period}',
    'dashboard.hourlyInsightRange': 'Préférence de lecture : {from} à {to}',
    'dashboard.hourlyWords': 'Mots par minute',
    'dashboard.wpmHint': 'Mots par minute',
    'dashboard.wpmSubtitle': 'Ce mois-ci {wpm} mots/min',
    'dashboard.wpmSubtitleUp': 'Ce mois-ci {wpm} mots/min, +{pct}% par rapport au mois dernier',
    'dashboard.wpmSubtitleDown': 'Ce mois-ci {wpm} mots/min, -{pct}% par rapport au mois dernier',
    'dashboard.wpmInsight': 'MPM le plus élevé le {period}',
    'dashboard.wpmInsightRange': 'MPM le plus élevé de {from} à {to}',
    'dashboard.hourlyTime': 'Temps de lecture par heure',
    'dashboard.wordsUnit': 'mots',
    'dashboard.period.lateNight': 'tard dans la nuit',
    'dashboard.period.dawn': 'aube',
    'dashboard.period.earlyMorning': 'tôt le matin',
    'dashboard.period.lateMorning': 'matin',
    'dashboard.period.earlyAfternoon': 'début d\'après-midi',
    'dashboard.period.lateAfternoon': 'fin d\'après-midi',
    'dashboard.period.evening': 'soir',
    'dashboard.period.nightfall': 'début de nuit',
    'dashboard.bookBreakdown': 'Livres',
    'dashboard.noData': 'Pas encore de données',
    'dashboard.editGoal': 'Définir objectif',
    'dashboard.viewDetails': 'Voir détails',
    'dashboard.yearlyTime': 'Temps de lecture annuel',
    'dashboard.yearlyWpm': 'Vitesse annuelle',
    'dashboard.monthUnit': '',
    'dashboard.wpmExplainPlaceholder': 'WPM (mots par minute) mesure votre vitesse de lecture — explication détaillée à venir.',
    // Milestones
    'milestone.streak_3d': '3 jours de lecture consécutifs !',
    'milestone.streak_7d': '7 jours de lecture consécutifs !',
    'milestone.streak_14d': '14 jours de lecture consécutifs !',
    'milestone.streak_21d': '21 jours de lecture consécutifs !',
    'milestone.streak_30d': '30 jours de lecture consécutifs !',
    'milestone.streak_60d': '60 jours de lecture consécutifs !',
    'milestone.streak_100d': '100 jours de lecture consécutifs !',
    'milestone.streak_180d': '180 jours de lecture consécutifs !',
    'milestone.streak_365d': '365 jours de lecture consécutifs !',
    'milestone.words_10k': '10 000 mots lus !',
    'milestone.words_50k': '50 000 mots lus !',
    'milestone.words_100k': '100 000 mots lus !',
    'milestone.words_500k': '500 000 mots lus !',
    'milestone.words_1m': '1 000 000 de mots lus !',
    'milestone.words_2m': '2 000 000 de mots lus !',
    'milestone.words_3m': '3 000 000 de mots lus !',
    'milestone.words_4m': '4 000 000 de mots lus !',
    'milestone.words_5m': '5 000 000 de mots lus !',
    'milestone.time_10h': '10 heures de lecture !',
    'milestone.time_50h': '50 heures de lecture !',
    'milestone.time_100h': '100 heures de lecture !',
    'milestone.time_300h': '300 heures de lecture !',
    'milestone.time_500h': '500 heures de lecture !',
    'milestone.time_1000h': '1 000 heures de lecture !',
    'milestone.time_2000h': '2 000 heures de lecture !',
    'milestone.weekly_goal_first': 'Premier objectif hebdomadaire atteint !',
    'milestone.weekly_goal_4': '4 semaines consécutives sur objectif !',
    'milestone.yearly_25': '25% de l\'objectif annuel !',
    'milestone.yearly_50': 'Mi-parcours de l\'objectif annuel !',
    'milestone.yearly_75': '75% de l\'objectif annuel !',
    'milestone.yearly_100': 'Objectif annuel atteint !',
    'milestone.books_1': '1er livre terminé !',
    'milestone.books_3': '3 livres terminés !',
    'milestone.books_5': '5 livres terminés !',
    'milestone.books_10': '10 livres terminés !',
    'milestone.books_25': '25 livres terminés !',
    'milestone.books_50': '50 livres terminés !',
    'milestone.books_75': '75 livres terminés !',
    'milestone.books_100': '100 livres terminés !',
    'milestone.days_7': '7 jours de lecture !',
    'milestone.days_30': '30 jours de lecture !',
    'milestone.days_100': '100 jours de lecture !',
    'milestone.days_200': '200 jours de lecture !',
    'milestone.days_365': '365 jours de lecture !',
    'milestone.member_365d': '365 jours en tant que membre Pro !',
    'milestone.lang_words': '{amount} mots lus en {lang} !',
    'milestone.lang_time': '{amount} de lecture en {lang} !',

    // Chunks — Paramètres
    'chunks.title': 'Annotations lexicales',
    'chunks.showAnnotations': 'Afficher les annotations',
    'chunks.filterRange': 'Plage d\'affichage',
    'chunks.levelAdaptive': 'Adaptatif',
    'chunks.modeManual': 'Manuel',
    'chunks.levelShowAll': 'Tout afficher',
    'chunks.density': 'Densité',
    'chunks.densityFocused': 'Ciblé',
    'chunks.densityDetailed': 'Détaillé',
    'chunks.adaptiveExplore': 'Apprentissage de vos préférences ({count}/{total})',
    'chunks.adaptiveActive': 'Adapté à partir de {count} interactions',
    'chunks.manualLevel': 'Niveau',
    'chunks.levelAll': 'Tous',
    'chunks.manualCategory': 'Catégorie',
    'chunks.cat_vocabulary': 'Vocabulaire',
    'chunks.cat_phrasal_verb': 'Verbe à part.',
    'chunks.cat_collocation': 'Collocation',
    'chunks.cat_idiom': 'Idiome',
    'chunks.cat_grammar': 'Grammaire',
    'chunks.cat_knowledge': 'Savoir',
    'chunks.cat_tone': 'Registre',
    'chunks.resetProfile': 'Réinitialiser le profil',
    'chunks.modeTranslate': 'Traduire',
    'chunks.modeSynonym': 'Synonyme',
    'chunks.loading': 'Chargement...',
    'chunks.rehinting': 'Repensé sous un autre angle…',
    'chunks.reportBadCache': 'Signaler une mauvaise traduction',
    'chunks.generating': 'Génération de l\'explication...',
    'chunks.askPlaceholder': 'Posez votre question',
    'lens.sentenceAnalysis': 'Analyse de phrase',
    'lens.originalSentence': 'Phrase originale',
    'lens.step1': 'Sens',
    'lens.step2': 'Structure',
    'lens.step3': 'Approfondir',
    'lens.analyzing': 'Analyse en cours...',
    'lens.showGrammar': 'Afficher les détails grammaticaux',
    'lens.hideGrammar': 'Masquer les détails grammaticaux',
    'lens.openChat': 'Posez une question',
    'lens.saveNote': 'Enregistrer la note',
    'lens.noteSaved': 'Enregistré',
    'lens.saveNoteFailed': 'Échec de l\'enregistrement',
    'lens.removeNote': 'Retirer la note',
    'lens.removeNoteFailed': 'Échec du retrait',
    'lens.checkingNote': 'Vérification…',
    'lens.collapse': 'Réduire',
    'chunks.resetConfirm': 'Réinitialiser toutes les données d\'apprentissage ? Cette action est irréversible.',
    'chunks.onboardingHint': 'Appuyez sur les points pour voir les explications',
    'chunks.statusLoading': 'Chargement des annotations…',
    'chunks.statusProcessing': 'Analyse du vocabulaire…',
    'chunks.cacheRefreshed': 'Annotations mises à niveau — cliquez pour actualiser',
    'chunks.proRequired': 'Passez à une formule payante pour débloquer les annotations lexicales.',
    'chunks.bookUsage': "Analyse lexicale : {used}/{limit} livres ce mois-ci",
    'chunks.bookLimitReached': "{limit}/{limit} livres ce mois-ci — passez à Pro pour un accès illimité",

    // Limite d'appareils
    'deviceLimit.titleConfirm': 'Limite d\'appareils atteinte',
    'deviceLimit.titleSelect': 'Limite d\'appareils atteinte',
    'deviceLimit.confirmDesc': 'Continuer déconnectera l\'appareil suivant :',
    'deviceLimit.selectDesc': 'Sélectionnez un appareil à remplacer :',
    'deviceLimit.loggedIn': 'connecté',
    'deviceLimit.cancel': 'Annuler',
    'deviceLimit.confirming': 'Connexion...',
    'deviceLimit.confirmLogin': 'Confirmer et se connecter',
    'deviceLimit.confirmReplace': 'Remplacer et se connecter',
    'deviceLimit.unknownDevice': 'Appareil inconnu',

    // Session expirée
    'session.invalidTitle': 'Session expirée',
    'session.invalidHint': 'Cet appareil a été déconnecté.',
    'session.reLogin': 'Se reconnecter',

    // Déconnexion en cours
    'signOut.title': 'Déconnexion…',
    'signOut.message': 'Enregistrement de vos modifications non synchronisées',
    'signOut.hint': 'Veuillez patienter',
  },

  ja: {
    'einkSel.highlight': 'ハイライト',
    'einkSel.highlighted': 'ハイライト済み',
    'einkSel.note': 'メモ',
    'einkSel.discard': 'ハイライトを削除',
    'einkSel.gloss': '意味',
    'einkSel.explain': '詳しい解説',
    'einkSel.notePlaceholder': 'メモを書く…',
    'einkSel.noteSave': '保存',
    'einkSel.vocab': '単語帳',
    'einkSel.vocabSaved': '追加済み',
    'einkSel.sentence': '文全体',
    'reader.remotePosition.title': '別の端末の位置から続けますか？',
    'reader.remotePosition.body': '別の端末がこの本の{percent}%まで読み進めています。',
    'reader.remotePosition.jump': 'そこへ移動',
    'reader.remotePosition.stay': 'ここに留まる',
    'reader.furthestRead': 'ここまで読んだ',
    // ライブラリ
    'library.addBook': '本を追加',
    'library.uploading': 'アップロード中...',
    'library.recentBooks': '最近読んだ本',
    'library.emptyTitle': 'ライブラリは空です',
    'library.emptyHint': '「本を追加」をクリックするかファイルをドラッグ＆ドロップ',
    'library.loading': 'ライブラリを読み込み中...',
    'library.dropHere': 'ここにドロップ',
    'library.dropFormats': 'epub, pdf, txt, mobi',
    'library.signOut': 'ログアウト',
    'library.account': 'アカウント',
    'stuckPdf.title': '{count}冊のPDFを再アップロードしてください',
    'stuckPdf.explanation': 'これらのPDFはPDF→EPUB変換機能の提供開始前にアップロードされたため、開くことができません。削除して再アップロードすると、AIによる章クリーニングとデュアルビュー読書が利用できます。',
    'stuckPdf.deleteBook': '削除',
    'sidebar.myBookshelf': 'マイ本棚',
    'sidebar.allBooks': 'すべての本',
    'sidebar.reading': '読書中',
    'sidebar.finished': '読了',
    'sidebar.curatedLibrary': 'おすすめ書庫',
    'sidebar.readingAnalytics': '読書分析',
    'sidebar.userCenter': 'ユーザーセンター',
    'sidebar.collapse': 'サイドバーを閉じる',
    'sidebar.expand': 'サイドバーを開く',
    'sidebar.darkMode': 'ダークモード',
    'sidebar.lightMode': 'ライトモード',
    'sidebar.search': '検索',
    'sidebar.searchPlaceholder': '本を検索...',
    'sidebar.home': 'ホーム',
    'sidebar.settings': '設定',
    'landing.continueReading': '続きを読む',
    'landing.quote': '今日の名言',
    'landing.like': 'いいね',
    'landing.liked': 'いいね済み',
    'landing.nextQuote': '次の名言',
    'landing.todayRead': '今日の読書',
    'landing.todayNotStarted': '今日はまだ読んでいません',
    'landing.weekRead': '今週の読書',
    'landing.monthRead': '今月の読書',
    'landing.weekReading': '今週',
    'landing.lastRead': '最近読んだ本',
    'library.deleteBook': '本を削除',
    'library.uploadFailed': 'アップロードに失敗しました',
    'library.drmProtected': 'このファイルはDRM保護されており、開くことができません。',
    'library.huffNotSupported': 'このMOBIファイルはHUFF/CDIC圧縮を使用しており、サポートされていません。標準圧縮で再保存してください。',
    'library.parseFailed': 'このファイルを読み取れませんでした — 破損しているか、サポートされていない形式の可能性があります。',
    'library.loadFailed': '本の読み込みに失敗しました',
    'library.deleteFailed': '本の削除に失敗しました',
    'library.invalidFormat': 'epub、mobi、pdf形式のファイルをアップロードしてください',
    'library.pdfTemporarilyDisabled': 'PDFのアップロードは、リーダーを再構築している間、一時的に無効になっています。現在は EPUB と MOBI がおすすめです。',
    'library.pdf.confirm.title': 'この PDF を変換しますか？',
    'library.pdf.confirm.summary': 'この変換で {pages} ページの割り当てを消費します',
    'library.pdf.confirm.colCurrent': '現在',
    'library.pdf.confirm.colAfter': '変換後',
    'library.pdf.confirm.rowMonthly': '今月',
    'library.pdf.confirm.rowDaily': '本日',
    'library.pdf.confirm.confirm': '変換する',
    'library.pdf.confirm.cancel': 'キャンセル',
    'library.pdf.unit.pages': '{n} ページ',
    'library.pdf.error.perBookCapTitle': 'PDF が現在のプランの上限を超えています',
    'library.pdf.error.perBookCapBody': 'この PDF は現在のプランの 1 冊上限を超えています',
    'library.pdf.error.perBookCapLegend': '{pages} ページ — 上限 {cap} ページを超過',
    'library.pdf.error.quotaExceededTitle': '割り当て不足',
    'library.pdf.error.quotaExceededBody': 'この変換には {pages} ページの割り当てが必要です',
    'library.pdf.error.needs': '必要 {pages}',
    'library.pdf.error.unreadableTitle': 'この PDF を読み取れません',
    'library.pdf.error.unreadableBody': 'このファイルは読み取れないようです。よくある原因：',
    'library.pdf.error.unreadableCause1': 'ファイルが破損しているか不完全',
    'library.pdf.error.unreadableCause2': 'PDF がパスワード保護または暗号化されている',
    'library.pdf.error.unreadableCause3': 'ファイルは実際には PDF ではない（拡張子だけ）',
    'library.pdf.error.dismiss': 'OK',
    'library.pdf.upgrade.title': 'PDF 変換は Pro 専用機能です',
    'library.pdf.upgrade.body': 'PDF 変換は Pro と Max の機能です。アップグレードすると：',
    'library.pdf.upgrade.benefit1': '1 冊あたり最大 400 ページ',
    'library.pdf.upgrade.benefit2': '月あたり最大 600 ページ',
    'library.pdf.upgrade.benefit3': 'デバイス間のクラウド同期',
    'library.pdf.upgrade.cta': 'プランを見る',
    'library.freeLimitReached': '無料プランでは最大{limit}冊まで。アップグレードで無制限＆クラウド同期。',
    'library.deleteBook': '本を削除',
    'library.returnBook': '本を返却',
    'library.bookLimitExceeded': '本棚の上限に達しました。本を返却して空きを作るか、無制限プランにアップグレードしてください。',
    'library.bookExists': '「{title}」{author}はすでにライブラリにあります。\n\n新しいコピーとしてアップロードしますか？',
    'library.bookExistsByAuthor': '（{author}）',
    'library.cloudProOnly': 'クラウド同期は有料ユーザー限定です。アップグレードしてクラウドバックアップを解除。',
    'library.cloudBackupTitle': '書籍はクラウドにバックアップされます',
    'library.cloudBackupDesc': '有料プランでは書籍をクラウドに非公開でバックアップし、どの端末でも読めます。「すべての本」右上のスイッチでいつでもオフにできます。',
    'library.cloudBackupTurnOff': 'オフにする',
    'library.cloudBackupGotIt': 'わかりました',
    'library.cloudBackupToggle': 'クラウドへ自動バックアップ',
    'library.notAvailable': 'この本は別のデバイスでアップロードされたため、ここでは利用できません。再アップロードしてください。',
    'library.phantomDialogDesc': 'この本のファイルはこのデバイスに存在せず、クラウドにもコピーがありません。ファイルを新しい本としてアップロードするか、この本を本棚から削除できます。',
    'library.phantomUploadNew': '新しい本としてアップロード',
    'library.phantomRemove': '本棚から削除',
    'library.cloudOnly': 'クラウド',
    'library.downloading': 'ダウンロード中…',
    'library.downloadFailed': 'ダウンロードに失敗しました。接続を確認して再試行してください。',
    'library.offlineCloudBook': 'オフラインです。この本はクラウドに保存されており、インターネット接続が必要です。',
    'library.confirmDelete': 'この本を削除しますか？',
    'library.confirmRemoveCurated': 'この本を本棚から削除しますか？おすすめ書庫からいつでも追加できます。',
    'library.deleteDialogDesc': 'この本はクラウドにバックアップされています。どうしますか？',
    'library.deleteLocalOnly': 'ローカル容量を解放',
    'library.deleteCompletely': '完全に削除',
    'library.confirmFreeLocalSpace': 'ローカルファイルを削除しますか？クラウドにバックアップがあり、いつでも再ダウンロードできます。',
    'library.confirmBulkFreeLocalSpace': '{count}冊のローカル容量を解放しますか？クラウドのコピーは影響されません。',
    'library.freeLocalSpace': 'ローカル容量を解放',
    'library.freeLocalSpaceShort': '容量を解放',
    'library.allBooks': 'すべての本',
    'library.converting': '変換中',
    'chapterSelection.batchTitle': '低速変換 — クォータ半分',
    'chapterSelection.batchHint': '通常は数時間以内、遅くとも翌日に完了します。完了後は本棚からすぐ読めます。',
    'chapterSelection.chargeSummary': '今回の消費は {pages} ページ分です',
    'chapterSelection.cachedDiscount': '（{pages} ページは共有キャッシュ命中で半額）',
    'library.batchPendingBadge': '待機中・低速',
    'reader.pdfBatch.title': '変換待機中',
    'reader.pdfBatch.hint': 'この本は低速変換（クォータ半分）を選択しています。通常は数時間以内、遅くとも翌日に完了し、完了後は本棚から開けます。',
    'library.badgeExtracting': '解析中',
    'library.badgeConverting': '整形',
    'library.badgeFinishing': 'もうすぐ完了',
    'library.pendingPreview': '章を選択',
    'library.pendingPreviewBadge': '章選択待ち',
    'library.uploadingBadge': 'アップロード中…',
    'library.scanOnlyBadge': 'OCR 必要',
    'library.scanOnlyBadgeTitle': 'スキャン PDF — アップロード前に OCR を実行してください',
    'chapterSelection.title': '変換する章を選択',
    'chapterSelection.unreadablePdf': 'この PDF を読み込めませんでした。ファイルが破損しているか、非標準の形式の可能性があります。書き出し直して（プレビュー → PDF として書き出す、または 印刷 → PDF として保存）から再アップロードしてください。',
    'chapterSelection.selectAll': 'すべて選択',
    'chapterSelection.invertSelection': '反転',
    'chapterSelection.selectedCount': '{n}章 · {pages}ページ',
    'chapterSelection.includesFreePages': '（うち{pages}ページ無料）',
    'chapterSelection.alreadyPaid': '購入済み · 無料',
    'chapterSelection.alreadyConverted': '✓ 変換済み',
    'library.addChapters': '章を追加',
    'chapterSelection.quotaUsage': '月間 {used} / {cap} ページを使用',
    'chapterSelection.exceedsCap': '1冊あたりの上限 {cap} ページを超過',
    'chapterSelection.exceedsQuota': '選択が残り配額を超過しています — 選択を減らしてください',
    'chapterSelection.outlineSparse': '目次情報が不完全です',
    'chapterSelection.outlineHeuristic': '目次なし — レイアウトから自動検出',
    'chapterSelection.tocHarvested': '原書の目次に基づいて章タイトルを補正しました',
    'chapterSelection.tocHarvestedPartial': '一部の章タイトルを原書の目次に基づいて補正しました（残りは自動検出）',
    'chapterSelection.fallbackSingle': '目次なし — 全体を1セクションとして変換',
    'chapterSelection.loadingOutline': '章を解析中…',
    'chapterSelection.fallbackChunks': '章タイトルを検出できません — ページ数で均等に分割',
    'chapterSelection.pageRangeTitle': '{start}–{end} ページ',
    'chapterSelection.scanOnlyTitle': '⚠️ スキャン PDF — 変換できません',
    'chapterSelection.scanOnlyBody': 'この PDF は1ページあたり平均 {chars} 文字しかなく、抽出可能なテキストがほとんどありません。アップロードの前に OCR（Adobe Acrobat、ABBYY FineReader、Tesseract など）を実行してください。',
    'chapterSelection.startConversion': '変換開始',
    'chapterSelection.submitting': '送信中…',
    'reader.newChaptersAvailable': '{n}件のセクションを追加',
    'reader.conversionDoneWithNew': '変換完了 · {n}件追加',
    'reader.conversionDone': '変換完了',
    'reader.refreshToView': '更新する',
    'common.dismiss': '閉じる',
    'library.searchResults': '件の結果',
    'library.continueReading': '続きを読む',
    'library.continue': '続き',
    'library.showAll': 'すべて表示',
    'library.back': '戻る',
    'library.bookCount': '{count}冊',
    'library.select': '選択',
    'library.cancel': 'キャンセル',
    'library.backToHome': 'ホームに戻る',
    'library.scrollToTop': 'トップに戻る',
    'library.selectedCount': '{count}冊を選択中',
    'library.selectAll': 'すべて選択',
    'library.pinToTop': 'ピン留め',
    'library.unpinFromTop': 'ピン留め解除',
    'library.downloadToLocal': 'ローカルにダウンロード',
    'library.addToCloudShort': 'クラウドに追加',
    'library.removeFromLocalShort': 'ローカルから削除',
    'library.deleteShort': '削除',
    'library.confirmRemoveFromLocal': '{count}冊をローカルから削除しますか？このデバイスから削除されます。',
    'library.finishedReading': '読了',
    'library.currentlyReading': '読書中',
    'library.finishedShort': '読了',
    'library.wpmUnit': 'WPM',
    'library.markFinished': '読了にする',
    'library.markFinishedShort': '読了',
    'library.markFinishedTip': '本文は終了しました。タップして読了にしましょう。',
    'library.confirmMarkFinished': 'この本を読了にしますか？',
    'reader.bookFinishedEyebrow': '読了',
    'reader.bookJourneyRevisitEyebrow': 'この旅をもう一度',
    'reader.bookJourneyBackToReading': '読書に戻る',
    'reader.bookJourneySentinel': '✓ 読了 · 旅をふり返る',
    'reader.bookJourneyButton': '読書の旅',
    'reader.bookJourneySelectAll': 'すべて選択',
    'reader.bookJourneyDeselectAll': '選択解除',
    'reader.bookJourneyWordsLabel': '調べた単語',
    'reader.bookJourneyNotesLabel': 'メモ',
    'reader.bookJourneyShowAll': 'すべて表示',
    'reader.bookJourneyAddToLoom': 'Loom に追加',
    'reader.bookJourneyLoomAdded': '{added} 件追加',
    'reader.bookJourneyLoomLimited': '{n} 件は無料上限超過',
    'reader.bookFinishedOrdinal': 'EaseVerse で読み終えた {n} 冊目の本',
    'reader.bookFinishedShareGroup': 'シェア先',
    'reader.bookFinishedShareWechat': 'WeChat',
    'reader.bookFinishedShareWeibo': 'Weibo',
    'reader.bookFinishedShareXhs': '小紅書',
    'reader.bookFinishedShareX': 'X',
    'reader.bookFinishedShareIg': 'Instagram',
    'reader.bookFinishedShareFb': 'Facebook',
    'reader.bookFinishedShareLine': 'LINE',
    'reader.bookFinishedShareTg': 'Telegram',
    'reader.bookFinishedShareCopy': 'リンクをコピー',
    'reader.bookFinishedShareText': 'EaseVerse で「{title}」を読み終えました ✨ {n} 冊目 📚 {url}',
    'reader.bookFinishedShareWechatReady': 'コピーしました — WeChat に貼り付けて共有',
    'reader.bookFinishedShareLinkCopied': 'リンクをコピーしました',
    'reader.bookFinishedShareXhsReady': '画像を保存・テキストをコピーしました — 小紅書へどうぞ',
    'reader.bookFinishedShareIgReady': '画像を保存・テキストをコピーしました — Instagram へどうぞ',
    'reader.bookFinishedShareFailed': '共有に失敗しました。再試行してください',
    'reader.bookFinishedLabelTotalTime': '総読書時間',
    'reader.bookFinishedLabelBookLength': '本全体',
    'reader.bookFinishedLabelHighlights': 'ハイライト',
    'reader.bookFinishedLabelNotes': 'ノート',
    'reader.bookFinishedLabelBookmarks': 'ブックマーク',
    'reader.bookFinishedJourneyLabel': 'あなたの読書の旅',
    'reader.bookFinishedJourneyPrev': '前の日々',
    'reader.bookFinishedJourneyNext': '次の日々',
    'reader.bookFinishedClose': 'ライブラリに戻る',
    'library.editFinishedDate': '日付を編集',
    'library.localLibrary': 'ローカル',
    'library.filter': 'フィルター',
    'library.filterStorageSection': 'ストレージ',
    'library.filterLanguageSection': '言語',
    'library.filterCloud': 'クラウド',
    'library.filterLocal': 'ローカル',
    'library.filterCurated': 'おすすめ',
    'library.curatedLibrary': 'おすすめ書庫',
    'library.curatedLibraryDesc': 'AI注釈つきのクラシック名著',
    'library.curatedComingSoon': 'まもなく公開——AI注釈つきの厳選書籍。',
    'library.freeBadge': '無料',
    'library.freeThisMonth': '今月無料',
    'library.curatedEnglish': '英語書庫',
    'library.curatedFrench': 'フランス語書庫',
    'library.addToLibrary': 'ライブラリに追加',
    'library.addToMyLibrary': 'マイライブラリに追加',
    'library.adding': '追加中...',
    'library.chapterUnit': '章',
    'library.cloudLibrary': 'クラウド',
    'library.cloudUsage': '{percent}% 使用中',
    'library.cloudUpgrade': 'クラウドは有料ユーザー限定です。アップグレードしてクラウドストレージを解放。',
    'library.upgradeLink': 'アップグレード',
    'library.last7Days': '過去7日間',
    'library.thisYear': '今年',
    'library.yearUploaded': '{year}年にアップロード',
    'library.yearFinishedCount': '{year}年（{count}）',
    'library.otherFinishedCount': 'その他（{count}）',
    'library.completedBooksLine1': '読了した本がここに表示されます',
    'library.completedBooksLine2': '読書は習慣から',
    'library.completedBooksLine3': '一冊ずつ',
    'library.readingEmptyLine1': '読書を始めよう',
    'library.readingEmptyLine2': '一冊選んで読み始めましょう',
    'library.readingEmptyLine3': '一ページずつ',

    // Site metadata
    'meta.siteTitle': 'EaseVerse Reader ｜ 多言語ディープリーディング',

    // 認証
    'auth.title': 'EaseVerse Reader',
    'auth.subtitle': 'あなたの読書空間',
    'auth.checking': '認証を確認中...',
    'auth.emailLabel': 'メールアドレス',
    'auth.emailPlaceholder': 'your@email.com',
    'auth.sendCode': '認証コードを送信',
    'auth.sendingCode': '送信中...',
    'auth.codeSent': '認証コードをメールに送信しました',
    'auth.enterCode': '認証コードを入力',
    'auth.codeSentTo': '6桁の認証コードを送信しました：',
    'auth.verify': '認証してログイン',
    'auth.verifying': '認証中...',
    'auth.resend': '再送信',
    'auth.resendIn': '{seconds}秒後に再送信',
    'auth.diffEmail': '別のメールアドレスを使う',
    'auth.codeIncomplete': '6桁の認証コードを入力してください',
    'auth.newCodeSent': '新しい認証コードを送信しました',
    'auth.helpLink': 'お困りですか？お問い合わせ',
    'auth.extLogin': 'EaseVerse Lens でログイン',
    'auth.extLoggingIn': 'ログイン中...',
    'auth.useEmail': 'メールでログイン',
    'auth.backToExt': 'EaseVerse Lens ログインに戻る',
    'auth.or': 'または',
    'auth.googleError': 'Google ログインに失敗しました',
    'auth.agreeTerms': '同意する：',
    'auth.termsLink': '利用規約',
    'auth.privacyLink': 'プライバシーポリシー',
    'auth.subscribeNewsletter': '製品アップデートと学習ヒントを購読（月 1〜2 通、いつでも配信停止可能）',
    'auth.darkMode': 'ダークモード',
    'auth.lightMode': 'ライトモード',
    'auth.products': 'EaseVerse 製品',
    'auth.lensDesc': 'コンテキスト辞書',
    'auth.loomDesc': 'ナレッジベース',
    'auth.readerDesc': '読書プラットフォーム',
    'auth.switchTitle': 'アカウントを切り替えますか？',
    'auth.switchDesc': 'EaseVerse Lens 拡張機能は {email} でログイン中です。Reader のアカウントと異なります。切り替えますか？',
    'auth.switchConfirm': '切り替え',
    'auth.switchDismiss': 'このまま',

    // サイドバー
    'sidebar.contents': '目次',
    'sidebar.coverage.read': '読了',
    'sidebar.coverage.partial': '一部読んだ',
    'sidebar.coverage.unread': '未読',
    'sidebar.noToc': '目次がありません',
    'sidebar.highlightsNotes': 'ハイライトとノート',
    'sidebar.bookmarks': 'ブックマーク',
    'sidebar.all': 'すべて',
    'sidebar.highlights': 'ハイライト',
    'sidebar.notes': 'ノート',
    'sidebar.emptyAll': 'ハイライトやノートはまだありません。読書中にテキストを選択してハイライトやノートを追加しましょう。',
    'sidebar.emptyHighlights': 'ハイライトはまだありません。読書中にテキストを選択してハイライトしましょう。',
    'sidebar.emptyNotes': 'ノートはまだありません。読書中にテキストを選択してノートを追加しましょう。',
    'sidebar.emptyBookmarks': 'ブックマークはまだありません。「追加」をタップして現在の位置をブックマーク。',
    'sidebar.add': '追加',
    'sidebar.save': '保存',
    'sidebar.cancel': 'キャンセル',
    'sidebar.removeHighlight': 'ハイライトを削除（ノートは残す）',
    'sidebar.unknownChapter': '不明な章',
    'sidebar.bookmark': 'ブックマーク',
    'sidebar.page': '{page}ページ',

    'reader.settingsCurrentBookHint': 'この本のみ',
    'reader.settingsAllBooksHint': 'すべての本',
    'reader.viewMode': '表示モード',
    'reader.scroll': 'スクロール',
    'reader.chapter': 'チャプター',
    'reader.fontSize': '文字サイズ',
    'reader.pageWidth': 'ページ幅',
    'reader.pageMargin': '余白',
    'reader.pageMarginNarrow': '狭い',
    'reader.pageMarginStandard': '標準',
    'reader.pageMarginWide': '広い',
    'reader.lineHeight': '行間',
    'reader.paragraph': '段落間隔',
    'reader.letter': '字間',
    'reader.setLanguage': 'この本の言語を選択してください',
    'reader.languageConfirm': 'OK',
    'notification.dismiss': '閉じる',
    'reader.changeLanguage': '言語を変更',
    'reader.languageMismatch': 'この本の言語：',
    'reader.bookLanguageLabel': '書籍の言語',
    'reader.font': 'フォント',
    'reader.theme': 'テーマ',
    'reader.progressBar': 'プログレスバー',
    'reader.always': '常に表示',
    'reader.hover': 'ホバー時',
    'reader.hidden': '非表示',
    'reader.einkMode': 'E-inkモード',
    'reader.einkAuto': '自動',
    'reader.einkOn': 'オン',
    'reader.einkOff': 'オフ',
    'reader.pagedMode': 'ページめくりモード',
    'reader.tapToSelect': 'タップで選択',
    'reader.autoPlayPronunciation': '発音を自動再生',
    'reader.autoPlayPronunciationHint': 'この本のみ',
    'settings.languageAndLearning': '言語と学習',
    'settings.interaction': 'インタラクション',
    'settings.pronunciation': '発音設定',
    'settings.accent': 'アクセント',
    'settings.voice': '声',
    'settings.accentUS': 'アメリカ英語',
    'settings.accentGB': 'イギリス英語',
    'settings.voiceMale': '男性',
    'settings.voiceFemale': '女性',
    'settings.proOnly': 'Pro限定',
    'language.name.zh': '中国語',
    'language.name.en': '英語',
    'language.name.ja': '日本語',
    'language.name.ko': '韓国語',
    'language.name.fr': 'フランス語',
    'language.name.de': 'ドイツ語',
    'language.name.es': 'スペイン語',
    'language.name.pt': 'ポルトガル語',
    'language.name.ru': 'ロシア語',
    'language.name.it': 'イタリア語',
    'language.name.vi': 'ベトナム語',
    'language.name.other': 'その他',
    'reader.chapterLabel': '本章',
    'reader.bookLabel': '全体',
    'reader.prevChapter': '前の章',
    'reader.nextChapter': '次の章',
    'reader.prevPage': '前のページ',
    'reader.nextPage': '次のページ',
    'reader.goBack': '戻る',
    'reader.backToPosition': '元の位置に戻る',
    'reader.sessionSummary': '{time}読了',
    'reader.sessionSummaryWithWords': '{time}読了 · {words}語',
    'reader.sessionSummaryCoverage': '{time}読了 · 新規{coverage}語',
    'reader.sessionSummaryReread': '{time}読了 · 再読{reread}語',
    'reader.sessionSummaryThreeBucket': '{time}読了 · 新規{coverage}語 · 再読{reread}語',
    'reader.formatTemporarilyUnavailable': '{format} は現在再構築中のため一時的にご利用いただけません。アップロードされたファイルと読書の進捗は安全に保管されており、{format} が再開されたらそのまま開けます。',
    'reader.pdfConverting': 'PDF を読みやすい形式に変換しています（通常 1〜2 分）。完了するとページが自動で更新されます。',
    'reader.pdfConvertingElapsed': '経過時間: {elapsed}',
    'reader.dualViewModeLabel': '読書モード',
    'reader.dualViewText': 'テキスト',
    'reader.dualViewDual': '並列',
    'reader.dualViewOriginal': '原版',
    'reader.dualViewNeedsUpgrade': 'この本を並列モードで使うには、アップグレードが必要です。',
    'reader.dualViewNeedsWiderScreen': '並列モードには広めの画面が必要です。',
    'reader.reanchor': '両側のペインを現在の段落に合わせる',
    'reader.dualViewUpgradeTooltip': 'この本をアップグレードして並列モードを有効にする',
    'reader.dualViewUpgradeConfirm': 'アップグレードすると本を再変換します（約 1〜2 分）。変換中は本を読めません。続けますか？',
    'reader.dualViewUpgradeFailed': 'アップグレードに失敗しました。後ほど再試行してください。',
    'reader.qualityMediumToast': 'この本のテキストは一部抽出が不完全な可能性があります。元の PDF と照らし合わせられるよう、並行表示を有効にしました。',
    'reader.qualityLowToast': 'この PDF はスキャンまたは低品質のようです。オリジナル表示をデフォルトにしました。',
    'reader.qualityLowTooltip': 'この本のテキスト抽出は信頼できません。クリックで詳細を表示。',
    'reader.qualityLowAria': 'テキスト品質の警告',
    'reader.qualityLowPopoverTitle': 'テキストレイヤーが不完全',
    'reader.qualityLowPopoverBody': 'この本は元の PDF からテキストを確実に抽出できませんでした。原本を尊重するため、オリジナル表示をデフォルトにしました。',
    'reader.qualityDontShowAgain': 'この本では今後表示しない',
    'reader.openAsOriginal': '元の PDF を開く',
    'reader.openAsOriginalHint': 'ソース PDF を直接表示します。このフォールバック表示ではハイライト・ノート・Lens は利用できません。',
    'reader.figurePeekFigureOne': '図 1 点',
    'reader.figurePeekFigureMany': '図 {n} 点',
    'reader.figurePeekTableOne': '表 1 点',
    'reader.figurePeekTableMany': '表 {n} 点',
    'reader.figurePeekEquationOne': '数式 1 点',
    'reader.figurePeekEquationMany': '数式 {n} 点',
    'reader.peekDialogLabel': '元 PDF ページのプレビュー',
    'reader.peekLoading': 'PDF ページを読み込み中…',
    'reader.peekClose': 'プレビューを閉じる',
    'reader.pdfConversionFailed': 'この PDF の変換に失敗しました。元ファイルは保存されています。削除して再アップロードするか、サポートにお問い合わせください。',
    'reader.minutesShort': '{n}分',
    'reader.hoursShort': '{n}時間',
    'reader.hoursMinutesShort': '{h}時間{m}分',
    'reader.theme.white': 'ホワイト',
    'reader.theme.slate': 'スレート',
    'reader.theme.sage': '目にやさしい',
    'reader.theme.parchment': 'パーチメント',
    'reader.theme.midnight': 'ミッドナイト',
    'reader.loginRequired': 'ログインしてから読んでください',
    'reader.loadFailed': '本の読み込みに失敗しました',
    'reader.bookNotFound': '本が見つかりません',
    'reader.deepReading.title': 'ディープリーディング',
    'reader.deepReading.close': 'ディープリーディングを閉じる',
    'reader.deepReading.toggle': 'ディープリーディングパネル',
    'reader.deepReading.bubbles': '検索マップ',
    'lens.pronounceLocked': 'この言語の発音には Pro が必要です',
    'reader.deepReading.viewHistory': '履歴',
    'reader.deepReading.viewFrequency': '頻度',
    'reader.deepReading.playNormal': '再生',
    'reader.deepReading.playSlow': 'ゆっくり再生 (0.7×)',
    'reader.deepReading.notes': 'ノート',
    'reader.deepReading.chat': 'チャット',
    'reader.deepReading.tabLookups': '検索',
    'reader.deepReading.tabNotes': 'メモ',
    'reader.deepReading.done': '完了',
    'reader.deepReading.comingSoon': '近日公開',
    'reader.deepReading.signInToSee': 'ログインして検索マップを表示',
    'reader.deepReading.loading': '読み込み中…',
    'reader.deepReading.loadError': '読み込みに失敗しました',
    'reader.deepReading.noLookups': 'この本にはまだ検索がありません',
    'reader.deepReading.contexts': '文脈',
    'reader.deepReading.back': '← マップに戻る',
    'reader.deepReading.noNotes': 'メモやハイライトはまだありません',
    'reader.deepReading.notePlaceholder': 'メモを入力…',
    'reader.deepReading.addNote': '+ メモを追加',
    'reader.deepReading.jumpToPassage': '本文へ移動',
    'reader.deepReading.toLoom': '→ Loom に追加',
    'reader.deepReading.inLoom': '✓ Loom に追加済み',
    'reader.deepReading.loomError': 'Loom への追加に失敗しました',
    'reader.deepReading.collectToLoom': 'Loom に保存',
    'reader.deepReading.deleteEntry': 'リストから削除',
    'reader.deepReading.reply': '↩ 返信',
    'reader.deepReading.replyOpen': 'このメモに返信',
    'reader.deepReading.replyPlaceholder': '返信を入力…',
    'reader.deepReading.repliesShow': '{n} 件の返信を表示',
    'reader.deepReading.repliesHide': '返信を隠す',
    'reader.deepReading.editCount': '{n}回 編集',
    'reader.deepReading.editHistoryOpen': '編集履歴を表示',
    'reader.deepReading.deleteNote': '削除',
    'reader.deepReading.timeJustNow': 'たった今',
    'reader.deepReading.timeMinAgo': '{n} 分前',
    'reader.deepReading.timeHourAgo': '{n} 時間前',
    'reader.deepReading.timeDayAgo': '{n} 日前',
    'reader.deepReading.editHistoryTitle': '編集履歴',
    'reader.deepReading.editHistoryCurrent': '現在のバージョン',
    'reader.deepReading.editHistoryNone': '以前のバージョンはありません',
    'reader.deepReading.editHistoryError': '履歴の読み込みに失敗しました',
    'reader.followLink': 'リンクへ移動',

    'note.placeholder': 'ノートを書く...',
    'note.addNotePlaceholder': 'ノートを追加...',
    'note.editNote': 'ノートを編集',
    'note.addNote': 'ノートを追加',
    'note.deleteNote': 'ノートを削除',
    'note.deleteHighlight': 'ハイライトを削除',
    'note.save': '保存',
    'note.copy': 'コピー',
    'note.cancel': 'キャンセル',
    'note.close': '閉じる',
    'note.unpin': 'ピン留め解除',
    'note.highlightedText': 'ハイライトされたテキスト',
    'note.highlightColor': 'ハイライト {color}',
    'note.underlineColor': '下線 {color}',
    'note.saving': '保存中...',
    'note.saved': '保存済み',
    'note.error': 'エラー',

    // 読み込み
    'loading.default': '読み込み中...',
    'loading.backToLibrary': 'ライブラリに戻る',

    // 統計カード
    'stats.lessThan1min': '1分未満',
    'stats.hour': '時間',
    'stats.minute': '分',
    'stats.goalLabel': '今月の目標：週{days}日、1日{time}',
    'stats.goalLabelDaily': '今月の目標：1日{time}',
    'stats.noGoal': '読書目標が未設定です',
    'stats.streakDays': '{n}日連続読書中',
    'stats.streakLabel': '連続',
    'stats.thisMonth': '今月',
    'stats.todayLabel': '今日',
    'stats.thisWeek': '今週',
    'stats.reading': '月の読書',
    'stats.yearGoalLabel': '年間目標',
    'stats.yearlyReadingGoal': '{year}年の読書目標',
    'stats.wan': '万',
    'stats.wordsUnit': '語',
    'stats.mon': '月', 'stats.tue': '火', 'stats.wed': '水', 'stats.thu': '木',
    'stats.fri': '金', 'stats.sat': '土', 'stats.sun': '日',
    'stats.month1': '1月', 'stats.month2': '2月', 'stats.month3': '3月',
    'stats.month4': '4月', 'stats.month5': '5月', 'stats.month6': '6月',
    'stats.month7': '7月', 'stats.month8': '8月', 'stats.month9': '9月',
    'stats.month10': '10月', 'stats.month11': '11月', 'stats.month12': '12月',
    'stats.setGoalTitle': '今月の読書目標を設定',
    'stats.weeklyDays': '週の読書日数',
    'stats.dailyMinutes': '1日の読書時間',
    'stats.yearlyWords': '年間の語数目標',
    'stats.unitWan': '万語',
    'stats.save': '保存',
    'stats.confirmSave': '確認',
    'stats.goalConfirmMsg': '保存後、今月はあと{n}回変更できます。続けますか？',
    'stats.goalEditExhausted': '今月の変更回数を使い切りました。',
    'stats.goalRemaining': '今月の残り変更回数：{n}回',
    'stats.cancel': 'キャンセル',
    'stats.minuteShort': '分',
    'stats.custom': 'カスタム',
    'stats.wordsCount': '≈{n}語',
    'stats.wordsEstimateHint': '読書位置の変化に基づく推定値',
    'stats.noReading': '未読',
    'stats.streakHint': '5分以上の読書で連続日数にカウント',
    'stats.dailyThreshold': '1日{time}',
    'stats.remaining': '残り{time}',
    // ダッシュボード
    'dashboard.title': '読書分析',
    'dashboard.titleLang': '{lang}の読書分析',
    'dashboard.backToLibrary': 'ライブラリ',
    'dashboard.langDistribution': '言語分布',
    'dashboard.langMonth': '月',
    'dashboard.langYear': '年',
    'dashboard.langAll': 'すべて',
    'dashboard.langSingleHint': '他の言語の本を読むと、ここで言語別にフィルタリングできます',
    'dashboard.langFold': '閉じる',
    'dashboard.langName.zh': '中国語',
    'dashboard.langName.en': '英語',
    'dashboard.langName.ja': '日本語',
    'dashboard.langName.ko': '韓国語',
    'dashboard.langName.fr': 'フランス語',
    'dashboard.langName.de': 'ドイツ語',
    'dashboard.langName.es': 'スペイン語',
    'dashboard.langName.pt': 'ポルトガル語',
    'dashboard.langName.ru': 'ロシア語',
    'dashboard.langName.it': 'イタリア語',
    'dashboard.langName.nl': 'オランダ語',
    'dashboard.langName.ar': 'アラビア語',
    'dashboard.langName.th': 'タイ語',
    'dashboard.langName.vi': 'ベトナム語',
    'dashboard.todaySummary': '今日',
    'dashboard.weekSummary': '今週',
    'dashboard.readingTime': '読書時間',
    'dashboard.wordsRead': '読んだ語数',
    'dashboard.totalReadingDays': '累計読書',
    'dashboard.dayUnit': '日',
    'dashboard.achievements': '読書バッジ',
    'dashboard.currentStreak': '{count}日連続',
    'dashboard.showAllBadges': 'すべてのバッジを見る...',
    'dashboard.cat_member': 'Pro会員',
    'dashboard.memberBadgeLabel': 'Pro',
    'dashboard.cat_days': '累計日数',
    'dashboard.cat_streak': '連続読書',
    'dashboard.cat_words': '読んだ語数',
    'dashboard.cat_time': '読書時間',
    'dashboard.cat_books': '読了した本',
    'dashboard.nextMilestone': '次の目標',
    'dashboard.memberRewardLine1': '有料プラン（Basic / Pro / Max）を365日継続',
    'dashboard.memberRewardLine2': 'で1ヶ月間のPro会員をプレゼント',
    'dashboard.memberGiftUnlocked': 'おめでとう！EaseVerse会員365日達成——ギフトをお届けしました🎁',
    'dashboard.memberActiveTooltip': 'EaseVerse会員{days}日目——365日目にプレゼントが待っています😊',
    'dashboard.memberPausedTooltip': 'EaseVerse会員として{days}日間ご利用いただきました。更新してカウントを継続しましょう〜',
    'dashboard.memberNotPaidTooltip': 'まだEaseVerse会員ではありません——プロフィールから会員登録しましょう〜',
    'dashboard.badgeUnlocked': '「{0}」バッジを獲得！\n次は「{1}」を目指そう',
    'dashboard.badgeMaxLevel': '「{0}」バッジを獲得！\n最高レベルに到達しました！',
    'dashboard.badgeLocked': '読書を始めて\n「{0}」を解除しよう',
    'dashboard.badgeCountSuffix': '人が獲得',
    'dashboard.badgeCongratsTooltip': 'おめでとう 🎉 解除済み！',
    'dashboard.badgeCountText': '{n}人が解除済み',
    'dashboard.badgeExpandMore': 'すべて表示',
    'dashboard.daysSuffix': '日',
    'dashboard.booksSuffix': '冊',
    'dashboard.viewAllBadges': 'すべてのバッジを見る',
    'dashboard.statToday': '今日の読書',
    'dashboard.statWords': '読んだ語数',
    'dashboard.statTotalWords': '累計読了語数',
    'dashboard.statRereadWords': '再読 {n} 語',
    'dashboard.wordsPopoverTitle': '表示言語',
    'dashboard.wordsPopoverSaved': '保存済み',
    'dashboard.statWeek': '今週の読書',
    'dashboard.statTotalDays': '累計日数',
    'dashboard.totalDaysTooltip': '{date}、EaseVerseでの読書旅が始まりました。{days}日が経ちました！',
    'dashboard.dailyTrend': '日別トレンド',
    'dashboard.monthlyTotal': '今月の読書 {time}',
    'dashboard.dailyAvg': '日平均 {time}',
    'dashboard.yearMonth': '{year}年{month}月',
    'dashboard.hourlyInsight': '{period}に読書する傾向',
    'dashboard.hourlyInsightRange': '{from}から{to}に読書する傾向',
    'dashboard.hourlyWords': '1分あたりの語数',
    'dashboard.wpmHint': '1分あたりの語数',
    'dashboard.wpmSubtitle': '今月 {wpm}語/分',
    'dashboard.wpmSubtitleUp': '今月 {wpm}語/分、先月より{pct}%アップ',
    'dashboard.wpmSubtitleDown': '今月 {wpm}語/分、先月より{pct}%ダウン',
    'dashboard.wpmInsight': '{period}のWPMが最高',
    'dashboard.wpmInsightRange': '{from}から{to}のWPMが最高',
    'dashboard.hourlyTime': '時間帯別の読書時間',
    'dashboard.wordsUnit': '語',
    'dashboard.period.lateNight': '深夜',
    'dashboard.period.dawn': '明け方',
    'dashboard.period.earlyMorning': '早朝',
    'dashboard.period.lateMorning': '午前',
    'dashboard.period.earlyAfternoon': '午後',
    'dashboard.period.lateAfternoon': '夕方',
    'dashboard.period.evening': '夜',
    'dashboard.period.nightfall': '夜更け',
    'dashboard.bookBreakdown': '書籍',
    'dashboard.noData': '読書データがまだありません',
    'dashboard.editGoal': '目標を設定',
    'dashboard.viewDetails': '詳細を見る',
    'dashboard.yearlyTime': '年間読書時間',
    'dashboard.yearlyWpm': '年間WPM',
    'dashboard.monthUnit': '月',
    'dashboard.wpmExplainPlaceholder': 'WPM（1分あたりの語数）はあなたの読書速度を測定します——詳細説明は近日公開。',
    // マイルストーン
    'milestone.streak_3d': '3日連続読書達成！',
    'milestone.streak_7d': '7日連続読書達成！',
    'milestone.streak_14d': '14日連続読書達成！',
    'milestone.streak_21d': '21日連続読書達成！',
    'milestone.streak_30d': '30日連続読書達成！',
    'milestone.streak_60d': '60日連続読書達成！',
    'milestone.streak_100d': '100日連続読書達成！',
    'milestone.streak_180d': '180日連続読書達成！',
    'milestone.streak_365d': '365日連続読書達成！',
    'milestone.words_10k': '1万語読了！',
    'milestone.words_50k': '5万語読了！',
    'milestone.words_100k': '10万語読了！',
    'milestone.words_500k': '50万語読了！',
    'milestone.words_1m': '100万語読了！',
    'milestone.words_2m': '200万語読了！',
    'milestone.words_3m': '300万語読了！',
    'milestone.words_4m': '400万語読了！',
    'milestone.words_5m': '500万語読了！',
    'milestone.time_10h': '累計10時間の読書！',
    'milestone.time_50h': '累計50時間の読書！',
    'milestone.time_100h': '累計100時間の読書！',
    'milestone.time_300h': '累計300時間の読書！',
    'milestone.time_500h': '累計500時間の読書！',
    'milestone.time_1000h': '累計1,000時間の読書！',
    'milestone.time_2000h': '累計2,000時間の読書！',
    'milestone.weekly_goal_first': '初めての週間目標達成！',
    'milestone.weekly_goal_4': '4週連続で目標達成！',
    'milestone.yearly_25': '年間目標の25%達成！',
    'milestone.yearly_50': '年間目標の半分達成！',
    'milestone.yearly_75': '年間目標の75%達成！',
    'milestone.yearly_100': '年間目標達成！',
    'milestone.books_1': '初めての1冊を読了！',
    'milestone.books_3': '3冊読了！',
    'milestone.books_5': '5冊読了！',
    'milestone.books_10': '10冊読了！',
    'milestone.books_25': '25冊読了！',
    'milestone.books_50': '50冊読了！',
    'milestone.books_75': '75冊読了！',
    'milestone.books_100': '100冊読了！',
    'milestone.days_7': '累計7日間の読書！',
    'milestone.days_30': '累計30日間の読書！',
    'milestone.days_100': '累計100日間の読書！',
    'milestone.days_200': '累計200日間の読書！',
    'milestone.days_365': '累計365日間の読書！',
    'milestone.member_365d': 'Pro会員365日達成！',
    'milestone.lang_words': '{lang}を{amount}語読了！',
    'milestone.lang_time': '{lang}を{amount}読書！',

    // チャンク — 設定
    'chunks.title': '語塊注釈',
    'chunks.showAnnotations': '語塊注釈を表示',
    'chunks.filterRange': '表示範囲',
    'chunks.levelAdaptive': '自動適応',
    'chunks.modeManual': '手動',
    'chunks.levelShowAll': 'すべて表示',
    'chunks.density': '密度',
    'chunks.densityFocused': '厳選',
    'chunks.densityDetailed': '詳細',
    'chunks.adaptiveExplore': '好みを学習中（{count}/{total}）',
    'chunks.adaptiveActive': '{count}回のインタラクションに基づいて適応済み',
    'chunks.manualLevel': 'レベル',
    'chunks.levelAll': 'すべて',
    'chunks.manualCategory': 'カテゴリ',
    'chunks.cat_vocabulary': '語彙',
    'chunks.cat_phrasal_verb': '句動詞',
    'chunks.cat_collocation': 'コロケーション',
    'chunks.cat_idiom': 'イディオム',
    'chunks.cat_grammar': '文法',
    'chunks.cat_knowledge': '知識',
    'chunks.cat_tone': 'トーン',
    'chunks.resetProfile': 'プロフィールをリセット',
    'chunks.modeTranslate': '翻訳',
    'chunks.modeSynonym': '類義語',
    'chunks.loading': '読み込み中...',
    'chunks.rehinting': '別の角度から考え直しています…',
    'chunks.reportBadCache': '翻訳ミスを報告',
    'chunks.generating': '解説を生成中...',
    'chunks.askPlaceholder': '何でも聞いて',
    'lens.sentenceAnalysis': '文分析',
    'lens.originalSentence': '原文',
    'lens.step1': '意味',
    'lens.step2': '構造',
    'lens.step3': '深掘り',
    'lens.analyzing': '分析中...',
    'lens.showGrammar': '文法詳細を表示',
    'lens.hideGrammar': '文法詳細を隠す',
    'lens.openChat': '質問する',
    'lens.saveNote': 'ノートに保存',
    'lens.noteSaved': '保存済み',
    'lens.saveNoteFailed': '保存失敗',
    'lens.removeNote': 'ノートから削除',
    'lens.removeNoteFailed': '削除失敗',
    'lens.checkingNote': '確認中…',
    'lens.collapse': '折りたたむ',
    'chunks.resetConfirm': 'すべての語塊学習データをリセットしますか？この操作は取り消せません。',
    'chunks.onboardingHint': '点をタップして解説を見る',
    'chunks.statusLoading': '注釈を読み込み中…',
    'chunks.statusProcessing': '語彙を分析中…',
    'chunks.cacheRefreshed': '注釈がアップグレードされました — クリックして更新',
    'chunks.proRequired': '有料プランにアップグレードして語塊注釈を解除しましょう。',
    'chunks.bookUsage': "今月の語塊：{used}/{limit} 冊",
    'chunks.bookLimitReached': "今月は {limit} 冊を使い切りました — Pro で無制限に",

    // デバイス制限モーダル
    'deviceLimit.titleConfirm': 'デバイス数の上限に達しました',
    'deviceLimit.titleSelect': 'デバイス数の上限に達しました',
    'deviceLimit.confirmDesc': '続行すると、以下のデバイスがログアウトされます：',
    'deviceLimit.selectDesc': '置き換えるデバイスを選択：',
    'deviceLimit.loggedIn': 'ログイン中',
    'deviceLimit.cancel': 'キャンセル',
    'deviceLimit.confirming': 'ログイン中...',
    'deviceLimit.confirmLogin': '確認してログイン',
    'deviceLimit.confirmReplace': '置き換えてログイン',
    'deviceLimit.unknownDevice': '不明なデバイス',

    // セッション無効モーダル
    'session.invalidTitle': 'セッションが期限切れです',
    'session.invalidHint': 'このデバイスはログアウトされました。',
    'session.reLogin': '再ログイン',

    // ログアウト進行中オーバーレイ
    'signOut.title': 'ログアウト中…',
    'signOut.message': '未同期の変更を保存しています',
    'signOut.hint': 'しばらくお待ちください',
  },
  de: {
    'einkSel.highlight': 'Markieren',
    'einkSel.highlighted': 'Markiert',
    'einkSel.note': 'Notiz',
    'einkSel.discard': 'Markierung löschen',
    'einkSel.gloss': 'Bedeutung',
    'einkSel.explain': 'Vertiefen',
    'einkSel.notePlaceholder': 'Notiz hinzufügen…',
    'einkSel.noteSave': 'Speichern',
    'einkSel.vocab': 'Vokabeln',
    'einkSel.vocabSaved': 'Gespeichert',
    'einkSel.sentence': 'Ganzer Satz',
    'reader.remotePosition.title': 'Auf dem anderen Gerät weiterlesen?',
    'reader.remotePosition.body': 'Ein anderes Gerät hat {percent} % dieses Buches erreicht.',
    'reader.remotePosition.jump': 'Dorthin springen',
    'reader.remotePosition.stay': 'Hier bleiben',
    'reader.furthestRead': 'Bis hier gelesen',
    'library.addBook': 'Buch hinzufügen',
    'library.uploading': 'Wird hochgeladen...',
    'library.recentBooks': 'Aktuelle Bücher',
    'library.emptyTitle': 'Deine Bibliothek ist leer',
    'library.emptyHint': 'Klicke auf "Buch hinzufügen" oder ziehe Dateien hierher, um loszulegen',
    'library.loading': 'Deine Bibliothek wird geladen...',
    'library.dropHere': 'Bücher hier ablegen',
    'library.dropFormats': 'epub, pdf, txt, mobi',
    'library.signOut': 'Abmelden',
    'library.account': 'Kontocenter',
    'stuckPdf.title': '{count} PDF(s) müssen neu hochgeladen werden',
    'stuckPdf.explanation': 'Diese PDFs wurden vor dem Start der PDF→EPUB-Konvertierung hochgeladen und können nicht geöffnet werden. Lösche und lade sie erneut hoch, um die KI-Kapitelbereinigung und die Dual-View-Lesung zu aktivieren.',
    'stuckPdf.deleteBook': 'Löschen',
    'sidebar.myBookshelf': 'Mein Bücherregal',
    'sidebar.allBooks': 'Alle Bücher',
    'sidebar.reading': 'Lese gerade',
    'sidebar.finished': 'Abgeschlossen',
    'sidebar.curatedLibrary': 'Kuratierte Bibliothek',
    'sidebar.readingAnalytics': 'Lese-Statistiken',
    'sidebar.userCenter': 'Nutzerkonto',
    'sidebar.collapse': 'Seitenleiste einklappen',
    'sidebar.expand': 'Seitenleiste ausklappen',
    'sidebar.darkMode': 'Dunkler Modus',
    'sidebar.lightMode': 'Heller Modus',
    'sidebar.search': 'Suche',
    'sidebar.searchPlaceholder': 'Bücher durchsuchen...',
    'sidebar.home': 'Startseite',
    'sidebar.settings': 'Einstellungen',
    'landing.continueReading': 'Weiterlesen',
    'landing.quote': 'Lesezitat',
    'landing.like': 'Mag ich',
    'landing.liked': 'Gefällt mir',
    'landing.nextQuote': 'Nächstes Zitat',
    'landing.todayRead': 'Heute gelesen',
    'landing.todayNotStarted': 'Heute noch nicht gelesen',
    'landing.weekRead': 'Diese Woche',
    'landing.monthRead': 'Diesen Monat',
    'landing.weekReading': 'Diese Woche',
    'landing.lastRead': 'Zuletzt gelesen',
    'library.deleteBook': 'Buch löschen',
    'library.uploadFailed': 'Datei konnte nicht hochgeladen werden',
    'library.drmProtected': 'Diese Datei ist DRM-geschützt und kann nicht geöffnet werden.',
    'library.huffNotSupported': 'Diese MOBI-Datei verwendet HUFF/CDIC-Komprimierung, die nicht unterstützt wird. Bitte mit Standard-Komprimierung neu speichern.',
    'library.parseFailed': 'Diese Datei konnte nicht gelesen werden – sie könnte beschädigt sein oder eine nicht unterstützte Variante des Formats sein.',
    'library.loadFailed': 'Bücher konnten nicht geladen werden',
    'library.deleteFailed': 'Buch konnte nicht gelöscht werden',
    'library.invalidFormat': 'Bitte lade EPUB-, MOBI- oder PDF-Dateien hoch',
    'library.pdfTemporarilyDisabled': 'PDF-Uploads sind vorübergehend deaktiviert, während wir den Reader neu aufbauen. EPUB und MOBI funktionieren einwandfrei.',
    'library.pdf.confirm.title': 'PDF konvertieren?',
    'library.pdf.confirm.summary': 'Diese Konvertierung verbraucht {pages} Seiten Kontingent.',
    'library.pdf.confirm.colCurrent': 'Jetzt',
    'library.pdf.confirm.colAfter': 'Danach',
    'library.pdf.confirm.rowMonthly': 'Monat',
    'library.pdf.confirm.rowDaily': 'Heute',
    'library.pdf.confirm.confirm': 'Konvertieren',
    'library.pdf.confirm.cancel': 'Abbrechen',
    'library.pdf.unit.pages': '{n} Seiten',
    'library.pdf.error.perBookCapTitle': 'PDF zu groß für deinen Plan',
    'library.pdf.error.perBookCapBody': 'Diese PDF überschreitet das Buchlimit deines Plans.',
    'library.pdf.error.perBookCapLegend': '{pages} Seiten — über dem Limit von {cap}',
    'library.pdf.error.quotaExceededTitle': 'Kontingent überschritten',
    'library.pdf.error.quotaExceededBody': 'Diese Konvertierung benötigt {pages} Seiten Kontingent.',
    'library.pdf.error.needs': 'benötigt {pages}',
    'library.pdf.error.unreadableTitle': 'PDF kann nicht gelesen werden',
    'library.pdf.error.unreadableBody': 'Diese Datei scheint nicht lesbar zu sein. Häufige Ursachen:',
    'library.pdf.error.unreadableCause1': 'Die Datei ist beschädigt oder unvollständig',
    'library.pdf.error.unreadableCause2': 'PDF ist passwortgeschützt oder verschlüsselt',
    'library.pdf.error.unreadableCause3': 'Die Datei ist eigentlich keine PDF (falsche Endung)',
    'library.pdf.error.dismiss': 'OK',
    'library.pdf.upgrade.title': 'PDF ist eine Pro-Funktion',
    'library.pdf.upgrade.body': 'PDF-Konvertierung gibt es bei Pro und Max. Upgrade für:',
    'library.pdf.upgrade.benefit1': 'Bis zu 400 Seiten pro Buch',
    'library.pdf.upgrade.benefit2': '600 Seiten pro Monat',
    'library.pdf.upgrade.benefit3': 'Cloud-Sync zwischen Geräten',
    'library.pdf.upgrade.cta': 'Pläne ansehen',
    'library.freeLimitReached': 'Der kostenlose Plan erlaubt bis zu {limit} Bücher. Upgrade für unbegrenzte Bücher und Cloud-Sync.',
    'library.deleteBook': 'Buch löschen',
    'library.returnBook': 'Buch zurückgeben',
    'library.bookLimitExceeded': 'Du hast dein Buchlimit erreicht. Gib ein Buch aus deinem Regal zurück, um Platz zu schaffen, oder upgrade für unbegrenzt.',
    'library.bookExists': '"{title}"{author} existiert bereits in deiner Bibliothek.\n\nNochmal als neue Kopie hochladen?',
    'library.bookExistsByAuthor': ' von {author}',
    'library.cloudProOnly': 'Cloud-Sync ist für zahlende Nutzer verfügbar. Upgrade, um Cloud-Sicherung freizuschalten.',
    'library.cloudBackupTitle': 'Deine Bücher werden in der Cloud gesichert',
    'library.cloudBackupDesc': 'Dein Tarif sichert deine Bücher privat in der Cloud, sodass du sie auf jedem Gerät lesen kannst. Du kannst es jederzeit über den Schalter oben rechts in „Alle Bücher“ deaktivieren.',
    'library.cloudBackupTurnOff': 'Deaktivieren',
    'library.cloudBackupGotIt': 'Verstanden',
    'library.cloudBackupToggle': 'Auto-Backup in die Cloud',
    'library.notAvailable': 'Dieses Buch wurde auf einem anderen Gerät hochgeladen und ist hier nicht verfügbar. Bitte lade es erneut hoch.',
    'library.phantomDialogDesc': 'Die Datei dieses Buches existiert nicht auf diesem Gerät und es gibt keine Cloud-Kopie. Du kannst eine Datei als neues Buch hochladen oder diesen Eintrag aus deinem Regal entfernen.',
    'library.phantomUploadNew': 'Als neues Buch hochladen',
    'library.phantomRemove': 'Aus dem Regal entfernen',
    'library.cloudOnly': 'Cloud',
    'library.downloading': 'Wird heruntergeladen…',
    'library.downloadFailed': 'Download fehlgeschlagen. Bitte überprüfe deine Verbindung und versuche es erneut.',
    'library.offlineCloudBook': 'Du bist offline. Dieses Buch wird in der Cloud gespeichert und benötigt eine Internetverbindung.',
    'library.confirmDelete': 'Möchtest du dieses Buch wirklich löschen?',
    'library.confirmRemoveCurated': 'Dieses Buch von deinem Regal entfernen? Du kannst es jederzeit aus der kuratierten Bibliothek erneut hinzufügen.',
    'library.deleteDialogDesc': 'Dieses Buch wird in der Cloud gesichert. Was möchtest du tun?',
    'library.deleteLocalOnly': 'Lokalen Speicher freigeben',
    'library.deleteCompletely': 'Komplett löschen',
    'library.confirmFreeLocalSpace': 'Lokale Datei entfernen? Das Buch wird in der Cloud gesichert und kann jederzeit neu heruntergeladen werden.',
    'library.confirmBulkFreeLocalSpace': 'Lokalen Speicher für {count} Bücher freigeben? Cloud-Kopien sind nicht betroffen.',
    'library.freeLocalSpace': 'Lokalen Speicher freigeben',
    'library.freeLocalSpaceShort': 'Speicher freigeben',
    'library.allBooks': 'Alle Bücher',
    'library.converting': 'Wird konvertiert',
    'chapterSelection.batchTitle': 'Langsame Spur — halbes Kontingent',
    'chapterSelection.batchHint': 'Meist in wenigen Stunden fertig, spätestens am nächsten Tag. Danach direkt im Regal lesbar.',
    'chapterSelection.chargeSummary': 'Diese Konvertierung verbraucht {pages} Seiten Kontingent',
    'chapterSelection.cachedDiscount': '({pages} Seiten im geteilten Cache — halber Preis)',
    'library.batchPendingBadge': 'Wartend · langsam',
    'reader.pdfBatch.title': 'In der Warteschlange',
    'reader.pdfBatch.hint': 'Dieses Buch nutzt die langsame Spur (halbes Kontingent). Meist in wenigen Stunden fertig, spätestens am nächsten Tag — danach direkt im Regal lesbar.',
    'library.badgeExtracting': 'Extrahieren',
    'library.badgeConverting': 'Aufbereiten',
    'library.badgeFinishing': 'Fast fertig',
    'library.pendingPreview': 'Kapitel auswählen',
    'library.pendingPreviewBadge': 'Kapitel auswählen',
    'library.scanOnlyBadge': 'OCR nötig',
    'library.scanOnlyBadgeTitle': 'Gescanntes PDF — vor dem Hochladen OCR ausführen',
    'chapterSelection.title': 'Zu konvertierende Kapitel wählen',
    'chapterSelection.unreadablePdf': 'Dieses PDF konnte nicht gelesen werden — die Datei ist möglicherweise beschädigt oder hat ein nicht standardmäßiges Format. Exportieren Sie sie neu (Vorschau → Als PDF exportieren oder Drucken → Als PDF sichern) und laden Sie sie erneut hoch.',
    'chapterSelection.selectAll': 'Alle auswählen',
    'chapterSelection.invertSelection': 'Umkehren',
    'chapterSelection.selectedCount': '{n} Kap. · {pages} Seiten',
    'chapterSelection.includesFreePages': '({pages} Seiten gratis)',
    'chapterSelection.alreadyPaid': 'Bezahlt · gratis',
    'chapterSelection.alreadyConverted': '✓ Konvertiert',
    'library.addChapters': 'Kapitel hinzufügen',
    'chapterSelection.quotaUsage': 'Verbraucht {used} / {cap} Seiten Ihres Monatslimits',
    'chapterSelection.exceedsCap': 'Überschreitet Buchlimit von {cap} Seiten',
    'chapterSelection.exceedsQuota': 'Auswahl überschreitet verbleibendes Kontingent — bitte reduzieren',
    'chapterSelection.outlineSparse': 'Kapitelinformation unvollständig',
    'chapterSelection.outlineHeuristic': 'Kein Inhaltsverzeichnis — Kapitel per Layout erkannt',
    'chapterSelection.tocHarvested': 'Kapiteltitel am Inhaltsverzeichnis des Buches ausgerichtet',
    'chapterSelection.tocHarvestedPartial': 'Einige Kapiteltitel wurden am Inhaltsverzeichnis ausgerichtet — der Rest ist automatisch erkannt',
    'chapterSelection.fallbackSingle': 'Keine Kapitelinfo — als ein Block konvertiert',
    'chapterSelection.loadingOutline': 'Kapitel werden analysiert…',
    'chapterSelection.fallbackChunks': 'Kapiteltitel nicht erkennbar — gleichmäßig nach Seitenzahl aufgeteilt',
    'chapterSelection.pageRangeTitle': 'Seiten {start}–{end}',
    'chapterSelection.scanOnlyTitle': '⚠️ Gescanntes PDF — Konvertierung nicht möglich',
    'chapterSelection.scanOnlyBody': 'Dieses PDF enthält durchschnittlich nur {chars} Zeichen pro Seite, also praktisch keinen extrahierbaren Text. Führen Sie vor dem Hochladen eine OCR-Erkennung durch (Adobe Acrobat, ABBYY FineReader, Tesseract).',
    'chapterSelection.startConversion': 'Konvertierung starten',
    'chapterSelection.submitting': 'Wird gesendet…',
    'reader.newChaptersAvailable': '+{n} Abschnitt(e) konvertiert',
    'reader.conversionDoneWithNew': 'Konvertierung abgeschlossen · +{n} Abschnitt(e)',
    'reader.conversionDone': 'Konvertierung abgeschlossen',
    'reader.refreshToView': 'Aktualisieren',
    'common.dismiss': 'Schließen',
    'library.searchResults': 'Ergebnisse',
    'library.continueReading': 'Weiterlesen',
    'library.continue': 'Weiter',
    'library.showAll': 'Alle anzeigen',
    'library.back': 'Zurück',
    'library.bookCount': '{count} Bücher',
    'library.select': 'Auswählen',
    'library.cancel': 'Abbrechen',
    'library.backToHome': 'Zurück zur Startseite',
    'library.scrollToTop': 'Nach oben scrollen',
    'library.selectedCount': '{count} ausgewählt',
    'library.selectAll': 'Alle auswählen',
    'library.pinToTop': 'Oben fixieren',
    'library.unpinFromTop': 'Lösen',
    'library.downloadToLocal': 'Lokal herunterladen',
    'library.addToCloudShort': 'Zur Cloud hinzufügen',
    'library.removeFromLocalShort': 'Lokal entfernen',
    'library.deleteShort': 'Löschen',
    'library.confirmRemoveFromLocal': '{count} Bücher aus deiner lokalen Bibliothek entfernen? Sie werden von diesem Gerät gelöscht.',
    'library.finishedReading': 'Fertig gelesen',
    'library.currentlyReading': 'Aktuell am Lesen',
    'library.finishedShort': 'Fertig',
    'library.wpmUnit': 'WPM',
    'library.markFinished': 'Als fertig markieren',
    'library.markFinishedShort': 'Fertig',
    'library.markFinishedTip': 'Der Hauptinhalt ist beendet. Tippe hier, um es als fertig zu markieren.',
    'library.confirmMarkFinished': 'Dieses Buch als fertig markieren?',
    'reader.bookFinishedEyebrow': 'Du hast zu Ende gelesen',
    'reader.bookJourneyRevisitEyebrow': 'Deine Reise noch einmal',
    'reader.bookJourneyBackToReading': 'Zurück zum Lesen',
    'reader.bookJourneySentinel': '✓ Fertig · Reise ansehen',
    'reader.bookJourneyButton': 'Lese-Reise',
    'reader.bookJourneySelectAll': 'Alle auswählen',
    'reader.bookJourneyDeselectAll': 'Auswahl aufheben',
    'reader.bookJourneyWordsLabel': 'Nachgeschlagene Wörter',
    'reader.bookJourneyNotesLabel': 'Notizen',
    'reader.bookJourneyShowAll': 'Alle anzeigen',
    'reader.bookJourneyAddToLoom': 'Zu Loom hinzufügen',
    'reader.bookJourneyLoomAdded': '{added} hinzugefügt',
    'reader.bookJourneyLoomLimited': '{n} über dem Gratis-Limit',
    'reader.bookFinishedOrdinal': 'Buch #{n} beendet auf EaseVerse',
    'reader.bookFinishedShareGroup': 'Teilen auf',
    'reader.bookFinishedShareWechat': 'WeChat',
    'reader.bookFinishedShareWeibo': 'Weibo',
    'reader.bookFinishedShareXhs': 'Xiaohongshu',
    'reader.bookFinishedShareX': 'X',
    'reader.bookFinishedShareIg': 'Instagram',
    'reader.bookFinishedShareFb': 'Facebook',
    'reader.bookFinishedShareLine': 'LINE',
    'reader.bookFinishedShareTg': 'Telegram',
    'reader.bookFinishedShareCopy': 'Link kopieren',
    'reader.bookFinishedShareText': 'Ich habe gerade "{title}" auf EaseVerse beendet ✨ Mein Buch #{n} 📚 {url}',
    'reader.bookFinishedShareWechatReady': 'Kopiert — zum Teilen in WeChat einfügen',
    'reader.bookFinishedShareLinkCopied': 'Link kopiert',
    'reader.bookFinishedShareXhsReady': 'Bild gespeichert & Beschriftung kopiert — bereit für Xiaohongshu',
    'reader.bookFinishedShareIgReady': 'Bild gespeichert & Beschriftung kopiert — bereit für Instagram',
    'reader.bookFinishedShareFailed': 'Teilen fehlgeschlagen, bitte versuche es erneut',
    'reader.bookFinishedLabelTotalTime': 'Gesamtzeit',
    'reader.bookFinishedLabelBookLength': 'Buchlänge',
    'reader.bookFinishedLabelHighlights': 'Hervorhebungen',
    'reader.bookFinishedLabelNotes': 'Notizen',
    'reader.bookFinishedLabelBookmarks': 'Lesezeichen',
    'reader.bookFinishedJourneyLabel': 'Deine Lesereise',
    'reader.bookFinishedJourneyPrev': 'Frühere Tage',
    'reader.bookFinishedJourneyNext': 'Spätere Tage',
    'reader.bookFinishedClose': 'Zurück zur Bibliothek',
    'library.editFinishedDate': 'Datum bearbeiten',
    'library.localLibrary': 'Lokal',
    'library.filter': 'Filter',
    'library.filterStorageSection': 'Speicher',
    'library.filterLanguageSection': 'Sprache',
    'library.filterCloud': 'Cloud',
    'library.filterLocal': 'Lokal',
    'library.filterCurated': 'Kuratiert',
    'library.curatedLibrary': 'Kuratierte Bibliothek',
    'library.curatedLibraryDesc': 'Vorverarbeitete, gemeinfreie Klassiker mit sofortigen Chunk-Anmerkungen',
    'library.curatedComingSoon': 'Kommt bald — kuratierte Bücher mit vorverarbeiteten Chunk-Anmerkungen.',
    'library.freeBadge': 'Gratis',
    'library.freeThisMonth': 'Diesen Monat gratis',
    'library.curatedEnglish': 'Englische Bibliothek',
    'library.curatedFrench': 'Französische Bibliothek',
    'library.addToLibrary': 'Zur Bibliothek hinzufügen',
    'library.addToMyLibrary': 'Zu meiner Bibliothek hinzufügen',
    'library.adding': 'Wird hinzugefügt...',
    'library.chapterUnit': 'Kap.',
    'library.cloudLibrary': 'Cloud',
    'library.cloudUsage': '{percent}% verwendet',
    'library.cloudUpgrade': 'Cloud-Bibliothek ist für zahlende Nutzer verfügbar. Mache ein Upgrade, um Cloud-Speicher freizuschalten.',
    'library.upgradeLink': 'Upgrade',
    'library.last7Days': 'Letzte 7 Tage',
    'library.thisYear': 'Dieses Jahr',
    'library.yearUploaded': 'In {year} hochgeladen',
    'library.yearFinishedCount': '{year} ({count})',
    'library.otherFinishedCount': 'Sonstiges ({count})',
    'library.completedBooksLine1': 'Deine abgeschlossenen Bücher werden hier angezeigt',
    'library.completedBooksLine2': 'Lesen schafft Systeme',
    'library.completedBooksLine3': 'Ein Buch nach dem anderen',
    'library.readingEmptyLine1': 'Starte mit dem Lesen',
    'library.readingEmptyLine2': 'Wähle ein Buch und tauche ein',
    'library.readingEmptyLine3': 'Eine Seite nach der anderen',
    'meta.siteTitle': 'EaseVerse Reader ｜ Mehrsprachiges Tiefenlesen',
    'auth.title': 'EaseVerse Reader',
    'auth.subtitle': 'Dein persönliches Lese-Refugium',
    'auth.checking': 'Authentifizierung wird überprüft...',
    'auth.emailLabel': 'E-Mail',
    'auth.emailPlaceholder': 'deine@email.com',
    'auth.sendCode': 'Bestätigungscode senden',
    'auth.sendingCode': 'Code wird gesendet...',
    'auth.codeSent': 'Bestätigungscode wurde an deine E-Mail gesendet',
    'auth.enterCode': 'Bestätigungscode eingeben',
    'auth.codeSentTo': 'Wir haben einen 6-stelligen Code gesendet an',
    'auth.verify': 'Verifizieren & Anmelden',
    'auth.verifying': 'Wird verifiziert...',
    'auth.resend': 'Code erneut senden',
    'auth.resendIn': 'Code erneut senden in {seconds}s',
    'auth.diffEmail': 'Eine andere E-Mail verwenden',
    'auth.codeIncomplete': 'Bitte gib den kompletten 6-stelligen Code ein',
    'auth.newCodeSent': 'Neuer Verifizierungscode gesendet',
    'auth.helpLink': 'Hast du Probleme? Kontaktiere uns',
    'auth.extLogin': 'Mit EaseVerse Lens anmelden',
    'auth.extLoggingIn': 'Anmelden...',
    'auth.useEmail': 'Stattdessen mit E-Mail anmelden',
    'auth.backToExt': 'Zurück zur EaseVerse-Lens-Anmeldung',
    'auth.or': 'oder',
    'auth.googleError': 'Google-Anmeldung fehlgeschlagen',
    'auth.agreeTerms': 'Ich stimme den',
    'auth.termsLink': 'Nutzungsbedingungen',
    'auth.privacyLink': 'Datenschutzerklärung',
    'auth.subscribeNewsletter': 'Produktupdates und Lesetipps abonnieren (1–2 E-Mails/Monat, jederzeit kündbar)',
    'auth.darkMode': 'Dunkler Modus',
    'auth.lightMode': 'Heller Modus',
    'auth.products': 'EaseVerse-Produkte',
    'auth.lensDesc': 'Kontext-Wörterbuch',
    'auth.loomDesc': 'Wissensdatenbank',
    'auth.readerDesc': 'Leseplattform',
    'auth.switchTitle': 'Konto wechseln?',
    'auth.switchDesc': 'Deine EaseVerse Lens-Erweiterung ist mit {email} angemeldet, was von deinem aktuellen Reader-Konto abweicht. Zu diesem Konto wechseln?',
    'auth.switchConfirm': 'Wechseln',
    'auth.switchDismiss': 'Bleiben',
    'sidebar.contents': 'Inhaltsverzeichnis',
    'sidebar.coverage.read': 'Gelesen',
    'sidebar.coverage.partial': 'Teilweise gelesen',
    'sidebar.coverage.unread': 'Noch nicht gelesen',
    'sidebar.noToc': 'Kein Inhaltsverzeichnis verfügbar',
    'sidebar.highlightsNotes': 'Markierungen & Notizen',
    'sidebar.bookmarks': 'Lesezeichen',
    'sidebar.all': 'Alle',
    'sidebar.highlights': 'Markierungen',
    'sidebar.notes': 'Notizen',
    'sidebar.emptyAll': 'Noch keine Markierungen oder Notizen. Markiere Text beim Lesen oder füge eine Notiz hinzu.',
    'sidebar.emptyHighlights': 'Noch keine Markierungen. Markiere Text beim Lesen.',
    'sidebar.emptyNotes': 'Noch keine Notizen. Markiere Text beim Lesen und füge eine Notiz hinzu.',
    'sidebar.emptyBookmarks': 'Noch keine Lesezeichen. Tippe auf "Hinzufügen", um deine aktuelle Position zu markieren.',
    'sidebar.add': 'Hinzufügen',
    'sidebar.save': 'Speichern',
    'sidebar.cancel': 'Abbrechen',
    'sidebar.removeHighlight': 'Markierung entfernen (Notiz behalten)',
    'sidebar.unknownChapter': 'Unbekanntes Kapitel',
    'sidebar.bookmark': 'Lesezeichen',
    'sidebar.page': 'Seite {page}',
    'reader.settingsCurrentBookHint': 'nur für das aktuelle Buch',
    'reader.settingsAllBooksHint': 'für alle Bücher',
    'reader.viewMode': 'Anzeigemodus',
    'reader.scroll': 'Scrollen',
    'reader.chapter': 'Kapitel',
    'reader.fontSize': 'Schriftgröße',
    'reader.pageWidth': 'Seitenbreite',
    'reader.pageMargin': 'Seitenränder',
    'reader.pageMarginNarrow': 'Schmal',
    'reader.pageMarginStandard': 'Standard',
    'reader.pageMarginWide': 'Breit',
    'reader.lineHeight': 'Zeilenhöhe',
    'reader.paragraph': 'Absatz',
    'reader.letter': 'Buchstabe',
    'reader.setLanguage': 'Bitte wähle die Sprache dieses Buches',
    'reader.languageConfirm': 'OK',
    'notification.dismiss': 'Schließen',
    'reader.changeLanguage': 'Sprache ändern',
    'reader.languageMismatch': 'Die Sprache dieses Buches könnte sein:',
    'reader.bookLanguageLabel': 'Buchsprache',
    'reader.font': 'Schrift',
    'reader.theme': 'Design',
    'reader.progressBar': 'Fortschrittsbalken',
    'reader.always': 'Immer',
    'reader.hover': 'Hover',
    'reader.hidden': 'Verborgen',
    'reader.einkMode': 'E-Ink-Modus',
    'reader.einkAuto': 'Automatisch',
    'reader.einkOn': 'An',
    'reader.einkOff': 'Aus',
    'reader.pagedMode': 'Seitenwechsel-Modus',
    'reader.tapToSelect': 'Tippen, um ein Wort auszuwählen',
    'reader.autoPlayPronunciation': 'Aussprache automatisch abspielen',
    'reader.autoPlayPronunciationHint': 'aktuelles Buch',
    'settings.languageAndLearning': 'Sprache & Lernen',
    'settings.interaction': 'Interaktion',
    'settings.pronunciation': 'Ausspracheeinstellungen',
    'settings.accent': 'Akzent',
    'settings.voice': 'Stimme',
    'settings.accentUS': 'Amerikanisch',
    'settings.accentGB': 'Britisch',
    'settings.voiceMale': 'Männlich',
    'settings.voiceFemale': 'Weiblich',
    'settings.proOnly': 'Nur Pro',
    'language.name.zh': 'Chinesisch',
    'language.name.en': 'Englisch',
    'language.name.ja': 'Japanisch',
    'language.name.ko': 'Koreanisch',
    'language.name.fr': 'Französisch',
    'language.name.de': 'Deutsch',
    'language.name.es': 'Spanisch',
    'language.name.pt': 'Portugiesisch',
    'language.name.ru': 'Russisch',
    'language.name.it': 'Italienisch',
    'language.name.vi': 'Vietnamesisch',
    'language.name.other': 'Sonstige',
    'reader.chapterLabel': 'Kapitel',
    'reader.bookLabel': 'Buch',
    'reader.prevChapter': 'Vorheriges Kapitel',
    'reader.nextChapter': 'Nächstes Kapitel',
    'reader.prevPage': 'Vorherige Seite',
    'reader.nextPage': 'Nächste Seite',
    'reader.goBack': 'Zurück',
    'reader.backToPosition': 'Zur vorherigen Position zurückkehren',
    'reader.sessionSummary': 'Gelesen {time}',
    'reader.sessionSummaryWithWords': 'Gelesen {time} · {words} Wörter',
    'reader.sessionSummaryCoverage': 'Gelesen {time} · {coverage} neue Wörter',
    'reader.sessionSummaryReread': 'Gelesen {time} · {reread} wiederholt',
    'reader.sessionSummaryThreeBucket': 'Gelesen {time} · {coverage} neu · {reread} wiederholt',
    'reader.formatTemporarilyUnavailable': '{format}-Unterstützung ist vorübergehend nicht verfügbar, während wir sie umbauen. Deine Datei und dein Lesefortschritt werden sicher aufbewahrt und sind verfügbar, wenn {format} wieder aktiviert wird.',
    'reader.pdfConverting': 'Dein PDF wird in ein lesbares Format umgewandelt — dauert in der Regel 1–2 Minuten. Die Seite lädt automatisch neu, sobald es fertig ist.',
    'reader.pdfConvertingElapsed': 'Verstrichen: {elapsed}',
    'reader.dualViewModeLabel': 'Lesemodus',
    'reader.dualViewText': 'Text',
    'reader.dualViewDual': 'Seite an Seite',
    'reader.dualViewOriginal': 'Original',
    'reader.dualViewNeedsUpgrade': 'Dieses Buch muss aktualisiert werden, um den Seite-an-Seite-Modus zu nutzen.',
    'reader.dualViewNeedsWiderScreen': 'Der Seite-an-Seite-Modus erfordert einen breiteren Bildschirm.',
    'reader.reanchor': 'Bereiche am aktuellen Absatz ausrichten',
    'reader.dualViewUpgradeTooltip': 'Dieses Buch aktualisieren, um den Seite-an-Seite-Modus zu aktivieren',
    'reader.dualViewUpgradeConfirm': 'Die Aktualisierung verarbeitet das Buch neu (~1–2 Minuten). Das Buch ist während der Umwandlung nicht lesbar. Fortfahren?',
    'reader.dualViewUpgradeFailed': 'Aktualisierung fehlgeschlagen. Bitte später erneut versuchen.',
    'reader.qualityMediumToast': 'Ein Teil des Textes dieses Buches könnte unvollständig extrahiert worden sein. Wir haben die Side-by-Side-Ansicht aktiviert, damit du mit dem Original-PDF abgleichen kannst.',
    'reader.qualityLowToast': 'Dieses PDF scheint ein Scan oder von geringer Qualität zu sein. Wir haben die Original-Ansicht voreingestellt.',
    'reader.qualityLowTooltip': 'Die Textextraktion war für dieses Buch unzuverlässig. Klicken für Details.',
    'reader.qualityLowAria': 'Textqualität-Warnung',
    'reader.qualityLowPopoverTitle': 'Textebene unvollständig',
    'reader.qualityLowPopoverBody': 'Der Text dieses Buches konnte nicht zuverlässig aus dem Quell-PDF extrahiert werden. Wir haben die Original-Ansicht voreingestellt, um die Originaltreue zu erhalten.',
    'reader.qualityDontShowAgain': 'Für dieses Buch nicht mehr anzeigen',
    'reader.openAsOriginal': 'Als Original-PDF öffnen',
    'reader.openAsOriginalHint': 'Zeigt das Quell-PDF direkt an. Hervorhebungen, Notizen und Lens sind in diesem Fallback-Modus nicht verfügbar.',
    'reader.figurePeekFigureOne': '1 Abbildung',
    'reader.figurePeekFigureMany': '{n} Abbildungen',
    'reader.figurePeekTableOne': '1 Tabelle',
    'reader.figurePeekTableMany': '{n} Tabellen',
    'reader.figurePeekEquationOne': '1 Gleichung',
    'reader.figurePeekEquationMany': '{n} Gleichungen',
    'reader.peekDialogLabel': 'Vorschau der Original-PDF-Seite',
    'reader.peekLoading': 'PDF-Seite wird geladen…',
    'reader.peekClose': 'Vorschau schließen',
    'reader.pdfConversionFailed': 'Wir konnten dieses PDF nicht umwandeln. Die Originaldatei ist gespeichert; du kannst sie löschen und erneut hochladen oder den Support kontaktieren.',
    'reader.minutesShort': '{n} Min',
    'reader.hoursShort': '{n} Std',
    'reader.hoursMinutesShort': '{h} Std {m} Min',
    'reader.theme.white': 'Weiß',
    'reader.theme.slate': 'Schiefergrau',
    'reader.theme.sage': 'Augenschonung',
    'reader.theme.parchment': 'Pergament',
    'reader.theme.midnight': 'Mitternacht',
    'reader.loginRequired': 'Bitte melde dich an, um dieses Buch zu lesen',
    'reader.loadFailed': 'Fehler beim Laden des Buches',
    'reader.bookNotFound': 'Buch nicht gefunden',
    'reader.deepReading.title': 'Deep Reading',
    'reader.deepReading.close': 'Deep Reading schließen',
    'reader.deepReading.toggle': 'Deep-Reading-Panel',
    'reader.deepReading.bubbles': 'Nachschlage-Karte',
    'lens.pronounceLocked': 'Aussprache dieser Sprache erfordert Pro',
    'reader.deepReading.viewHistory': 'Verlauf',
    'reader.deepReading.viewFrequency': 'Häufigkeit',
    'reader.deepReading.playNormal': 'Abspielen',
    'reader.deepReading.playSlow': 'Langsam abspielen (0,7×)',
    'reader.deepReading.notes': 'Notizen',
    'reader.deepReading.chat': 'Chat',
    'reader.deepReading.tabLookups': 'Wörter',
    'reader.deepReading.tabNotes': 'Notizen',
    'reader.deepReading.done': 'Fertig',
    'reader.deepReading.comingSoon': 'Demnächst',
    'reader.deepReading.signInToSee': 'Anmelden, um die Karte zu sehen',
    'reader.deepReading.loading': 'Wird geladen…',
    'reader.deepReading.loadError': 'Laden fehlgeschlagen',
    'reader.deepReading.noLookups': 'Noch keine Nachschlagungen in diesem Buch',
    'reader.deepReading.contexts': 'Kontexte',
    'reader.deepReading.back': '← Zurück zur Karte',
    'reader.deepReading.noNotes': 'Noch keine Notizen oder Markierungen',
    'reader.deepReading.notePlaceholder': 'Notiz schreiben…',
    'reader.deepReading.addNote': '+ Notiz hinzufügen',
    'reader.deepReading.jumpToPassage': 'Zur Stelle springen',
    'reader.deepReading.toLoom': '→ Zu Loom hinzufügen',
    'reader.deepReading.inLoom': '✓ In Loom',
    'reader.deepReading.loomError': 'Hinzufügen zu Loom fehlgeschlagen',
    'reader.deepReading.collectToLoom': 'In Loom speichern',
    'reader.deepReading.deleteEntry': 'Aus Liste entfernen',
    'reader.deepReading.reply': '↩ Antworten',
    'reader.deepReading.replyOpen': 'Auf diese Notiz antworten',
    'reader.deepReading.replyPlaceholder': 'Antwort schreiben…',
    'reader.deepReading.repliesShow': '{n} Antworten anzeigen',
    'reader.deepReading.repliesHide': 'Antworten ausblenden',
    'reader.deepReading.editCount': '{n}× bearbeitet',
    'reader.deepReading.editHistoryOpen': 'Bearbeitungsverlauf anzeigen',
    'reader.deepReading.deleteNote': 'Löschen',
    'reader.deepReading.timeJustNow': 'gerade eben',
    'reader.deepReading.timeMinAgo': 'vor {n} Min.',
    'reader.deepReading.timeHourAgo': 'vor {n} Std.',
    'reader.deepReading.timeDayAgo': 'vor {n} T.',
    'reader.deepReading.editHistoryTitle': 'Bearbeitungsverlauf',
    'reader.deepReading.editHistoryCurrent': 'Aktuelle Version',
    'reader.deepReading.editHistoryNone': 'Keine früheren Versionen',
    'reader.deepReading.editHistoryError': 'Verlauf konnte nicht geladen werden',
    'reader.followLink': 'Link öffnen',
    'note.placeholder': 'Schreib deine Notiz...',
    'note.addNotePlaceholder': 'Notiz hinzufügen...',
    'note.editNote': 'Notiz bearbeiten',
    'note.addNote': 'Notiz hinzufügen',
    'note.deleteNote': 'Notiz löschen',
    'note.deleteHighlight': 'Markierung löschen',
    'note.save': 'Speichern',
    'note.copy': 'Kopieren',
    'note.cancel': 'Abbrechen',
    'note.close': 'Schließen',
    'note.unpin': 'Lösen',
    'note.highlightedText': 'Markierter Text',
    'note.highlightColor': 'Mit {color} markieren',
    'note.underlineColor': 'Mit {color} unterstreichen',
    'note.saving': 'Speichern läuft...',
    'note.saved': 'Gespeichert',
    'note.error': 'Fehler',
    'loading.default': 'Wird geladen...',
    'loading.backToLibrary': 'Zurück zur Bibliothek',
    'stats.lessThan1min': '<1 Min',
    'stats.hour': 'Std ',
    'stats.minute': 'Min',
    'stats.goalLabel': 'Monatliches Ziel: {days} Tage/Woche, {time}/Tag',
    'stats.goalLabelDaily': 'Monatliches Ziel: {time}/Tag',
    'stats.noGoal': 'Kein Leseziel gesetzt',
    'stats.streakDays': '{n} Tage Lesestreak',
    'stats.streakLabel': 'Streak',
    'stats.thisMonth': 'Dieser Monat',
    'stats.todayLabel': 'Heute',
    'stats.thisWeek': 'Diese Woche',
    'stats.reading': ' Lesen',
    'stats.yearGoalLabel': ' Ziel',
    'stats.yearlyReadingGoal': '{year} Leseziel',
    'stats.wan': 'Tsd',
    'stats.wordsUnit': 'Wörter',
    'stats.mon': 'Mo',
    'stats.tue': 'Di',
    'stats.wed': 'Mi',
    'stats.thu': 'Do',
    'stats.fri': 'Fr',
    'stats.sat': 'Sa',
    'stats.sun': 'So',
    'stats.month1': 'Jan',
    'stats.month2': 'Feb',
    'stats.month3': 'Mär',
    'stats.month4': 'Apr',
    'stats.month5': 'Mai',
    'stats.month6': 'Jun',
    'stats.month7': 'Jul',
    'stats.month8': 'Aug',
    'stats.month9': 'Sep',
    'stats.month10': 'Okt',
    'stats.month11': 'Nov',
    'stats.month12': 'Dez',
    'stats.setGoalTitle': 'Monatliches Leseziel setzen',
    'stats.weeklyDays': 'Tage pro Woche',
    'stats.dailyMinutes': 'Tägliche Lesezeit',
    'stats.yearlyWords': 'Jährliches Wortlernziel',
    'stats.unitWan': 'x10k Wörter',
    'stats.save': 'Speichern',
    'stats.confirmSave': 'Bestätigen',
    'stats.goalConfirmMsg': 'Nach dem Speichern kannst du dein Ziel diesen Monat noch {n}-mal ändern. Fortfahren?',
    'stats.goalEditExhausted': 'Du hast alle Zieländerungen für diesen Monat aufgebraucht.',
    'stats.goalRemaining': 'Noch {n} Änderung(en) diesen Monat möglich',
    'stats.cancel': 'Abbrechen',
    'stats.minuteShort': 'Min',
    'stats.custom': 'Benutzerdefiniert',
    'stats.wordsCount': '≈{n} Wörter',
    'stats.wordsEstimateHint': 'Geschätzt aus Änderungen der Leseposition',
    'stats.noReading': 'Keine Leseaktivität',
    'stats.streakHint': 'Lesen von ≥5 min zählt zur Serie',
    'stats.dailyThreshold': '{time}/Tag',
    'stats.remaining': '{time} verbleibend',
    'dashboard.title': 'Leseanalytics',
    'dashboard.titleLang': '{lang} Leseanalytics',
    'dashboard.backToLibrary': 'Bibliothek',
    'dashboard.langDistribution': 'Sprachverteilung',
    'dashboard.langMonth': 'Monat',
    'dashboard.langYear': 'Jahr',
    'dashboard.langAll': 'Alle',
    'dashboard.langSingleHint': 'Wenn du Bücher in anderen Sprachen liest, kannst du hier nach Sprache filtern',
    'dashboard.langFold': 'Weniger',
    'dashboard.langName.zh': 'Chinesisch',
    'dashboard.langName.en': 'Englisch',
    'dashboard.langName.ja': 'Japanisch',
    'dashboard.langName.ko': 'Koreanisch',
    'dashboard.langName.fr': 'Französisch',
    'dashboard.langName.de': 'Deutsch',
    'dashboard.langName.es': 'Spanisch',
    'dashboard.langName.pt': 'Portugiesisch',
    'dashboard.langName.ru': 'Russisch',
    'dashboard.langName.it': 'Italienisch',
    'dashboard.langName.nl': 'Niederländisch',
    'dashboard.langName.ar': 'Arabisch',
    'dashboard.langName.th': 'Thailändisch',
    'dashboard.langName.vi': 'Vietnamesisch',
    'dashboard.todaySummary': 'Heute',
    'dashboard.weekSummary': 'Diese Woche',
    'dashboard.readingTime': 'Lesezeit',
    'dashboard.wordsRead': 'Wörter gelesen',
    'dashboard.totalReadingDays': 'Lesetage gesamt',
    'dashboard.dayUnit': ' Tage',
    'dashboard.achievements': 'Erfolge',
    'dashboard.currentStreak': '{count}-Tage-Serie',
    'dashboard.showAllBadges': 'Alle Abzeichen anzeigen...',
    'dashboard.cat_member': 'Pro-Mitglied',
    'dashboard.memberBadgeLabel': 'Pro',
    'dashboard.cat_days': 'Tage insgesamt',
    'dashboard.cat_streak': 'Leseserie',
    'dashboard.cat_words': 'Wörter gelesen',
    'dashboard.cat_time': 'Lesezeit',
    'dashboard.cat_books': 'Bücher abgeschlossen',
    'dashboard.nextMilestone': 'Nächster',
    'dashboard.memberRewardLine1': 'Halte einen beliebigen Bezahlplan (Basic / Pro / Max) für 365 Tage',
    'dashboard.memberRewardLine2': 'um 1 Monat kostenlose Pro-Mitgliedschaft freizuschalten',
    'dashboard.memberGiftUnlocked': 'Glückwunsch! 365 Tage als EaseVerse-Mitglied — dein Geschenk ist angekommen~🎁',
    'dashboard.memberActiveTooltip': 'Tag {days} als EaseVerse-Mitglied — am Tag 365 wartet ein Geschenk auf dich~',
    'dashboard.memberPausedTooltip': 'Du warst {days} Tage lang EaseVerse-Mitglied. Erneuere, um weiterzuzählen~',
    'dashboard.memberNotPaidTooltip': 'Du bist noch kein EaseVerse-Mitglied — gehe zu deinem Profil, um die Mitgliedschaft zu aktivieren~',
    'dashboard.badgeUnlocked': 'Glückwunsch zu "{0}"!\nWeiter so, um "{1}" freizuschalten',
    'dashboard.badgeMaxLevel': 'Glückwunsch zu "{0}"!\nDu hast die höchste Stufe erreicht!',
    'dashboard.badgeLocked': 'Fang an zu lesen\num "{0}" freizuschalten',
    'dashboard.badgeCountSuffix': ' erworben',
    'dashboard.badgeCongratsTooltip': 'Glückwunsch 🎉 Freigeschaltet!',
    'dashboard.badgeCountText': '{n} Leser haben das freigeschaltet',
    'dashboard.badgeExpandMore': 'Alle anzeigen',
    'dashboard.daysSuffix': 'd',
    'dashboard.booksSuffix': '',
    'dashboard.viewAllBadges': 'Alle Abzeichen anschauen',
    'dashboard.statToday': 'Heute gelesen',
    'dashboard.statWords': 'Gelesene Wörter',
    'dashboard.statTotalWords': 'Insgesamt gelesene Wörter',
    'dashboard.statRereadWords': '+{n} wiederholt',
    'dashboard.wordsPopoverTitle': 'Anzeigesprachen',
    'dashboard.wordsPopoverSaved': 'Gespeichert',
    'dashboard.statWeek': 'Diese Woche gelesen',
    'dashboard.statTotalDays': 'Gesamttage',
    'dashboard.totalDaysTooltip': 'Am {date} hast du deine Lesereise auf EaseVerse begonnen. {days} Tage und es geht weiter!',
    'dashboard.dailyTrend': 'Täglicher Trend',
    'dashboard.monthlyTotal': 'Diesen Monat {time} gelesen',
    'dashboard.dailyAvg': 'Täglich durchschnittlich {time}',
    'dashboard.yearMonth': '{month}/{year}',
    'dashboard.hourlyInsight': 'Du liest am liebsten {period}',
    'dashboard.hourlyInsightRange': 'Du liest am liebsten von {from} bis {to}',
    'dashboard.hourlyWords': 'Wörter pro Minute',
    'dashboard.wpmHint': 'Wörter pro Minute',
    'dashboard.wpmSubtitle': 'Diesen Monat {wpm} Wörter/Min',
    'dashboard.wpmSubtitleUp': 'Diesen Monat {wpm} Wörter/Min, um {pct}% höher als letzten Monat',
    'dashboard.wpmSubtitleDown': 'Diesen Monat {wpm} Wörter/Min, um {pct}% niedriger als letzten Monat',
    'dashboard.wpmInsight': '{period} WPM am höchsten',
    'dashboard.wpmInsightRange': '{from} bis {to} WPM am höchsten',
    'dashboard.hourlyTime': 'Lesedauer pro Stunde',
    'dashboard.wordsUnit': 'Wörter',
    'dashboard.period.lateNight': 'spät nachts',
    'dashboard.period.dawn': 'in der Frühe',
    'dashboard.period.earlyMorning': 'frühmorgens',
    'dashboard.period.lateMorning': 'spätvormittags',
    'dashboard.period.earlyAfternoon': 'frühnachmittags',
    'dashboard.period.lateAfternoon': 'spätnachmittags',
    'dashboard.period.evening': 'abends',
    'dashboard.period.nightfall': 'in der Dämmerung',
    'dashboard.bookBreakdown': 'Bücher',
    'dashboard.noData': 'Noch keine Lesedaten',
    'dashboard.editGoal': 'Ziel setzen',
    'dashboard.viewDetails': 'Details anschauen',
    'dashboard.yearlyTime': 'Jährliche Lesedauer',
    'dashboard.yearlyWpm': 'Jährliches WPM',
    'dashboard.monthUnit': '',
    'dashboard.wpmExplainPlaceholder': 'WPM (Wörter pro Minute) misst deine Lesegeschwindigkeit – eine ausführliche Erklärung folgt bald.',
    'milestone.streak_3d': '3-Tage-Lesesträhne!',
    'milestone.streak_7d': '7-Tage-Lesesträhne!',
    'milestone.streak_14d': '14-Tage-Lesesträhne!',
    'milestone.streak_21d': '21-Tage-Lesesträhne!',
    'milestone.streak_30d': '30-Tage-Lesesträhne!',
    'milestone.streak_60d': '60-Tage-Lesesträhne!',
    'milestone.streak_100d': '100-Tage-Lesesträhne!',
    'milestone.streak_180d': '180-Tage-Lesesträhne!',
    'milestone.streak_365d': '365-Tage-Lesesträhne!',
    'milestone.words_10k': '10.000 Wörter gelesen!',
    'milestone.words_50k': '50.000 Wörter gelesen!',
    'milestone.words_100k': '100.000 Wörter gelesen!',
    'milestone.words_500k': '500.000 Wörter gelesen!',
    'milestone.words_1m': '1.000.000 Wörter gelesen!',
    'milestone.words_2m': '2.000.000 Wörter gelesen!',
    'milestone.words_3m': '3.000.000 Wörter gelesen!',
    'milestone.words_4m': '4.000.000 Wörter gelesen!',
    'milestone.words_5m': '5.000.000 Wörter gelesen!',
    'milestone.time_10h': '10 Stunden gelesen!',
    'milestone.time_50h': '50 Stunden gelesen!',
    'milestone.time_100h': '100 Stunden gelesen!',
    'milestone.time_300h': '300 Stunden gelesen!',
    'milestone.time_500h': '500 Stunden gelesen!',
    'milestone.time_1000h': '1.000 Stunden gelesen!',
    'milestone.time_2000h': '2.000 Stunden gelesen!',
    'milestone.weekly_goal_first': 'Erstes wöchentliches Ziel erreicht!',
    'milestone.weekly_goal_4': '4 Wochen in Folge Ziel erreicht!',
    'milestone.yearly_25': '25% deines jährlichen Ziels!',
    'milestone.yearly_50': 'Hälfte deines jährlichen Ziels!',
    'milestone.yearly_75': '75% deines jährlichen Ziels!',
    'milestone.yearly_100': 'Jährliches Ziel erreicht!',
    'milestone.books_1': 'Dein erstes Buch ausgelesen!',
    'milestone.books_3': '3 Bücher ausgelesen!',
    'milestone.books_5': '5 Bücher ausgelesen!',
    'milestone.books_10': '10 Bücher ausgelesen!',
    'milestone.books_25': '25 Bücher ausgelesen!',
    'milestone.books_50': '50 Bücher ausgelesen!',
    'milestone.books_75': '75 Bücher ausgelesen!',
    'milestone.books_100': '100 Bücher ausgelesen!',
    'milestone.days_7': '7 Tage gelesen!',
    'milestone.days_30': '30 Tage gelesen!',
    'milestone.days_100': '100 Tage Lesen!',
    'milestone.days_200': '200 Tage Lesen!',
    'milestone.days_365': '365 Tage Lesen!',
    'milestone.member_365d': '365 Tage Pro-Mitglied!',
    'milestone.lang_words': 'Lies {amount} {lang} Wörter!',
    'milestone.lang_time': '{amount} {lang} gelesen!',
    'chunks.title': 'Wortschatz-Blöcke',
    'chunks.showAnnotations': 'Vokabel-Anmerkungen anzeigen',
    'chunks.filterRange': 'Anzeigebereich',
    'chunks.levelAdaptive': 'Adaptiv',
    'chunks.modeManual': 'Manuell',
    'chunks.levelShowAll': 'Alle anzeigen',
    'chunks.density': 'Dichte',
    'chunks.densityFocused': 'Fokussiert',
    'chunks.densityDetailed': 'Detailliert',
    'chunks.adaptiveExplore': 'Lerne deine Vorlieben kennen ({count}/{total})',
    'chunks.adaptiveActive': 'Angepasst nach {count} Interaktionen',
    'chunks.manualLevel': 'Level',
    'chunks.levelAll': 'Alle',
    'chunks.manualCategory': 'Kategorie',
    'chunks.cat_vocabulary': 'Vokabeln',
    'chunks.cat_phrasal_verb': 'Phrasenverb',
    'chunks.cat_collocation': 'Wortverbindung',
    'chunks.cat_idiom': 'Redewendung',
    'chunks.cat_grammar': 'Grammatik',
    'chunks.cat_knowledge': 'Wissen',
    'chunks.cat_tone': 'Ton',
    'chunks.resetProfile': 'Profil zurücksetzen',
    'chunks.modeTranslate': 'Übersetzen',
    'chunks.modeSynonym': 'Synonym',
    'chunks.loading': 'Wird geladen...',
    'chunks.rehinting': 'Aus einem anderen Blickwinkel betrachten…',
    'chunks.reportBadCache': 'Schlechte Übersetzung melden',
    'chunks.generating': 'Erklärung wird generiert...',
    'chunks.askPlaceholder': 'Frag mich alles',
    'lens.sentenceAnalysis': 'Satzanalyse',
    'lens.originalSentence': 'Originalsatz',
    'lens.step1': 'Bedeutung',
    'lens.step2': 'Struktur',
    'lens.step3': 'Tiefere Analyse',
    'lens.analyzing': 'Analysiere...',
    'lens.showGrammar': 'Grammatik anzeigen',
    'lens.hideGrammar': 'Grammatik ausblenden',
    'lens.openChat': 'Frag mich alles',
    'lens.saveNote': 'Notiz speichern',
    'lens.noteSaved': 'Gespeichert',
    'lens.saveNoteFailed': 'Speichern fehlgeschlagen',
    'lens.removeNote': 'Notiz löschen',
    'lens.removeNoteFailed': 'Löschen fehlgeschlagen',
    'lens.checkingNote': 'Überprüfe...',
    'lens.collapse': 'Einklappen',
    'chunks.resetConfirm': 'Alle Daten der Sprachblöcke zurücksetzen? Das lässt sich nicht rückgängig machen.',
    'chunks.onboardingHint': 'Tippe auf die Punkte, um Erklärungen zu sehen',
    'chunks.statusLoading': 'Lade Erklärungen...',
    'chunks.statusProcessing': 'Analysiere Vokabeln...',
    'chunks.cacheRefreshed': 'Erklärungen aktualisiert — klicken zum Aktualisieren',
    'chunks.proRequired': 'Upgrade auf einen kostenpflichtigen Tarif, um Sprachblock-Erklärungen freizuschalten.',
    'chunks.bookUsage': "Sprachblöcke: {used}/{limit} Bücher diesen Monat",
    'chunks.bookLimitReached': "{limit}/{limit} Bücher diesen Monat — Pro für unbegrenzt",
    'deviceLimit.titleConfirm': 'Gerätelimit erreicht',
    'deviceLimit.titleSelect': 'Gerätelimit erreicht',
    'deviceLimit.confirmDesc': 'Fortfahren wird das folgende Gerät abmelden:',
    'deviceLimit.selectDesc': 'Wähle ein Gerät zum Ersetzen:',
    'deviceLimit.loggedIn': 'angemeldet',
    'deviceLimit.cancel': 'Abbrechen',
    'deviceLimit.confirming': 'Melde dich an...',
    'deviceLimit.confirmLogin': 'Bestätigen & Anmelden',
    'deviceLimit.confirmReplace': 'Ersetzen & Anmelden',
    'deviceLimit.unknownDevice': 'Unbekanntes Gerät',
    'session.invalidTitle': 'Sitzung abgelaufen',
    'session.invalidHint': 'Dieses Gerät wurde abgemeldet.',
    'session.reLogin': 'Erneut anmelden',
    'signOut.title': 'Melde dich ab...',
    'signOut.message': 'Speichere deine nicht synchronisierten Änderungen',
    'signOut.hint': 'Bitte warte einen Moment',
  },
};

// Active languages shown in the UI selector
export const ACTIVE_LANGUAGES = ['en', 'zh', 'zh-TW', 'fr', 'ja', 'de'];

export const languageLabels = {
  en: 'English',
  zh: '简体中文',
  'zh-TW': '繁體中文',
  fr: 'Français',
  ja: '日本語',
  de: 'Deutsch',
};

// Detect user's preferred language.
// Priority: URL param (?lang=) → localStorage → browser detection → 'en' fallback.
function detectLanguage() {
  // 1. URL param (from Lens onboarding or cross-product navigation)
  try {
    const urlLang = new URLSearchParams(window.location.search).get('lang');
    if (urlLang && ACTIVE_LANGUAGES.includes(urlLang)) {
      localStorage.setItem('easeverse-reader-lang', urlLang);
      const clean = new URL(window.location.href);
      clean.searchParams.delete('lang');
      history.replaceState(null, '', clean.pathname + clean.search);
      return urlLang;
    }
  } catch {}

  // 2. localStorage persisted value
  const stored = localStorage.getItem('easeverse-reader-lang');
  if (stored && translations[stored]) return stored;

  // 3. Browser language detection
  const list = navigator.languages && navigator.languages.length
    ? navigator.languages
    : [navigator.language || 'en'];
  const tags = list.map((l) => (l || '').toLowerCase());

  if (tags.some((t) => t.startsWith('zh-tw') || t.startsWith('zh-hant'))) return 'zh-TW';
  if (tags.some((t) => t.startsWith('zh'))) return 'zh';
  if (tags.some((t) => t.startsWith('de'))) return 'de';
  if (tags.some((t) => t.startsWith('fr'))) return 'fr';
  if (tags.some((t) => t.startsWith('ja'))) return 'ja';

  for (const tag of tags) {
    if (tag.startsWith('en')) return 'en';
    if (tag.startsWith('fr')) return 'fr';
    if (tag.startsWith('ja')) return 'ja';
  }
  return 'en';
}

for (const [locale, copy] of Object.entries(landingCopy)) {
  for (const [key, value] of Object.entries(copy)) {
    translations[locale][`reader.landing.${key}`] = value;
  }
}

let currentLang = detectLanguage();
let listeners = new Set();

export function getLanguage() {
  return currentLang;
}

export function setLanguage(lang) {
  if (!translations[lang]) return;
  currentLang = lang;
  localStorage.setItem('easeverse-reader-lang', lang);
  syncSiteTitle();
  listeners.forEach((fn) => fn(lang));
}

// Keep document.title in sync with the active language's site title.
// Pages that need a custom title (e.g. book pages) override inside their own
// effects keyed on `language`, so they'll re-apply after this runs.
function syncSiteTitle() {
  if (typeof document === 'undefined') return;
  document.title = translations[currentLang]?.['meta.siteTitle']
    ?? translations.en['meta.siteTitle']
    ?? 'EaseVerse Reader';
}

/**
 * Translate a key, with optional interpolation: t('key', { var: value })
 */
export function t(key, params) {
  const str = translations[currentLang]?.[key] ?? translations.en[key] ?? key;
  if (!params) return panguCJK(str);
  const interpolated = str.replace(/\{(\w+)\}/g, (_, k) => (params[k] !== undefined ? params[k] : `{${k}}`));
  return panguCJK(interpolated);
}

/** Insert spaces between CJK ideographs and half-width alphanumerics (盘古之白) */
const CJK = '\u4e00-\u9fff\u3400-\u4dbf\uf900-\ufaff';
const cjkBeforeAlnum = new RegExp(`([${CJK}])([A-Za-z0-9])`, 'g');
const alnumBeforeCjk = new RegExp(`([A-Za-z0-9])([${CJK}])`, 'g');
function panguCJK(s) {
  if (currentLang !== 'zh' && currentLang !== 'zh-TW') return s;
  return s.replace(cjkBeforeAlnum, '$1 $2').replace(alnumBeforeCjk, '$1 $2');
}

/**
 * React hook: re-renders component when language changes.
 */
export function useTranslation() {
  const [, setLang] = React.useState(currentLang);

  React.useEffect(() => {
    const handler = (lang) => setLang(lang);
    listeners.add(handler);
    return () => listeners.delete(handler);
  }, []);

  return { t, language: currentLang, setLanguage };
}

// Listen for real-time language changes relayed from Lens extension via bridge content script.
// The bridge dispatches a CustomEvent when the user changes language in the Lens popup.
window.addEventListener('easeverse:lang-change', (event) => {
  const lang = event.detail?.lang;
  if (lang && ACTIVE_LANGUAGES.includes(lang)) {
    setLanguage(lang);
  }
});

// We need React for the hook
import React from 'react';

// Apply the detected language's site title on first load so the tab reflects
// the user's language even before any page-specific effect runs.
syncSiteTitle();
